import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { 
  READY_MADE_TEMPLATES, 
  TASK_FLOWS, 
  AGENT_CATEGORIES, 
  AgentTemplate, 
  TaskFlow 
} from "../data/agentTemplates";
import { 
  Sparkles, Search, Sliders, Check, Eye, Play, Zap, Shield, 
  User, CheckSquare, RefreshCw, X, FolderMinus, AlertTriangle, Cpu, Terminal, 
  Lock, ArrowUpRight, Copy, Plus, BadgeCheck, FileText, Smartphone, LayoutGrid, BarChart2,
  Building, Globe, Briefcase, Landmark, History, Radio, Hammer, UserCheck, PlaneTakeoff, Settings,
  ChevronLeft, ChevronRight, CheckCircle, Info, Activity, Download
} from "lucide-react";
import { useToast } from "../contexts/ToastContext";
import { NexoraXAgentWorkspace } from "./NexoraXAgentWorkspace";
import { authenticatedFetch } from "./direct-delivery/authenticatedApiHelper";

export function NexoraXAgentTemplateLibrary() {
  const verifiedPreamble = "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.";
  const { triggerToast } = useToast();

  // Multi-column filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("ALL");
  const [selectedStatus, setSelectedStatus] = useState<string>("ALL");
  const [selectedApproval, setSelectedApproval] = useState<string>("ALL");
  const [activeTab, setActiveTab ] = useState<"TEMPLATES" | "TASK_FLOWS" | "WIZARD" | "WORKSPACE" | "USAGE_METER" | "PRICING" | "ADMIN_PLAN_MGR">("WIZARD");

  // SaaS Persona switcher: 'VISITOR' | 'CLIENT' | 'AGENCY_ADMIN' | 'SUPER_ADMIN'
  const [saasPersona, setSaasPersona] = useState<'VISITOR' | 'CLIENT' | 'AGENCY_ADMIN' | 'SUPER_ADMIN'>(() => {
    const saved = localStorage.getItem("nx_saas_persona");
    return saved === 'VISITOR' ? 'VISITOR' : 'CLIENT';
  });

  // Keep old role in sync for other system components
  const [currentUserRole, setCurrentUserRole] = useState<'VISITOR' | 'CLIENT' | 'ADMIN'>(() => {
    return 'CLIENT';
  });

  // Keep legacy and saas role synced
  useEffect(() => {
    localStorage.setItem("nx_saas_persona", saasPersona);
    if (saasPersona === 'VISITOR') {
      setCurrentUserRole('VISITOR');
    } else if (saasPersona === 'CLIENT') {
      setCurrentUserRole('CLIENT');
    } else {
      setCurrentUserRole('ADMIN');
    }
  }, [saasPersona]);

  // Client's active subscription tier state
  const [clientPlan, setClientPlan] = useState<'Starter' | 'Growth' | 'Agency' | 'Enterprise'>(() => {
    return (localStorage.getItem("nx_saas_client_plan") as any) || "Starter";
  });

  useEffect(() => {
    localStorage.setItem("nx_saas_client_plan", clientPlan);
  }, [clientPlan]);

  // Plan limits config mapping
  const [planLimits, setPlanLimits] = useState<Record<string, { name: string; maxAgents: number; maxTasks: number; maxWorkflows: number; maxConnectors: number }>>(() => {
    const saved = localStorage.getItem("nx_saas_plan_limits");
    return saved ? JSON.parse(saved) : {
      Starter: { name: "Starter", maxAgents: 2, maxTasks: 100, maxWorkflows: 0, maxConnectors: 3 },
      Growth: { name: "Growth", maxAgents: 5, maxTasks: 1000, maxWorkflows: 20, maxConnectors: 10 },
      Agency: { name: "Agency", maxAgents: 20, maxTasks: 10000, maxWorkflows: 100, maxConnectors: 50 },
      Enterprise: { name: "Enterprise", maxAgents: 999, maxTasks: 999999, maxWorkflows: 999, maxConnectors: 999 }
    };
  });

  const savePlanLimits = (limits: typeof planLimits) => {
    setPlanLimits(limits);
    localStorage.setItem("nx_saas_plan_limits", JSON.stringify(limits));
  };

  // Template Plan Gating mapping
  const [templatePlanGating, setTemplatePlanGating] = useState<Record<string, 'Starter' | 'Growth' | 'Agency' | 'Enterprise'>>(() => {
    const saved = localStorage.getItem("nx_saas_template_gating");
    if (saved) return JSON.parse(saved);
    return {
      "cust-support": "Starter",
      "email-management": "Starter",
      "calendar-scheduling": "Starter",
      "content-writer": "Starter",
      "website-chat": "Starter",

      "sales-assistant": "Growth",
      "product-search": "Growth",
      "social-media-mgr": "Growth",
      "seo-assistant": "Growth",
      "project-manager": "Growth",
      "client-success": "Growth",
      "meeting-notes": "Growth",
      "newsletter": "Growth",

      "campaign-planner": "Agency",
      "crm-update": "Agency",
      "proposal-writer": "Agency",
      "data-reporting": "Agency",
      "review-reputation": "Agency",
      "onboarding-agent": "Agency",
      "operations-assistant": "Agency",

      "exec-assistant": "Enterprise",
      "market-research": "Enterprise",
      "compliance-assistant": "Enterprise",
      "finance-summary": "Enterprise",
      "legal-draft-support": "Enterprise",
      "internal-knowledge": "Enterprise",
      "risk-escalation": "Enterprise"
    };
  });

  const saveTemplatePlanGating = (gating: typeof templatePlanGating) => {
    setTemplatePlanGating(gating);
    localStorage.setItem("nx_saas_template_gating", JSON.stringify(gating));
  };

  // Add-on products list
  const [addOnList, setAddOnList] = useState<any[]>(() => {
    const saved = localStorage.getItem("nx_saas_addons");
    return saved ? JSON.parse(saved) : [
      { id: "extra-agent", name: "Extra Agent Slot", unlocks: "Add +1 deployed agent slots onto your current active tier.", recommendedFor: "Growth & Agency users scaling support loops.", status: "Available", price: "£49/mo" },
      { id: "extra-tasks", name: "Extra Monthly Tasks", unlocks: "Purchase and inject +5,000 tasks block dynamically.", recommendedFor: "High-volume transactional campaigns.", status: "Available", price: "£99/mo" },
      { id: "adv-analytics", name: "Advanced Analytics Pack", unlocks: "Real-time granular visualizer and executive metric exports.", recommendedFor: "Agency accounts mapping client value structures.", status: "Available", price: "£29/mo" },
      { id: "premium-tpls", name: "Premium Template Pack", unlocks: "Instant clearance access to 8 advanced operational templates.", recommendedFor: "Niche content and operations builders.", status: "Available", price: "£39/mo" },
      { id: "multi-client", name: "Multi-Client Workspace", unlocks: "Enable isolated dashboards for external clients to sign in.", recommendedFor: "Agencies managing 10+ operational clients.", status: "Available", price: "£149/mo" },
      { id: "priority-support", name: "Priority Support", unlocks: "SLA response time under 15 minutes + 24/7 critical monitoring.", recommendedFor: "Enterprise nodes requiring absolute up-times.", status: "Available", price: "£199/mo" },
      { id: "custom-build", name: "Custom Agent Build", unlocks: "One-on-one custom template curation by NexoraX systems engineers.", recommendedFor: "Complex transactional or bespoke database rules.", status: "Available", price: "Contact Sales" },
      { id: "adv-connector", name: "Advanced Connector Pack", unlocks: "Clearance for Zapier, Webhooks, custom APIs, and secure SQL databases.", recommendedFor: "Integrated cross-suite architectures.", status: "Available", price: "£59/mo" },
      { id: "video-auto", name: "Video Automation Pack", unlocks: "Generative video and automated media dispatch pipelines.", recommendedFor: "Marketing agencies scaling delivery.", status: "Available", price: "£89/mo" },
      { id: "social-auto", name: "Social Media Automation Pack", unlocks: "Autopilot multi-channel publishing with human-in-the-loop sign-off.", recommendedFor: "Brand presence builders.", status: "Available", price: "£49/mo" }
    ];
  });

  const saveAddOns = (addons: typeof addOnList) => {
    setAddOnList(addons);
    localStorage.setItem("nx_saas_addons", JSON.stringify(addons));
  };

  // Simulated upgrade demand logs & churn risk signals
  const [upgradeDemand, setUpgradeDemand] = useState<any[]>(() => {
    const saved = localStorage.getItem("nx_saas_upgrade_demand");
    return saved ? JSON.parse(saved) : [
      { id: "upg-1", timestamp: "2026-06-13 11:32", company: "CyberDyne Systems", role: "Sales Lead Qualifier Assistant", currentPlan: "Starter", targetPlan: "Growth", agentId: "sales-assistant" },
      { id: "upg-2", timestamp: "2026-06-13 09:14", company: "AeroSpace Tech", role: "Executive Assistant Agent", currentPlan: "Growth", targetPlan: "Enterprise", agentId: "exec-assistant" }
    ];
  });

  const logUpgradeInterest = (agentName: string, agentId: string, current: string, target: string) => {
    const newInterest = {
      id: "upg-" + Date.now(),
      timestamp: new Date().toISOString().replace('T', ' ').substring(0, 16),
      company: customiseForm.businessName || "Registered Client",
      role: agentName,
      currentPlan: current,
      targetPlan: target,
      agentId: agentId
    };
    const updated = [newInterest, ...upgradeDemand];
    setUpgradeDemand(updated);
    localStorage.setItem("nx_saas_upgrade_demand", JSON.stringify(updated));
  };

  // Client-faced usage states
  const [clientUsage, setClientUsage] = useState({
    agentsUsed: 3,
    tasksUsed: 420,
    workflowsUsed: 8,
    connectorsUsed: 3,
    approvalsPending: 12,
    hoursSaved: 86,
    estimatedValue: 2400
  });

  // Locked Agent upgrade modal state
  const [lockedAgentModal, setLockedAgentModal] = useState<AgentTemplate | null>(null);

  // Approved public contact settings synced to local storage
  const [publicContactSettings, setPublicContactSettings] = useState(() => {
    const saved = localStorage.getItem("nx_public_contact_settings");
    return saved ? JSON.parse(saved) : {
      websiteUrl: "https://nexorax.co.uk",
      publicEmail: "support@nexorax.co.uk",
      publicPhone: "08000 622827",
      bookingLink: "https://nexorax.co.uk/book-demo",
      supportLink: "https://support.nexorax.co.uk"
    };
  });

  // Brand Intro Mode setting (optional preambles options)
  const [brandIntroMode, setBrandIntroMode] = useState<"None" | "Professional" | "Founder Message" | "Spiritual / Values-Based Intro" >(() => {
    return (localStorage.getItem("nx_brand_intro_mode") as any) || "Professional";
  });

  // Check if public contact settings are missing
  const isPublicContactDetailsMissing = !publicContactSettings.websiteUrl || 
                                        !publicContactSettings.publicEmail || 
                                        !publicContactSettings.publicPhone || 
                                        !publicContactSettings.bookingLink || 
                                        !publicContactSettings.supportLink;

  // Customization flow states
  const [selectedTemplateForCustomise, setSelectedTemplateForCustomise] = useState<AgentTemplate | null>(null);
  const [customiseForm, setCustomiseForm] = useState({
    agentDisplayName: "",
    businessName: "NexoraX Enterprise",
    websiteUrl: publicContactSettings.websiteUrl,
    publicEmail: publicContactSettings.publicEmail,
    publicPhone: publicContactSettings.publicPhone,
    brandVoice: "Authoritative, Professional, Institutional",
    toneStyle: "Precise, Futuristic, Clean",
    industry: "SaaS & AI Operations",
    targetCustomer: "Commercial Clients & Operational Leaders",
    mainOffer: "Automated AI integration with robust security",
    cta: "Contact [BUSINESS_NAME] today for an automated consultation at [PUBLIC_PHONE].",
    approvalRequirement: "Require Human Approval before committing external state or messaging.",
    escalationContact: publicContactSettings.publicEmail,
    connectedTools: "Google Workspace, Stripe Billing, Client CRM",
    knowledgeSources: "General Business Operations SOP, Customer Support Guide",
    doAndDontRules: "DO: Maintain prompt, professional responses adhering to brand guidelines.\nDON'T: Expose internal operational structures or raw backend logs."
  });

  // Keep customization form elements synchronized if state changes
  useEffect(() => {
    setCustomiseForm(prev => ({
      ...prev,
      websiteUrl: publicContactSettings.websiteUrl,
      publicEmail: publicContactSettings.publicEmail,
      publicPhone: publicContactSettings.publicPhone,
      escalationContact: publicContactSettings.publicEmail
    }));
  }, [publicContactSettings]);

  // Dynamic replacement preview
  const [customTextPreview, setCustomTextPreview] = useState("");

  // Interactive templates state (enables admin modifications & saving customized templates)
  const [templates, setTemplates] = useState<AgentTemplate[]>(() => {
    const saved = localStorage.getItem("nx_agent_templates");
    return saved ? JSON.parse(saved) : READY_MADE_TEMPLATES;
  });

  // Admin / Supervisory control state
  const [isAdminMode, setIsAdminMode] = useState(() => {
    const isAuth = sessionStorage.getItem('admin session verification') === 'verified_admin_active';
    return isAuth;
  });
  const [adminTemplateUsage, setAdminTemplateUsage] = useState<Record<string, { deployments: number; usage: number; perfScore: number }>>(() => {
    const weights: Record<string, { deployments: number; usage: number; perfScore: number }> = {};
    READY_MADE_TEMPLATES.forEach(t => {
      weights[t.id] = {
        deployments: Math.floor(Math.random() * 140 + 40),
        usage: Math.floor(Math.random() * 800 + 200),
        perfScore: parseFloat((Math.random() * 8 + 91).toFixed(1))
      };
    });
    return weights;
  });

  // Selected Detail Modal states
  const [detailTemplate, setDetailTemplate] = useState<AgentTemplate | null>(null);
  
  // Human Approval Gates State
  const [activeApprovalGateTemplate, setActiveApprovalGateTemplate] = useState<AgentTemplate | TaskFlow | null>(null);
  const [approvalGateCallback, setApprovalGateCallback] = useState<(() => void) | null>(null);
  const [approvalGateChecks, setApprovalGateChecks] = useState({
    levyVerified: false,
    accessToWorkVerified: false,
    securityVerified: false,
    latencyVerified: false
  });

  // Admin Manager active subtab state
  const [adminActiveTab, setAdminActiveTab] = useState<"MASTER_REGISTRY" | "NODE_MIGRATE" | "NEXORAX_Secure Invoice Register" | "NODE_07_BILLING" | "PUBLIC_CONTACT">("MASTER_REGISTRY");
  
  // Node-07 Revenue ledger inputs
  const [dailyGrossHarvest, setDailyGrossHarvest] = useState<string>("100000");

  // Premium Wizard Setup States
  const [wizardStep, setWizardStep] = useState<number>(1);
  const [wizardSelectedTemplate, setWizardSelectedTemplate] = useState<AgentTemplate | null>(null);
  const [wizardAgentForm, setWizardAgentForm] = useState({
    agentDisplayName: "",
    businessName: "NexoraX Global",
    websiteUrl: publicContactSettings.websiteUrl,
    publicEmail: publicContactSettings.publicEmail,
    publicPhone: publicContactSettings.publicPhone,
    industry: "SaaS Enterprise",
    targetCustomer: "Commercial Clients",
    mainService: "AI Workflow Solutions",
    brandVoice: "Innovative, Professional, Institutional",
    toneStyle: "Professional", // Professional, Friendly, Premium, Direct, Helpful, Technical, Executive
    cta: "Contact [BUSINESS_NAME] today at [PUBLIC_EMAIL] or visit [WEBSITE_URL] for live onboarding checks.",
    escalationContact: "escalations@nexorax.co.uk",
    approvalRule: "Require human permission review for all system transactions."
  });

  // Track task flows added to current wizard
  const [wizardSelectedTaskFlows, setWizardSelectedTaskFlows] = useState<string[]>([
    "New Lead Follow-Up",
    "Customer Support Reply Draft",
    "Daily Email Summary"
  ]);
  
  // Track mock connector states
  const [wizardConnectedTools, setWizardConnectedTools] = useState<Record<string, "CONNECTED" | "NOT_CONNECTED" | "TOKEN_EXPIRED" | "PERMISSION_MISSING" | "RECONNECT_REQUIRED" | "ADMIN_APPROVAL_REQUIRED">>({
    "Gmail / Email": "CONNECTED",
    "Google Drive": "CONNECTED",
    "Google Calendar": "PERMISSION_MISSING",
    "Slack": "NOT_CONNECTED",
    "HubSpot / CRM": "ADMIN_APPROVAL_REQUIRED",
    "Website Forms": "CONNECTED",
    "Social Media": "NOT_CONNECTED",
    "Blog / CMS": "NOT_CONNECTED",
    "Stripe / Billing Status": "RECONNECT_REQUIRED",
    "PayPal / Billing Status": "NOT_CONNECTED",
    "Webhooks": "CONNECTED",
    "File Library": "CONNECTED",
    "Client Workspace": "NOT_CONNECTED"
  });

  // Approval rules states
  const [wizardApprovalLevel, setWizardApprovalLevel] = useState<"Low" | "Balanced" | "High" | "Admin-controlled">("Balanced");
  const [wizardApprovalRules, setWizardApprovalRules] = useState<Record<string, boolean>>({
    sendEmails: true,
    publishContent: true,
    updateBilling: true,
    changeClientRecords: true,
    financialClaims: true,
    legalMessaging: true,
    deleteData: true,
    connectExternalPlatforms: false,
    postSocialMedia: true,
    sendSensitiveClientMessages: true
  });

  // Success screen tracker state
  const [wizardDeployedAgent, setWizardDeployedAgent] = useState<any>(null);

  // Admin and feature settings
  const [wizardAdminSettings, setWizardAdminSettings] = useState({
    enforceBalancedRule: true,
    featureRecommendedPreset: true,
    disableRiskyFlows: false,
    planRestrictedMode: false,
    deploymentLog: [] as string[]
  });


  // Server-Side Role and Session Verification Guard Hook
  useEffect(() => {
    const runClearanceCheck = async () => {
      if (currentUserRole === "ADMIN" || isAdminMode) {
        try {
          const data = await authenticatedFetch("/api/admin/verify-clearance", {
            method: "POST"
          });
          const hasAdminClearance = data?.authenticated === true && ['ADMIN', 'SUPER_ADMIN'].includes(data?.role);
          if (!hasAdminClearance) {
            // Revert unauthorized frontend toggle
            setCurrentUserRole("CLIENT");
            setIsAdminMode(false);
            triggerToast("🔒 Server-Side Lock: Admin controls rejected. Unauthorized session or browser forgery detected. All real admin controls are validated server-side.", "error");
          }
        } catch (err) {
          // If unauthenticated or connection fails, reset admin switches to prevent spoofing
          setCurrentUserRole("CLIENT");
          setIsAdminMode(false);
          console.warn("Client clearance validation could not reach key endpoint securely.");
        }
      }
    };
    runClearanceCheck();
  }, [currentUserRole, isAdminMode]);

  const handleOpenApprovalGate = (tpl: AgentTemplate | TaskFlow, onConfirm: () => void) => {
    if (isPublicContactDetailsMissing) {
      triggerToast("⚠️ Action Blocked: Public Contact Details Required!", "error");
      return;
    }
    playNexoraXSound(260, 0.35, "sine");
    setActiveApprovalGateTemplate(tpl);
    setApprovalGateCallback(() => onConfirm);
    setApprovalGateChecks({
      levyVerified: false,
      accessToWorkVerified: false,
      securityVerified: false,
      latencyVerified: false
    });
  };

  // Deploying animation status
  const [deployingTemplateId, setDeployingTemplateId] = useState<string | null>(null);
  const [deploymentProgress, setDeploymentProgress] = useState(0);

  // New template model for Admin creator
  const [showAdminCreator, setShowAdminCreator] = useState(false);
  const [newTemplate, setNewTemplate] = useState<Partial<AgentTemplate>>({
    name: "",
    role: "",
    category: "Executive & Admin",
    bestFor: "",
    tasks: [""],
    setupTime: "3 Minutes",
    automationLevel: 90,
    humanApprovalRequired: true,
    status: "Ready to Deploy",
    description: "",
    toolsConnected: [""],
    knowledgeSources: [""],
    exampleOutput: "",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Elite",
    timeSaved: "10 Hours / Week"
  });

  // Sound cues generator
  const playNexoraXSound = (pitch: number, duration: number, type: OscillatorType = "sine") => {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = type;
      osc.frequency.setValueAtTime(pitch, ctx.currentTime);
      gain.gain.setValueAtTime(0.05, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
      osc.start();
      osc.stop(ctx.currentTime + duration);
    } catch (e) {
      // Ignored browser audio policy
    }
  };

  // Live replacement preview compiler
  useEffect(() => {
    if (!selectedTemplateForCustomise) return;
    const baseText = "Contact [BUSINESS_NAME] today for premium [MAIN_SERVICE] operations. Direct assistance on [PUBLIC_PHONE] or email [PUBLIC_EMAIL]. Brand voice: [BRAND_TONE]";
    let processed = baseText
      .replace(/\[BUSINESS_NAME\]/g, customiseForm.businessName || "[BUSINESS_NAME]")
      .replace(/\[WEBSITE_URL\]/g, customiseForm.websiteUrl || "[WEBSITE_URL]")
      .replace(/\[PUBLIC_EMAIL\]/g, customiseForm.publicEmail || "[PUBLIC_EMAIL]")
      .replace(/\[PUBLIC_PHONE\]/g, customiseForm.publicPhone || "[PUBLIC_PHONE]")
      .replace(/\[BRAND_TONE\]/g, customiseForm.brandVoice || "[BRAND_TONE]")
      .replace(/\[TARGET_CUSTOMER\]/g, customiseForm.targetCustomer || "[TARGET_CUSTOMER]")
      .replace(/\[MAIN_SERVICE\]/g, customiseForm.industry || "[MAIN_SERVICE]")
      .replace(/\[CTA\]/g, customiseForm.cta || "[CTA]")
      .replace(/\[ESCALATION_CONTACT\]/g, customiseForm.escalationContact || "[ESCALATION_CONTACT]")
      .replace(/\[APPROVAL_RULE\]/g, customiseForm.approvalRequirement || "[APPROVAL_RULE]");
    setCustomTextPreview(processed);
  }, [customiseForm, selectedTemplateForCustomise]);

  // Handle Deployment Simulation
  const handleTriggerDeploy = (templateId: string) => {
    // Plan Gating Check
    const reqPlan = templatePlanGating[templateId] || "Starter";
    const planLevels: Record<string, number> = {
      "Starter": 1,
      "Growth": 2,
      "Agency": 3,
      "Enterprise": 4
    };
    const currentLvl = planLevels[clientPlan] || 1;
    const reqLvl = planLevels[reqPlan] || 1;

    if (currentLvl < reqLvl) {
      const parentTpl = templates.find(t => t.id === templateId);
      if (parentTpl) {
        setLockedAgentModal(parentTpl);
        logUpgradeInterest(parentTpl.name, parentTpl.id, clientPlan, reqPlan);
      }
      playNexoraXSound(150, 0.3);
      triggerToast(`⚠️ Gating Block: Deployment requires ${reqPlan.toUpperCase()} subscription.`, "warning");
      return;
    }

    playNexoraXSound(520, 0.45, "triangle");
    setDeployingTemplateId(templateId);
    setDeploymentProgress(0);
    triggerToast("Initializing secure NexoraX Agent sandbox environment...", "info");

    const timer = setInterval(() => {
      setDeploymentProgress(p => {
        if (p >= 100) {
          clearInterval(timer);
          setTimeout(() => {
            setDeployingTemplateId(null);
            playNexoraXSound(840, 0.35, "sine");
            // Increment deployment counter in admin metrics
            setAdminTemplateUsage(prev => {
              const record = prev[templateId] || { deployments: 0, usage: 0, perfScore: 95.0 };
              return {
                ...prev,
                [templateId]: {
                  ...record,
                  deployments: record.deployments + 1,
                  usage: record.usage + 1
                }
              };
            });
            triggerToast("Agent deployed successfully on CloudSync-02 core cluster!", "success");
          }, 300);
          return 100;
        }
        return p + 4;
      });
    }, 80);
  };

  // Filter templates list
  const filteredTemplates = templates.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          t.role.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          t.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "ALL" || t.category === selectedCategory;
    const matchesStatus = selectedStatus === "ALL" || t.status === selectedStatus;
    const matchesApproval = selectedApproval === "ALL" || 
      (selectedApproval === "REQUIRED" && t.humanApprovalRequired) || 
      (selectedApproval === "BYPASS" && !t.humanApprovalRequired);

    return matchesSearch && matchesCategory && matchesStatus && matchesApproval;
  });

  // Safe saving of templates
  const saveTemplatesState = (newT: AgentTemplate[]) => {
    setTemplates(newT);
    localStorage.setItem("nx_agent_templates", JSON.stringify(newT));
  };

  // Customize Form Trigger
  const handleTriggerCustomiseSelection = (tpl: AgentTemplate) => {
    if (isPublicContactDetailsMissing) {
      triggerToast("⚠️ Customisation Blocked: Public Contact Details Required!", "error");
      return;
    }

    // Plan Gating Check
    const reqPlan = templatePlanGating[tpl.id] || "Starter";
    const planLevels: Record<string, number> = {
      "Starter": 1,
      "Growth": 2,
      "Agency": 3,
      "Enterprise": 4
    };
    const currentLvl = planLevels[clientPlan] || 1;
    const reqLvl = planLevels[reqPlan] || 1;

    if (currentLvl < reqLvl) {
      setLockedAgentModal(tpl);
      logUpgradeInterest(tpl.name, tpl.id, clientPlan, reqPlan);
      playNexoraXSound(150, 0.3);
      triggerToast(`⚠️ Lock Screen: Custom template configuration requires ${reqPlan.toUpperCase()} subscription.`, "warning");
      return;
    }

    playNexoraXSound(440, 0.25);
    setSelectedTemplateForCustomise(tpl);
    setCustomiseForm(prev => ({
      ...prev,
      agentDisplayName: tpl.name,
      connectedTools: tpl.toolsConnected.join(", "),
      knowledgeSources: tpl.knowledgeSources.join(", "),
      approvalRequirement: tpl.humanApprovalRequired 
        ? "Require absolute Human Approval before committing external state." 
        : "Automated execution bypass permitted but logs saved to core ledger."
    }));
  };

  // Customise Apply & Deploy
  const handleSaveCustomisedAgent = () => {
    if (isPublicContactDetailsMissing) {
      triggerToast("⚠️ Action Blocked: Public Contact Details Required!", "error");
      return;
    }
    if (!selectedTemplateForCustomise) return;
    playNexoraXSound(620, 0.3);

    // Formulate final prompt logs
    const customizedRecord: AgentTemplate = {
      ...selectedTemplateForCustomise,
      name: customiseForm.agentDisplayName,
      description: `[Custom Agent] ${selectedTemplateForCustomise.description}. Adapted for ${customiseForm.businessName}. Tone style: ${customiseForm.toneStyle}.`,
      toolsConnected: customiseForm.connectedTools.split(",").map(i => i.trim()),
      knowledgeSources: customiseForm.knowledgeSources.split(",").map(i => i.trim()),
      humanApprovalRequired: customiseForm.approvalRequirement.toLowerCase().includes("require") || customiseForm.approvalRequirement.toLowerCase().includes("manual"),
      status: "Ready to Deploy"
    };

    const index = templates.findIndex(t => t.id === selectedTemplateForCustomise.id);
    if (index !== -1) {
      const copy = [...templates];
      copy[index] = customizedRecord;
      saveTemplatesState(copy);
    }
    
    setSelectedTemplateForCustomise(null);
    triggerToast("Customized agent specifications applied to localized registry!", "success");
  };

  // Task Flow Deploy simulation
  const handleDeployTaskFlow = (flow: TaskFlow) => {
    if (isPublicContactDetailsMissing) {
      triggerToast("⚠️ Action Blocked: Public Contact Details Required!", "error");
      return;
    }
    playNexoraXSound(600, 0.4, "sawtooth");
    triggerToast(`Deploying task flow: "${flow.title}" linked to ${flow.agentAssigned}...`, "info");
    
    // Quick progress alert
    setTimeout(() => {
      triggerToast(`Task flow "${flow.title}" successfully compiled and synchronized with the Google Drive Vault.`, "success");
    }, 1500);
  };

  // Create new template - Admin Functionality
  const handleAdminCreateTemplate = () => {
    if (!newTemplate.name || !newTemplate.role) {
      triggerToast("Template Name and Role are required.", "error");
      return;
    }

    const compiled: AgentTemplate = {
      id: "tpl-" + Date.now(),
      name: newTemplate.name,
      role: newTemplate.role,
      category: newTemplate.category || "Executive & Admin",
      bestFor: newTemplate.bestFor || "General administrative workspace tasks",
      tasks: newTemplate.tasks || [],
      setupTime: newTemplate.setupTime || "3 Minutes",
      automationLevel: newTemplate.automationLevel || 90,
      humanApprovalRequired: !!newTemplate.humanApprovalRequired,
      status: (newTemplate.status || "Ready to Deploy") as any,
      description: newTemplate.description || "Synthesised administrative AI worker.",
      toolsConnected: newTemplate.toolsConnected || [],
      knowledgeSources: newTemplate.knowledgeSources || [],
      exampleOutput: newTemplate.exampleOutput || "Automated run success.",
      riskLevel: (newTemplate.riskLevel || "LOW") as any,
      recommendedPlan: newTemplate.recommendedPlan || "NexoraX Elite",
      timeSaved: newTemplate.timeSaved || "10 Hours / Week"
    };

    const updated = [compiled, ...templates];
    saveTemplatesState(updated);
    
    // Generate admin metrics weights
    setAdminTemplateUsage(prev => ({
      ...prev,
      [compiled.id]: { deployments: 0, usage: 0, perfScore: 98.4 }
    }));

    setShowAdminCreator(false);
    triggerToast(`New template "${compiled.name}" successfully written to core registry!`, "success");
  };

  const handleDuplicateTemplate = (tpl: AgentTemplate) => {
    const copy: AgentTemplate = {
      ...tpl,
      id: `${tpl.id}-copy-${Date.now()}`,
      name: `${tpl.name} (Copy)`
    };
    const updated = [copy, ...templates];
    saveTemplatesState(updated);
    setAdminTemplateUsage(prev => ({
      ...prev,
      [copy.id]: { deployments: 0, usage: 0, perfScore: tpl.automationLevel }
    }));
    triggerToast(`Duplicated template "${tpl.name}" successfully`, "success");
  };

  const handleDeleteTemplate = (id: string, name: string) => {
    if (window.confirmNexoraXAction) {
      window.confirmNexoraXAction({
        title: `PERMANENTLY PURGE AGENT TEMPLATE`,
        message: `Are you entirely certain you want to delete "${name}" from the ready-made registry? This protocol is irreversible and destroys custom compliance structures.`,
        impactLevel: "HIGH",
        nodeName: "Node-12.7 Agent Registry",
        confirmLabel: "PURGE TEMPLATE",
        cancelLabel: "KEEP TEMPLATE"
      }, () => {
        const remaining = templates.filter(t => t.id !== id);
        saveTemplatesState(remaining);
        triggerToast("Template purged from the storage registers", "info");
      });
    } else {
      const remaining = templates.filter(t => t.id !== id);
      saveTemplatesState(remaining);
      triggerToast("Template deleted", "info");
    }
  };

  const handleToggleFeature = (id: string) => {
    const updated = templates.map(t => {
      if (t.id === id) {
        return { ...t, isFeatured: !t.isFeatured };
      }
      return t;
    });
    saveTemplatesState(updated);
    triggerToast("Featured status changed", "success");
  };

  const renderPremiumAgentSetupWizard = () => {
    // List of 20 task flows as specified
    const wizardAvailableTaskFlows = [
      { id: "wf-1", title: "New Lead Follow-Up", trigger: "Form Webhook Received", steps: "Enrich details -> Match service template -> Prepare greeting", output: "Draft pending approval in Outbox", requirement: "Manually approved", timeSaved: "4 hours / week", risk: "LOW" },
      { id: "wf-2", title: "Customer Support Reply Draft", trigger: "Incoming email support inquiry", steps: "Analyse question -> Query Knowledge Base -> Compile draft reply", output: "Auto-saved draft answer", requirement: "Manually approved", timeSaved: "6 hours / week", risk: "LOW" },
      { id: "wf-3", title: "Customer Complaint Escalation", trigger: "Negative sentiment rating", steps: "Alert manager -> Classify issue -> Compile incident summary", output: "Internal Slack & Email notification", requirement: "Immediate Human Review", timeSaved: "3 hours / week", risk: "MEDIUM" },
      { id: "wf-4", title: "Daily Email Summary", trigger: "Schedule: 08:00 UTC", steps: "Extract unread emails -> Group key items -> Outline responses", output: "Consolidated Daily Digest Document", requirement: "Informational", timeSaved: "5 hours / week", risk: "LOW" },
      { id: "wf-5", title: "Meeting Notes to Action Plan", trigger: "Audio/transcript upload", steps: "Transcribe -> Auto-identify action items -> Map deadlines", output: "Formatted task checklist and summary", requirement: "Client review required", timeSaved: "4 hours / week", risk: "LOW" },
      { id: "wf-6", title: "Social Post Creation", trigger: "Weekly Content Planner", steps: "Select topic -> Generate visual prompts -> Draft description", output: "Buffer profile post drafts queue", requirement: "Manually approved", timeSaved: "3 hours / week", risk: "MEDIUM" },
      { id: "wf-7", title: "Product Research Comparison", trigger: "Client inquiry about specs", steps: "Query competitor URLs -> Extract terms -> Format table", output: "Interactive comparison spreadsheet", requirement: "Autopilot draft", timeSaved: "8 hours / week", risk: "LOW" },
      { id: "wf-8", title: "Supplier Shortlist", trigger: "New Project Charter", steps: "Read target specs -> Match available suppliers -> Score records", output: "Ranked supplier recommendation card", requirement: "Manually approved", timeSaved: "5 hours / week", risk: "LOW" },
      { id: "wf-9", title: "Client Onboarding Checklist", trigger: "New client contract signed", steps: "Create shared drive -> Initiate calendar link -> Sync form", output: "Initial welcome briefing document", requirement: "Autopilot draft", timeSaved: "2 hours / week", risk: "LOW" },
      { id: "wf-10", title: "Invoice Reminder Draft", trigger: "Overdue payment status trigger", steps: "Check invoice date -> Match client contact -> State reminders", output: "Soft-tone reminder email copy", requirement: "Human approval protect", timeSaved: "3 hours / week", risk: "MEDIUM" },
      { id: "wf-11", title: "Weekly Performance Report", trigger: "Schedule: Every Friday", steps: "Aggregate active pipeline records -> Format chart metrics", output: "Interactive dashboard report", requirement: "Informational", timeSaved: "4 hours / week", risk: "LOW" },
      { id: "wf-12", title: "CRM Update Flow", trigger: "Contact status modification", steps: "Scan client feedback -> Refresh system metadata logs", output: "Synchronized CRM list records", requirement: "Autopilot draft", timeSaved: "6 hours / week", risk: "LOW" },
      { id: "wf-13", title: "Demo Booking Flow", trigger: "Inquiry on premium tier booking", steps: "Match advisor schedules -> Reserve 15m slot -> Send confirmation", output: "Calendar reservation link entry", requirement: "Human approval protect", timeSaved: "4 hours / week", risk: "LOW" },
      { id: "wf-14", title: "Review Request Flow", trigger: "Successful delivery milestone", steps: "Wait 48h -> Prepare custom feedback link", output: "Polite survey questionnaire alert", requirement: "Autopilot draft", timeSaved: "2 hours / week", risk: "LOW" },
      { id: "wf-15", title: "Website Update Announcement", trigger: "New feature log update", steps: "Draft change summary -> Format visual markdown bullet logs", output: "Customer announcement notification copy", requirement: "Manually approved", timeSaved: "3 hours / week", risk: "LOW" },
      { id: "wf-16", title: "New Service Promo Draft", trigger: "Internal service deployment plan", steps: "Align target audience -> Choose voice parameters -> Draft promo copy", output: "Promotional system notification sheet", requirement: "Manually approved", timeSaved: "4 hours / week", risk: "MEDIUM" },
      { id: "wf-17", title: "Support Ticket Triage", trigger: "New ticket incoming", steps: "Assess technical difficulty -> Route to specialized desk", output: "Auto-classified ticket routing status", requirement: "Autopilot draft", timeSaved: "7 hours / week", risk: "LOW" },
      { id: "wf-18", title: "Churn Risk Alert", trigger: "Zero activity for 14 days", steps: "Flag account -> Retrieve recent support ticket history -> Compile audit", output: "Slack notification alert to Accounts Director", requirement: "Immediate Human Review", timeSaved: "3 hours / week", risk: "MEDIUM" },
      { id: "wf-19", title: "Monthly Executive Brief", trigger: "Schedule: Monthly", steps: "Aggregate high-level financial outcomes -> Summarise logs", output: "Strategic corporate review draft", requirement: "Informational", timeSaved: "10 hours / week", risk: "LOW" },
      { id: "wf-20", title: "Automation Ideas Report", trigger: "Monthly operational ledger query", steps: "Compile performance feedback -> Detect visual bottleneck nodes", output: "Interactive pipeline options draft", requirement: "Informational", timeSaved: "6 hours / week", risk: "LOW" }
    ];

    // Safe live replacement logic for step 2 preview card
    const processLiveTemplateIntro = () => {
      let draftIntro = "I help [BUSINESS_NAME] respond to customer requests, prepare helpful replies, and escalate sensitive issues for human approval.";
      
      // Let's replace placeholders safely
      const safeBusiness = wizardAgentForm.businessName || "Your Business";
      const safeWebsite = wizardAgentForm.websiteUrl || publicContactSettings.websiteUrl;
      const safeEmail = wizardAgentForm.publicEmail || publicContactSettings.publicEmail;
      const safePhone = wizardAgentForm.publicPhone || publicContactSettings.publicPhone;
      const safeTone = wizardAgentForm.toneStyle || "Professional";
      const safeCustomer = wizardAgentForm.targetCustomer || "target customers";
      const safeService = wizardAgentForm.mainService || "services";
      const safeCta = wizardAgentForm.cta || "get started";
      const safeEscalation = wizardAgentForm.escalationContact || "";
      const safeRule = wizardAgentForm.approvalRule || "";

      // Perform strict, safe local replacements
      const processed = draftIntro
        .replace(/\[BUSINESS_NAME\]/g, safeBusiness)
        .replace(/\[WEBSITE_URL\]/g, safeWebsite)
        .replace(/\[PUBLIC_EMAIL\]/g, safeEmail)
        .replace(/\[PUBLIC_PHONE\]/g, safePhone)
        .replace(/\[BRAND_TONE\]/g, safeTone)
        .replace(/\[TARGET_CUSTOMER\]/g, safeCustomer)
        .replace(/\[MAIN_SERVICE\]/g, safeService)
        .replace(/\[CTA\]/g, safeCta)
        .replace(/\[ESCALATION_CONTACT\]/g, safeEscalation)
        .replace(/\[APPROVAL_RULE\]/g, safeRule);

      return processed;
    };

    // Calculate aggregated metrics for workflow preview (Step 6)
    const getWizardMetrics = () => {
      let totalTimeSaved = 0;
      let riskScoreValue = 10; // base score
      let selectionCount = wizardSelectedTaskFlows.length;

      wizardSelectedTaskFlows.forEach(flowTitle => {
        const matchingFlow = wizardAvailableTaskFlows.find(f => f.title === flowTitle);
        if (matchingFlow) {
          const numberOnly = parseInt(matchingFlow.timeSaved) || 3;
          totalTimeSaved += numberOnly;
          if (matchingFlow.risk === "MEDIUM") riskScoreValue += 20;
          if (matchingFlow.risk === "HIGH") riskScoreValue += 40;
        }
      });

      // Factor in selected template risk
      if (wizardSelectedTemplate) {
        if (wizardSelectedTemplate.riskLevel === "MEDIUM") riskScoreValue += 15;
        if (wizardSelectedTemplate.riskLevel === "HIGH") riskScoreValue += 35;
      }

      // Decrement risk based on safety levels
      if (wizardApprovalLevel === "Low") riskScoreValue -= 15;
      if (wizardApprovalLevel === "Balanced") riskScoreValue -= 10;
      if (wizardApprovalLevel === "Admin-controlled") riskScoreValue -= 20;

      // Bound final scores
      const finalRiskScore = Math.min(Math.max(riskScoreValue, 5), 95);
      const readyStatePercentage = Math.min(100 - Math.floor(finalRiskScore / 4) + (wizardSelectedTemplate ? 10 : 0), 100);

      return {
        hoursSaved: totalTimeSaved * 4, // converted to estimated monthly hours
        riskScore: finalRiskScore,
        readiness: readyStatePercentage
      };
    };

    const metrics = getWizardMetrics();

    // Reset setup wizard
    const handleResetWizard = () => {
      playNexoraXSound(200, 0.2);
      setWizardStep(1);
      setWizardSelectedTemplate(null);
      setWizardSelectedTaskFlows(["New Lead Follow-Up", "Customer Support Reply Draft", "Daily Email Summary"]);
      setWizardDeployedAgent(null);
      triggerToast("Agent setup wizard registers reset successfully.", "info");
    };

    // Confirm deploy starting simulated execution logger
    const handleNexoraXDeployWizardAgent = () => {
      if (!wizardSelectedTemplate) {
        triggerToast("Please select an Agent Template in Step 1.", "error");
        setWizardStep(1);
        return;
      }

      playNexoraXSound(520, 0.45, "triangle");
      setWizardStep(6); // stay on workflow preview while launching
      
      triggerToast("Initializing secure agent compile sequence...", "info");
      
      // Let's create a simulated compiling logging state
      let progress = 0;
      const interval = setInterval(() => {
        progress += 20;
        if (progress <= 100) {
          triggerToast(`NexoraX Deploy Progress: ${progress}% - compiling telemetry...`, "info");
        } else {
          clearInterval(interval);
          // Auto route to page 7 Success
          const finalizedAgent = {
            id: "wizard-agent-" + Date.now(),
            name: wizardAgentForm.agentDisplayName || wizardSelectedTemplate.name,
            templateName: wizardSelectedTemplate.name,
            activeTaskFlows: [...wizardSelectedTaskFlows],
            toolsList: Object.keys(wizardConnectedTools).filter(key => wizardConnectedTools[key] === "CONNECTED"),
            approvalLevel: wizardApprovalLevel,
            firstTask: "Process Incoming Message Queues at " + new Date().toLocaleTimeString(),
            monthlySaved: metrics.hoursSaved
          };

          setWizardDeployedAgent(finalizedAgent);
          
          // Inject custom agent into the general templates state
          const customAgentRecord: AgentTemplate = {
            id: finalizedAgent.id,
            name: finalizedAgent.name,
            role: wizardSelectedTemplate.role,
            category: wizardSelectedTemplate.category,
            bestFor: `Custom enterprise integration for ${wizardAgentForm.businessName}. Website: ${wizardAgentForm.websiteUrl}`,
            tasks: finalizedAgent.activeTaskFlows,
            setupTime: "Instant Deploy",
            automationLevel: 100 - metrics.riskScore,
            humanApprovalRequired: wizardApprovalLevel !== "High",
            status: "Ready to Deploy",
            description: `Tailored ${wizardSelectedTemplate.name} customized for ${wizardAgentForm.businessName}. Active Tone: ${wizardAgentForm.toneStyle}. Contact Point: ${wizardAgentForm.publicPhone}.`,
            toolsConnected: finalizedAgent.toolsList,
            knowledgeSources: ["Enterprise SOP System", "Brand Identity Guidelines"],
            exampleOutput: `[LAUNCH_OK] Dynamic workflow ready. Routed under ${finalizedAgent.approvalLevel} automation metrics parameters.`,
            riskLevel: metrics.riskScore > 60 ? "HIGH" : metrics.riskScore > 30 ? "MEDIUM" : "LOW",
            recommendedPlan: "NexoraX Tiered Level-Ready",
            timeSaved: `${matchingTimeSaved(wizardSelectedTemplate.id)} / Week`
          };

          // Save to local registry so it replicates in main tabs
          const updatedTemplates = [customAgentRecord, ...templates];
          setTemplates(updatedTemplates);
          localStorage.setItem("nx_agent_templates", JSON.stringify(updatedTemplates));

          playNexoraXSound(960, 0.6, "sine");
          setWizardStep(7);
          triggerToast("Successfully compiled & launched AI agent!", "success");
        }
      }, 500);
    };

    const matchingTimeSaved = (id: string) => {
      const found = templates.find(t => t.id === id);
      return found ? found.timeSaved : "12 Hours";
    };

    return (
      <div className="space-y-6">
        {/* Title Block Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-gradient-to-r from-purple-950/20 to-indigo-950/20 border border-purple-500/10 p-6 rounded-3xl">
          <div className="text-left space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 bg-pink-500/10 border border-pink-500/30 text-pink-400 font-mono text-[8.5px] uppercase font-bold rounded">
                Guided SaaS Onboarding
              </span>
              <span className="text-zinc-500 font-mono text-[8.5px]">Step {wizardStep} of 7</span>
            </div>
            <h2 className="text-2xl font-black font-mono text-white tracking-tight uppercase">
              Deploy Your AI Agent
            </h2>
            <p className="text-xs text-zinc-400 font-sans normal-case">
              Choose a template. Customise the workflow. Launch safely.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button 
              onClick={handleResetWizard}
              className="px-3 py-1.5 border border-zinc-800 text-zinc-500 hover:text-zinc-300 font-mono text-[9px] uppercase tracking-wider rounded-lg transition-colors cursor-pointer"
            >
              🔄 RESET WIZARD
            </button>
            {currentUserRole === "ADMIN" && (
              <span className="px-3 py-1.5 bg-red-950/30 border border-red-500/30 text-red-400 rounded-lg font-mono text-[9px] uppercase font-bold">
                Admin Settings Connected
              </span>
            )}
          </div>
        </div>

        {/* Setup Progress Dashboard Line */}
        <div className="bg-black/80 border border-zinc-900 rounded-2xl p-4">
          <div className="flex flex-col sm:flex-row justify-between gap-3 text-left">
            {[
              { num: 1, name: "Template" },
              { num: 2, name: "Identity" },
              { num: 3, name: "Task Flows" },
              { num: 4, name: "Integrations" },
              { num: 5, name: "Guardrails" },
              { num: 6, name: "Validate" },
              { num: 7, name: "Active" }
            ].map(step => (
              <button
                key={step.num}
                onClick={() => {
                  if (step.num <= wizardStep || wizardSelectedTemplate) {
                    playNexoraXSound(250 + step.num * 40, 0.1);
                    setWizardStep(step.num);
                  } else {
                    triggerToast("Please select a template in Step 1 first.", "warning");
                  }
                }}
                className={`flex-1 flex items-center gap-2 pb-2 border-b-2 transition-all cursor-pointer text-left ${
                  wizardStep === step.num 
                    ? "border-pink-500 text-pink-400 font-bold" 
                    : step.num < wizardStep
                      ? "border-cyan-500 text-cyan-400 font-bold"
                      : "border-zinc-800 text-zinc-600"
                }`}
              >
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-mono font-bold ${
                  wizardStep === step.num 
                    ? "bg-pink-500 text-black" 
                    : step.num < wizardStep
                      ? "bg-cyan-500 text-black"
                      : "bg-zinc-800 text-zinc-400"
                }`}>
                  {step.num < wizardStep ? "✓" : step.num}
                </span>
                <span className="font-mono text-[9px] uppercase whitespace-nowrap tracking-wide">
                  {step.name}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* WIZARD PANEL VIEWS CONTENT SWITCHER */}
        <div className="min-h-[400px]">
          {/* STEP 1: CHOOSE TARGET AGENT TEMPLATE */}
          {wizardStep === 1 && (
            <div className="space-y-6 animate-fadeIn">
              <div className="bg-purple-950/20 p-4 border border-purple-500/10 rounded-2xl text-left">
                <span className="font-mono text-[9px] text-[#e840f3] font-bold block uppercase tracking-widest mb-1">
                  Step 1: Core Selection
                </span>
                <h3 className="text-sm font-black font-mono text-white uppercase">
                  Select Pre-Configured Agent Blueprint Template
                </h3>
                <p className="text-xs text-zinc-400 font-sans leading-relaxed mt-1">
                  Choose a high-performance blueprint aligned to your primary business activity. Blueprints come secure, containing optimized prompt architectures.
                </p>
              </div>

              {/* Department chips in wizard */}
              <div className="flex flex-wrap gap-1 border-b border-zinc-900 pb-3 font-mono text-[8.5px]">
                {["ALL DEPARTMENTS", ...AGENT_CATEGORIES].map((dept, i) => {
                  const queryNorm = dept.replace("&", "and").replace(" Support", "").toUpperCase();
                  const mapSel = selectedCategory === "ALL" ? "ALL DEPARTMENTS" : selectedCategory;
                  return (
                    <button
                      key={i}
                      onClick={() => {
                        playNexoraXSound(200 + i * 15, 0.1);
                        setSelectedCategory(dept === "ALL DEPARTMENTS" ? "ALL" : dept);
                      }}
                      className={`px-3 py-1 rounded-md border transition-all ${
                        (dept === "ALL DEPARTMENTS" && selectedCategory === "ALL") || selectedCategory === dept
                          ? "border-pink-500 bg-pink-950/15 text-pink-400 font-bold" 
                          : "border-zinc-850 bg-black/40 text-zinc-500 hover:text-zinc-300"
                      }`}
                    >
                      {dept.toUpperCase()}
                    </button>
                  );
                })}
              </div>

              {/* Featured Template Row (indicated in wizard search) */}
              <div className="space-y-3 text-left">
                <div className="flex items-center gap-1.5 text-zinc-400 font-mono text-[9px] uppercase tracking-wider">
                  <Sparkles className="w-3.5 h-3.5 text-pink-400 animate-pulse" />
                  <span>Recommend Featured Blueprints:</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {templates
                    .filter(t => iContainsFeaturedName(t.name) && (selectedCategory === "ALL" || t.category === selectedCategory))
                    .map(t => (
                      <div 
                        key={`wizard-featured-${t.id}`}
                        onClick={() => {
                          playNexoraXSound(360, 0.2);
                          setWizardSelectedTemplate(t);
                          setWizardAgentForm(prev => ({
                            ...prev,
                            agentDisplayName: t.name + " (" + wizardAgentForm.businessName + " Assistant)"
                          }));
                          triggerToast(`Selected ${t.name} blueprint template!`, "success");
                        }}
                        className={`p-4 rounded-2xl border transition-all text-left flex flex-col justify-between cursor-pointer group relative ${
                          wizardSelectedTemplate?.id === t.id
                            ? "border-pink-400 bg-gradient-to-b from-pink-950/15 to-transparent shadow-[0_0_20px_rgba(236,72,153,0.15)]"
                            : "border-purple-500/10 bg-black/60 hover:bg-zinc-900/10 hover:border-purple-500/25"
                        }`}
                      >
                        {wizardSelectedTemplate?.id === t.id && (
                          <div className="absolute top-2 right-2 text-[8px] bg-pink-500 text-black font-bold font-mono py-0.5 px-1.5 rounded uppercase tracking-wider">
                            ACTIVE SELECT
                          </div>
                        )}
                        <div className="space-y-1.5 text-left">
                          <span className="font-mono text-[7.5px] text-zinc-500 uppercase block font-black leading-none">{t.category}</span>
                          <h4 className="text-xs font-mono font-black text-white uppercase select-none line-clamp-1 group-hover:text-pink-400 transition-colors">{t.name}</h4>
                          <p className="text-[10px] text-zinc-400 leading-normal font-sans line-clamp-2">{t.description}</p>
                        </div>

                        <div className="mt-3 pt-2.5 border-t border-zinc-900 flex justify-between items-center text-[8.5px] font-mono text-zinc-500">
                          <span>SLA: <strong className="text-emerald-400 font-bold">{t.timeSaved}</strong></span>
                          <span>RISK: <strong className={t.riskLevel === "HIGH" ? "text-rose-400 font-bold" : "text-zinc-500 font-bold"}>{t.riskLevel}</strong></span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Scrollable grid matching categories */}
              <div className="space-y-3 text-left border-t border-zinc-900 pt-5">
                <span className="font-mono text-[8.5px] text-zinc-500 uppercase block font-black tracking-widest">
                  👾 BROWSE GENERAL REGISTRY FILES
                </span>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {templates
                    .filter(t => selectedCategory === "ALL" || t.category === selectedCategory)
                    .map(t => (
                      <div 
                        key={`wizard-normal-${t.id}`}
                        onClick={() => {
                          playNexoraXSound(300, 0.15);
                          setWizardSelectedTemplate(t);
                          setWizardAgentForm(prev => ({
                            ...prev,
                            agentDisplayName: t.name + " (" + wizardAgentForm.businessName + " Assistant)"
                          }));
                          triggerToast(`Selected ${t.name}!`, "success");
                        }}
                        className={`p-4 rounded-xl border transition-all flex justify-between items-center cursor-pointer select-none text-left ${
                          wizardSelectedTemplate?.id === t.id
                            ? "border-pink-500 bg-pink-950/10"
                            : "border-zinc-800/80 bg-black/40 hover:border-zinc-700"
                        }`}
                      >
                        <div className="space-y-1">
                          <h4 className="text-xs font-mono font-bold text-white uppercase leading-tight">{t.name}</h4>
                          <p className="text-[9.5px] text-zinc-500 font-sans break-words max-w-md line-clamp-1">{t.bestFor}</p>
                          <div className="flex gap-2 text-[7.5px] font-mono text-zinc-500 uppercase items-center">
                            <span>Saves: <strong className="text-emerald-400 font-bold">{t.timeSaved}</strong></span>
                            <span>&bull;</span>
                            <span>SLA level: <strong className="text-cyan-400 font-bold">{t.automationLevel}% Autopilot</strong></span>
                            <span>&bull;</span>
                            <span>Approval required: <strong className={t.humanApprovalRequired ? "text-amber-400" : "text-zinc-500"}>{t.humanApprovalRequired ? "YES" : "NO"}</strong></span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1 shrink-0 bg-black/60 p-1 rounded-lg border border-zinc-800">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              playNexoraXSound(400, 0.1);
                              setDetailTemplate(t);
                            }}
                            className="p-1 text-zinc-400 hover:text-white text-[8px] font-mono font-bold uppercase rounded hover:bg-zinc-800 cursor-pointer"
                          >
                            PREVIEW
                          </button>
                          <div className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center font-mono text-[8px] text-black ${
                            wizardSelectedTemplate?.id === t.id
                              ? "bg-pink-500 border-pink-400"
                              : "border-zinc-700"
                          }`}>
                            {wizardSelectedTemplate?.id === t.id ? "✓" : ""}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Progress and bottom action */}
              <div className="flex justify-between items-center border-t border-zinc-900 pt-5">
                <span className="text-[10px] text-zinc-500 font-mono">
                  {wizardSelectedTemplate 
                    ? `SELECTED BLUEPRINT: ${wizardSelectedTemplate.name.toUpperCase()}`
                    : "No Blueprint Selected"}
                </span>

                <button
                  type="button"
                  disabled={!wizardSelectedTemplate}
                  onClick={() => {
                    playNexoraXSound(450, 0.25);
                    setWizardStep(2);
                  }}
                  className={`px-6 py-2.5 rounded-xl font-mono text-[10px] uppercase tracking-widest font-black transition-all flex items-center gap-1.5 cursor-pointer ${
                    wizardSelectedTemplate
                      ? "bg-pink-500 text-black border border-pink-400 hover:bg-pink-400 shadow-[0_0_15px_rgba(236,72,153,0.3)]"
                      : "bg-zinc-900 text-zinc-600 cursor-not-allowed"
                  }`}
                >
                  <span>Accept and Continue Identity</span>
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: CUSTOMISE AGENT IDENTITY */}
          {wizardStep === 2 && (
            <div className="space-y-6 animate-fadeIn">
              <div className="bg-purple-950/20 p-4 border border-purple-500/10 rounded-2xl text-left">
                <span className="font-mono text-[9px] text-[#e840f3] font-bold block uppercase tracking-widest mb-1">
                  Step 2: Brand Identity Sync
                </span>
                <h3 className="text-sm font-black font-mono text-white uppercase">
                  Personalise Chosen Template Parameters
                </h3>
                <p className="text-xs text-zinc-400 font-sans leading-relaxed mt-1">
                  Modify display text configurations. Placed fields map directly to dynamic workflow models. Placeholders will resolve naturally in live chat pipelines.
                </p>
              </div>

              {/* Grid 2 Column form + live preview */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 text-left">
                
                {/* Form fields */}
                <div className="lg:col-span-7 space-y-4 bg-black/60 border border-zinc-900 p-5 rounded-2xl">
                  <span className="font-mono text-[8px] text-zinc-400 block font-bold uppercase tracking-widest pb-2 border-b border-zinc-800">IDENTITY FORM CONFIG</span>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs font-mono">
                    
                    <div className="space-y-1.5">
                      <span className="text-zinc-400 text-[9px] font-bold block uppercase">Agent Display Name:</span>
                      <input 
                        type="text"
                        value={wizardAgentForm.agentDisplayName}
                        onChange={(e) => setWizardAgentForm({...wizardAgentForm, agentDisplayName: e.target.value})}
                        className="w-full bg-slate-950 border border-zinc-800 rounded-xl p-2.5 outline-none focus:border-pink-500 text-white"
                        placeholder="e.g. support bot"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <span className="text-zinc-400 text-[9px] font-bold block uppercase">Business Name:</span>
                      <input 
                        type="text"
                        value={wizardAgentForm.businessName}
                        onChange={(e) => setWizardAgentForm({...wizardAgentForm, businessName: e.target.value})}
                        className="w-full bg-slate-950 border border-zinc-800 rounded-xl p-2.5 outline-none focus:border-pink-500 text-white"
                        placeholder="e.g. NexoraX"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <span className="text-zinc-400 text-[9px] font-bold block uppercase">Website URL:</span>
                      <input 
                        type="text"
                        value={wizardAgentForm.websiteUrl}
                        onChange={(e) => setWizardAgentForm({...wizardAgentForm, websiteUrl: e.target.value})}
                        className="w-full bg-slate-950 border border-zinc-800 rounded-xl p-2.5 outline-none focus:border-pink-500 text-white"
                        placeholder="e.g. https://domain.com"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <span className="text-zinc-400 text-[9px] font-bold block uppercase">Public Email:</span>
                      <input 
                        type="text"
                        value={wizardAgentForm.publicEmail}
                        onChange={(e) => setWizardAgentForm({...wizardAgentForm, publicEmail: e.target.value})}
                        className="w-full bg-slate-950 border border-zinc-800 rounded-xl p-2.5 outline-none focus:border-pink-500 text-white"
                        placeholder="e.g. support@domain.com"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <span className="text-zinc-400 text-[9px] font-bold block uppercase">Public Phone:</span>
                      <input 
                        type="text"
                        value={wizardAgentForm.publicPhone}
                        onChange={(e) => setWizardAgentForm({...wizardAgentForm, publicPhone: e.target.value})}
                        className="w-full bg-slate-950 border border-zinc-800 rounded-xl p-2.5 outline-none focus:border-pink-500 text-white"
                        placeholder="e.g. 08000 622 827"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <span className="text-zinc-400 text-[9px] font-bold block uppercase">Industry Category:</span>
                      <input 
                        type="text"
                        value={wizardAgentForm.industry}
                        onChange={(e) => setWizardAgentForm({...wizardAgentForm, industry: e.target.value})}
                        className="w-full bg-slate-950 border border-zinc-800 rounded-xl p-2.5 outline-none focus:border-pink-500 text-white"
                        placeholder="e.g. SaaS / Ecommerce"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <span className="text-zinc-400 text-[9px] font-bold block uppercase">Target Customer:</span>
                      <input 
                        type="text"
                        value={wizardAgentForm.targetCustomer}
                        onChange={(e) => setWizardAgentForm({...wizardAgentForm, targetCustomer: e.target.value})}
                        className="w-full bg-slate-950 border border-zinc-800 rounded-xl p-2.5 outline-none focus:border-pink-500 text-white"
                        placeholder="e.g. business owners"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <span className="text-zinc-400 text-[9px] font-bold block uppercase">Main Service / Offer:</span>
                      <input 
                        type="text"
                        value={wizardAgentForm.mainService}
                        onChange={(e) => setWizardAgentForm({...wizardAgentForm, mainService: e.target.value})}
                        className="w-full bg-slate-950 border border-zinc-800 rounded-xl p-2.5 outline-none focus:border-pink-500 text-white"
                        placeholder="e.g. automated setup support"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <span className="text-zinc-400 text-[9px] font-bold block uppercase">Brand Voice:</span>
                      <input 
                        type="text"
                        value={wizardAgentForm.brandVoice}
                        onChange={(e) => setWizardAgentForm({...wizardAgentForm, brandVoice: e.target.value})}
                        className="w-full bg-slate-950 border border-zinc-800 rounded-xl p-2.5 outline-none focus:border-pink-500 text-white"
                        placeholder="e.g. highly responsive, firm"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <span className="text-zinc-400 text-[9px] font-bold block uppercase">Tone Style:</span>
                      <select 
                        value={wizardAgentForm.toneStyle}
                        onChange={(e) => setWizardAgentForm({...wizardAgentForm, toneStyle: e.target.value})}
                        className="w-full bg-slate-950 border border-zinc-800 rounded-xl p-2.5 outline-none focus:border-pink-500 text-white cursor-pointer"
                      >
                        <option value="Professional">Professional</option>
                        <option value="Friendly">Friendly</option>
                        <option value="Premium">Premium</option>
                        <option value="Direct">Direct</option>
                        <option value="Helpful">Helpful</option>
                        <option value="Technical">Technical</option>
                        <option value="Executive">Executive</option>
                      </select>
                    </div>

                    <div className="space-y-1.5 sm:col-span-2">
                      <span className="text-zinc-400 text-[9px] font-bold block uppercase">Call to Action (CTA):</span>
                      <input 
                        type="text"
                        value={wizardAgentForm.cta}
                        onChange={(e) => setWizardAgentForm({...wizardAgentForm, cta: e.target.value})}
                        className="w-full bg-slate-950 border border-zinc-800 rounded-xl p-2.5 outline-none focus:border-pink-500 text-white"
                        placeholder="e.g. call or book details"
                      />
                    </div>

                    <div className="space-y-1.5 sm:col-span-2">
                      <span className="text-zinc-400 text-[9px] font-bold block uppercase">Internal Escalation Email / Contact:</span>
                      <input 
                        type="text"
                        value={wizardAgentForm.escalationContact}
                        onChange={(e) => setWizardAgentForm({...wizardAgentForm, escalationContact: e.target.value})}
                        className="w-full bg-slate-950 border border-zinc-800 rounded-xl p-2.5 outline-none focus:border-pink-500 text-white"
                        placeholder="e.g. director@domain.com"
                      />
                    </div>

                  </div>
                </div>

                {/* Right col: live preview telemetry card */}
                <div className="lg:col-span-5 space-y-4">
                  
                  {/* Holographic identity template preview */}
                  <div className="bg-gradient-to-b from-[#1c083c] to-black border-2 border-pink-500/35 p-5 rounded-2xl shadow-[0_0_25px_rgba(236,72,153,0.1)] relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-3 text-[8px] font-mono text-pink-500/50 font-black uppercase tracking-wider">
                      LIVE PREVIEW FEED
                    </div>
                    
                    <span className="w-3 h-3 rounded-full bg-pink-500 absolute top-4 left-4 animate-ping" />
                    
                    <div className="space-y-5 pt-4 text-left">
                      <div className="border-b border-zinc-800 pb-3">
                        <span className="text-[7.5px] text-zinc-500 font-mono block uppercase tracking-widest leading-none">ACTIVE AGENT NAME</span>
                        <h4 className="text-base font-mono font-black text-white uppercase mt-1">
                          {wizardAgentForm.agentDisplayName || wizardSelectedTemplate?.name || "Unbranded AI Worker"}
                        </h4>
                        <span className="text-[8px] text-pink-400 font-mono font-bold uppercase tracking-wider leading-none">
                          {wizardSelectedTemplate?.role || "Operational Liaison"}
                        </span>
                      </div>

                      {/* Live processed text replacement */}
                      <div className="space-y-2">
                        <span className="text-[7.5px] text-zinc-500 font-mono block uppercase tracking-widest leading-none">PROMETHEUS INTRO SPEECH:</span>
                        <div className="p-3 bg-black/80 border border-zinc-900 rounded-xl min-h-[90px]">
                          <p className="text-xs text-zinc-300 font-sans leading-relaxed normal-case select-all">
                            &quot;{processLiveTemplateIntro()}&quot;
                          </p>
                        </div>
                      </div>

                      {/* Display replacement telemetry details */}
                      <div className="bg-zinc-950/60 p-3 rounded-xl border border-zinc-900 space-y-1 text-[8.5px] font-mono text-zinc-500">
                        <div className="flex justify-between">
                          <span>Business Name:</span>
                          <span className="text-white">{wizardAgentForm.businessName}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Tone Parameter:</span>
                          <span className="text-pink-400 font-bold uppercase">{wizardAgentForm.toneStyle}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Primary Target:</span>
                          <span className="text-zinc-300">{wizardAgentForm.targetCustomer}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Active Service:</span>
                          <span className="text-zinc-300 truncate max-w-[150px]">{wizardAgentForm.mainService}</span>
                        </div>
                      </div>

                      <div className="p-2.5 bg-pink-950/20 border border-pink-500/10 rounded-xl space-y-1">
                        <span className="text-[7.5px] text-pink-400 font-mono font-black uppercase tracking-wider block">APPROVED PLACEHOLDER RULES</span>
                        <p className="text-[8px] text-zinc-400 font-sans leading-normal normal-case">
                          The system enforces absolute data compliance. Placeholders resolve variables securely. Private credentials are fully isolated server-side.
                        </p>
                      </div>

                    </div>
                  </div>

                  {/* Warning Box */}
                  <div className="bg-black/40 border border-zinc-900 p-4 rounded-xl text-xs font-sans text-zinc-400 leading-normal text-left">
                    <span className="text-[8px] font-mono font-black text-rose-400 uppercase tracking-widest block mb-1">🛡️ TELEMETRY RESTRICTION FILTER</span>
                    Strict system sandbox parameters protect financial secrecy. Placeholders representing account records, banking networks, or private phone frequencies cannot be parsed in draft previews.
                  </div>

                </div>

              </div>

              {/* Progress and bottom actions */}
              <div className="flex justify-between items-center border-t border-zinc-900 pt-5">
                <button
                  type="button"
                  onClick={() => {
                    playNexoraXSound(200, 0.2);
                    setWizardStep(1);
                  }}
                  className="px-5 py-2.5 border border-zinc-800 text-zinc-400 hover:text-white font-mono text-[10px] uppercase rounded-xl transition-all cursor-pointer"
                >
                  Back to Template
                </button>

                <button
                  type="button"
                  disabled={!wizardAgentForm.agentDisplayName || !wizardAgentForm.businessName}
                  onClick={() => {
                    playNexoraXSound(450, 0.25);
                    setWizardStep(3);
                  }}
                  className={`px-6 py-2.5 rounded-xl font-mono text-[10px] uppercase tracking-widest font-black transition-all flex items-center gap-1.5 cursor-pointer ${
                    wizardAgentForm.agentDisplayName && wizardAgentForm.businessName
                      ? "bg-pink-500 text-black border border-pink-400 hover:bg-pink-400 shadow-[0_0_15px_rgba(236,72,153,0.3)]"
                      : "bg-zinc-900 text-zinc-600 cursor-not-allowed"
                  }`}
                >
                  <span>Select Automated Task Flows</span>
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: SELECT TASK FLOWS */}
          {wizardStep === 3 && (
            <div className="space-y-6 animate-fadeIn">
              <div className="bg-purple-950/20 p-4 border border-purple-500/10 rounded-2xl text-left">
                <span className="font-mono text-[9px] text-[#e840f3] font-bold block uppercase tracking-widest mb-1">
                  Step 3: Workflow Pipeline Actions
                </span>
                <h3 className="text-sm font-black font-mono text-white uppercase">
                  Choose Task Flows For Your Custom Agent
                </h3>
                <p className="text-xs text-zinc-400 font-sans leading-relaxed mt-1">
                  Select key operational tasks. The agent will execute these flows autonomously or save draft logs under human verification gates. Checkboxes register flows to the runtime template.
                </p>
              </div>

              {/* List of 20 flows in visually pleasing responsive layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                {wizardAvailableTaskFlows.map((flow) => {
                  const isSelected = wizardSelectedTaskFlows.includes(flow.title);
                  return (
                    <div 
                      key={flow.id}
                      onClick={() => {
                        playNexoraXSound(isSelected ? 205 : 390, 0.15);
                        if (isSelected) {
                          setWizardSelectedTaskFlows(wizardSelectedTaskFlows.filter(f => f !== flow.title));
                        } else {
                          setWizardSelectedTaskFlows([...wizardSelectedTaskFlows, flow.title]);
                        }
                      }}
                      className={`p-4 border rounded-xl transition-all flex flex-col justify-between cursor-pointer relative group ${
                        isSelected 
                          ? "border-pink-500 bg-gradient-to-b from-pink-950/10 to-transparent shadow-[0_0_15px_rgba(236,72,153,0.06)]"
                          : "border-zinc-800 bg-black/60 hover:border-zinc-700"
                      }`}
                    >
                      {/* Check mark badge */}
                      {isSelected && (
                        <div className="absolute top-2.5 right-2.5 bg-pink-500 text-black p-0.5 rounded-full w-4 h-4 flex items-center justify-center font-mono text-[9px] font-black">
                          ✓
                        </div>
                      )}

                      <div className="space-y-1.5 text-left">
                        <span className="text-[7.5px] text-zinc-500 uppercase tracking-wider block font-bold">TASK PIPELINE INDEX</span>
                        <h4 className="text-xs font-mono font-black text-white uppercase group-hover:text-pink-400 transition-all">{flow.title}</h4>
                        
                        <div className="text-[9.5px] font-sans text-zinc-400 space-y-1 pt-1">
                          <div className="normal-case leading-normal"><strong className="font-mono text-[8.5px] text-cyan-400 uppercase">Trigger:</strong> {flow.trigger}</div>
                          <div className="normal-case leading-normal"><strong className="font-mono text-[8.5px] text-purple-400 uppercase">Steps:</strong> {flow.steps}</div>
                          <div className="normal-case leading-normal"><strong className="font-mono text-[8.5px] text-emerald-400 uppercase">Output:</strong> {flow.output}</div>
                        </div>
                      </div>

                      {/* Metres footer */}
                      <div className="mt-4 pt-2.5 border-t border-zinc-900 flex justify-between items-center text-[8.5px] font-mono text-zinc-500">
                        <span>Risk Rating: <strong className={flow.risk === "MEDIUM" ? "text-amber-400" : "text-emerald-400 font-bold"}>{flow.risk}</strong></span>
                        <span>Saving Estimate: <strong className="text-emerald-400 font-bold">{flow.timeSaved}</strong></span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Progress and bottom actions */}
              <div className="flex justify-between items-center border-t border-zinc-900 pt-5">
                <button
                  type="button"
                  onClick={() => {
                    playNexoraXSound(200, 0.2);
                    setWizardStep(2);
                  }}
                  className="px-5 py-2.5 border border-zinc-800 text-zinc-400 hover:text-white font-mono text-[10px] uppercase rounded-xl transition-all cursor-pointer"
                >
                  Back to Identity
                </button>

                <div className="text-center font-mono text-[10px] text-zinc-500 hidden sm:block">
                  SELECTED TASKS: <strong className="text-pink-400 font-black">{wizardSelectedTaskFlows.length} NODES</strong>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    playNexoraXSound(450, 0.25);
                    setWizardStep(4);
                  }}
                  className="px-6 py-2.5 rounded-xl font-mono text-[10px] uppercase tracking-widest font-black transition-all flex items-center gap-1.5 cursor-pointer bg-pink-500 text-black border border-pink-400 hover:bg-pink-400"
                >
                  <span>Connect Corporate Tools</span>
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}

          {/* STEP 4: CONNECT TOOLS */}
          {wizardStep === 4 && (
            <div className="space-y-6 animate-fadeIn">
              <div className="bg-purple-950/20 p-4 border border-purple-500/10 rounded-2xl text-left">
                <span className="font-mono text-[9px] text-[#e840f3] font-bold block uppercase tracking-widest mb-1">
                  Step 4: Secure Connector Hub
                </span>
                <h3 className="text-sm font-black font-mono text-white uppercase">
                  Simulate Safe Integration Integrations
                </h3>
                <p className="text-xs text-zinc-400 font-sans leading-relaxed mt-1">
                  Setup connection targets safely. Secret API keys, credentials, and credentials are sequestered in compliance vaults. If a connection is missed, you remain capable of proceeding on limited parameters.
                </p>
              </div>

              {/* Grid of connector statuses */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 text-left">
                {Object.keys(wizardConnectedTools).map((toolName, idx) => {
                  const status = wizardConnectedTools[toolName];
                  return (
                    <div 
                      key={idx}
                      className="p-4 bg-black/60 border border-zinc-900 rounded-xl flex flex-col justify-between"
                    >
                      <div className="space-y-1.5 text-left">
                        <span className="text-[7.5px] text-zinc-600 block font-bold font-mono tracking-wider">PLATFORM ACCESSORY</span>
                        <h4 className="text-xs font-mono font-black text-white uppercase">{toolName}</h4>
                        
                        <div className="pt-1 select-all">
                          {status === "CONNECTED" && (
                            <span className="px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/20 rounded text-emerald-400 font-mono text-[8.5px] uppercase font-bold inline-block">
                              ● CONNECTED
                            </span>
                          )}
                          {status === "NOT_CONNECTED" && (
                            <span className="px-2 py-0.5 bg-zinc-800 border border-zinc-700 rounded text-zinc-400 font-mono text-[8.5px] uppercase font-bold inline-block">
                              ● NOT CONNECTED
                            </span>
                          )}
                          {status === "PERMISSION_MISSING" && (
                            <span className="px-2 py-0.5 bg-rose-500/10 border border-rose-500/20 rounded text-rose-400 font-mono text-[8.5px] uppercase font-bold inline-block animate-pulse">
                              ● PERMISSION MISSING
                            </span>
                          )}
                          {status === "TOKEN_EXPIRED" && (
                            <span className="px-2 py-0.5 bg-amber-500/10 border border-amber-500/20 rounded text-amber-400 font-mono text-[8.5px] uppercase font-bold inline-block">
                              ● TOKEN EXPIRED
                            </span>
                          )}
                          {status === "RECONNECT_REQUIRED" && (
                            <span className="px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 rounded text-blue-400 font-mono text-[8.5px] uppercase font-bold inline-block">
                              ● RECONNECT REQUIRED
                            </span>
                          )}
                          {status === "ADMIN_APPROVAL_REQUIRED" && (
                            <span className="px-2 py-0.5 bg-indigo-500/10 border border-indigo-500/20 rounded text-indigo-400 font-mono text-[8.5px] uppercase font-bold inline-block">
                              ● ADMIN APPROVAL REQUIRED
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Interactive toggle for mock simulation */}
                      <div className="pt-4 border-t border-zinc-900/60 mt-3 flex justify-between items-center">
                        <span className="text-[7.5px] text-zinc-500 font-mono leading-none">NO CREDENTIAL SHOWN</span>
                        
                        <button
                          type="button"
                          onClick={() => {
                            playNexoraXSound(380, 0.2);
                            const current = wizardConnectedTools[toolName];
                            setWizardConnectedTools({
                              ...wizardConnectedTools,
                              [toolName]: current === "CONNECTED" ? "NOT_CONNECTED" : "CONNECTED"
                            });
                          }}
                          className={`px-2 py-1 font-mono text-[8px] font-bold rounded cursor-pointer transition-colors ${
                            status === "CONNECTED"
                              ? "bg-rose-950/20 border border-rose-500/20 text-rose-300 hover:bg-rose-900/30"
                              : "bg-cyan-950/20 border border-cyan-500/20 text-cyan-300 hover:bg-cyan-900/30"
                          }`}
                        >
                          {status === "CONNECTED" ? "DISCONNECT" : "CONNECT SAFE"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Warning/Continuance panel */}
              <div className="p-4 bg-amber-950/10 border border-amber-500/10 rounded-2xl block text-left text-xs font-sans text-zinc-400 leading-normal">
                <span className="text-[8px] tracking-wider font-mono font-black text-amber-400 uppercase block mb-1">⚠️ CONNECTOR DISPATCH COMPLIANCE ADVISORY</span>
                Several connectors register as <strong className="text-amber-400 font-bold">&quot;Not Connected&quot;</strong> or <strong className="text-amber-400 font-bold">&quot;Permission Missing&quot;</strong>. You can bypass authentication credentials now. Deployed agents can proceed immediately running mock integrations.
              </div>

              {/* Progress and bottom actions */}
              <div className="flex justify-between items-center border-t border-zinc-900 pt-5">
                <button
                  type="button"
                  onClick={() => {
                    playNexoraXSound(200, 0.2);
                    setWizardStep(3);
                  }}
                  className="px-5 py-2.5 border border-zinc-800 text-zinc-400 hover:text-white font-mono text-[10px] uppercase rounded-xl transition-all cursor-pointer"
                >
                  Back to Task Flows
                </button>

                <button
                  type="button"
                  onClick={() => {
                    playNexoraXSound(450, 0.25);
                    setWizardStep(5);
                  }}
                  className="px-6 py-2.5 rounded-xl font-mono text-[10px] uppercase tracking-widest font-black transition-all flex items-center gap-1.5 cursor-pointer bg-pink-500 text-black border border-pink-400 hover:bg-pink-400"
                >
                  <span>Select Approval Guardrails</span>
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}

          {/* STEP 5: APPROVAL & SAFETY RULES */}
          {wizardStep === 5 && (
            <div className="space-y-6 animate-fadeIn">
              <div className="bg-purple-950/20 p-4 border border-purple-500/10 rounded-2xl text-left">
                <span className="font-mono text-[9px] text-[#e840f3] font-bold block uppercase tracking-widest mb-1">
                  Step 5: Security Guardrails
                </span>
                <h3 className="text-sm font-black font-mono text-white uppercase">
                  Verify Approval Standards & System Integrity
                </h3>
                <p className="text-xs text-zinc-400 font-sans leading-relaxed mt-1">
                  Establish safety level protocols. Standard operations feature fully automated workflows, but high-risk transactional points require physical human sign-off roadblocks.
                </p>
              </div>

              {/* Left col: level selector, Right col: checkboxes */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 text-left">
                
                {/* Level selection option radio block */}
                <div className="lg:col-span-4 space-y-4 bg-black/60 border border-zinc-800 p-5 rounded-2xl text-left">
                  <span className="font-mono text-[8px] text-zinc-500 block uppercase font-extrabold tracking-wider">CHOOSE SYSTEM AUTOMATION SCALE</span>
                  
                  <div className="space-y-2 font-mono text-xs text-zinc-300">
                    {[
                      { l: "Low", d: "Highly limited. All automated actions require physical user checks before drafting." },
                      { l: "Balanced", d: "Recommended. Low-risk operations run on auto. High-risk writes require human sign-off." },
                      { l: "High", d: "Maximum autonomous execution. Safe responses skip draft queues. High risk remains flagged." },
                      { l: "Admin-controlled", d: "Organizational lock step. Standard client users cannot bypass preambles or telemetry." }
                    ].map(opt => (
                      <label 
                        key={opt.l}
                        onClick={() => {
                          playNexoraXSound(400, 0.1);
                          setWizardApprovalLevel(opt.l as any);
                        }}
                        className={`flex flex-col p-3 border rounded-xl cursor-pointer transition-all ${
                          wizardApprovalLevel === opt.l 
                            ? "border-pink-500 bg-pink-950/5 text-white" 
                            : "border-zinc-850 hover:border-zinc-700 text-zinc-400"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <input 
                            type="radio" 
                            checked={wizardApprovalLevel === opt.l}
                            onChange={() => {}} // handled by click
                            className="text-pink-500 focus:ring-pink-500 bg-black cursor-pointer"
                          />
                          <span className="font-bold uppercase text-[10px] tracking-wide">{opt.l} Automation</span>
                        </div>
                        <p className="text-[9px] text-zinc-500 mt-1.5 font-sans leading-relaxed normal-case">{opt.d}</p>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Specific checkboxes for guardrails rules */}
                <div className="lg:col-span-8 space-y-4">
                  <div className="bg-gradient-to-r from-[#1c1145]/30 to-black p-4 border border-purple-500/10 rounded-2xl flex justify-between items-center">
                    <div className="space-y-0.5 text-left">
                      <span className="text-[8px] font-mono text-[#e840f3] block tracking-widest font-black">AUTOMATION CONTROL SHIELD</span>
                      <span className="text-xs text-white font-mono uppercase font-black">AI Safe-Gate Active State</span>
                    </div>

                    {/* Safety Badge */}
                    <div className="px-3.5 py-1.5 bg-emerald-500/15 border-2 border-emerald-400/35 rounded-xl font-mono text-[9px] text-emerald-400 font-extrabold tracking-widest flex items-center gap-1.5 animate-pulse">
                      <Shield size={12} className="shrink-0" />
                      <span>HUMAN APPROVAL PROTECTED</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs font-mono text-zinc-300">
                    {[
                      { key: "sendEmails", label: "Sending External Emails" },
                      { key: "publishContent", label: "Publishing Public Copy / Contents" },
                      { key: "updateBilling", label: "Updating billing details / balances" },
                      { key: "changeClientRecords", label: "Changing client account fields" },
                      { key: "financialClaims", label: "Updating financial liability terms" },
                      { key: "legalMessaging", label: "Producing contracts / standard disclaimers" },
                      { key: "deleteData", label: "Deleting internal pipeline summaries" },
                      { key: "connectExternalPlatforms", label: "Authenticating new platform endpoints" },
                      { key: "postSocialMedia", label: "Posting to buffer/social media hubs" },
                      { key: "sendSensitiveClientMessages", label: "Broadcasting bulk outbound text threads" }
                    ].map((rule) => (
                      <label 
                        key={rule.key}
                        className="flex items-start gap-3 p-3 bg-black/60 border border-zinc-900 rounded-xl hover:border-zinc-800 cursor-pointer select-none"
                      >
                        <input 
                          type="checkbox"
                          checked={wizardApprovalRules[rule.key]}
                          onChange={(e) => {
                            playNexoraXSound(e.target.checked ? 440 : 250, 0.1);
                            setWizardApprovalRules({
                              ...wizardApprovalRules,
                              [rule.key]: e.target.checked
                            });
                          }}
                          className="mt-0.5 text-pink-500 bg-black border-zinc-800 rounded focus:ring-pink-500 focus:ring-offset-black cursor-pointer"
                        />
                        <div className="space-y-0.5 text-left">
                          <span className="text-[9.5px] uppercase font-bold text-zinc-200 block">{rule.label}</span>
                          <span className="text-[8px] text-zinc-503 font-sans block normal-case font-bold text-zinc-500">Require direct physical human sign-off</span>
                        </div>
                      </label>
                    ))}
                  </div>

                  <div className="p-3 bg-[#0d0725]/50 border border-purple-500/10 rounded-2xl block text-left text-xs font-sans text-zinc-400 normal-case leading-normal">
                    <strong className="text-[#e840f3] uppercase font-mono text-[8px] block tracking-widest mb-1">MANDATORIAL AUTOMATION SAFETY COMPLIANCE</strong>
                    We certify that NexoraX deployment structures strictly adhere to client security guidelines. Standard templates exclude all physical transfers, routing variables, and backend keys to preserve total system containment.
                  </div>

                </div>

              </div>

              {/* Progress and bottom actions */}
              <div className="flex justify-between items-center border-t border-zinc-900 pt-5">
                <button
                  type="button"
                  onClick={() => {
                    playNexoraXSound(200, 0.2);
                    setWizardStep(4);
                  }}
                  className="px-5 py-2.5 border border-zinc-800 text-zinc-400 hover:text-white font-mono text-[10px] uppercase rounded-xl transition-all cursor-pointer"
                >
                  Back to Connectors
                </button>

                <button
                  type="button"
                  onClick={() => {
                    playNexoraXSound(450, 0.25);
                    setWizardStep(6);
                  }}
                  className="px-6 py-2.5 rounded-xl font-mono text-[10px] uppercase tracking-widest font-black transition-all flex items-center gap-1.5 cursor-pointer bg-pink-500 text-black border border-pink-400 hover:bg-pink-400"
                >
                  <span>Compile and Preview Pipeline</span>
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}

          {/* STEP 6: PREVIEW AGENT WORKFLOW */}
          {wizardStep === 6 && (
            <div className="space-y-6 animate-fadeIn">
              <div className="bg-purple-950/20 p-4 border border-purple-500/10 rounded-2xl text-left">
                <span className="font-mono text-[9px] text-[#e840f3] font-bold block uppercase tracking-widest mb-1">
                  Step 6: High Fidelity Validation Summary
                </span>
                <h3 className="text-sm font-black font-mono text-white uppercase">
                  Verify Flow Telemetry and Deploy Safely
                </h3>
                <p className="text-xs text-zinc-400 font-sans leading-relaxed mt-1">
                  Review calculated workflow parameters. Review active tools, security configurations, and risk levels. If ready, trigger direct node assignment sequence.
                </p>
              </div>

              {/* Workflow visual block */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 text-left">
                
                {/* Visual workflow tree flow chart */}
                <div className="lg:col-span-8 bg-black/80 border border-zinc-900 rounded-3xl p-5 space-y-4">
                  <span className="font-mono text-[8px] text-cyan-400 font-bold uppercase tracking-wider block border-b border-zinc-850 pb-2">
                    ACTIVE WORKFLOW SCHEMATIC PIPELINE
                  </span>

                  <div className="relative py-4 pl-6 border-l-2 border-dashed border-purple-500/30 space-y-5 text-left text-xs font-mono">
                    
                    {/* Step A */}
                    <div className="relative">
                      <div className="absolute -left-[31px] top-0 w-4 h-4 rounded-full bg-pink-500 flex items-center justify-center text-black text-[8px] font-bold">
                        1
                      </div>
                      <div className="space-y-0.5">
                        <span className="text-white font-bold block uppercase text-[10px]">New Customer Message Received</span>
                        <p className="text-[9.5px] text-zinc-500 font-sans normal-case leading-normal">
                          Detected via Website forms, Email webhook, or custom Slack event channels.
                        </p>
                      </div>
                    </div>

                    {/* Step B */}
                    <div className="relative">
                      <div className="absolute -left-[31px] top-0 w-4 h-4 rounded-full bg-cyan-500 flex items-center justify-center text-black text-[8px] font-bold">
                        2
                      </div>
                      <div className="space-y-0.5">
                        <span className="text-white font-bold block uppercase text-[10px]">Contextual Intent Query Sweep</span>
                        <p className="text-[9.5px] text-zinc-500 font-sans normal-case leading-normal">
                          Agent queries verified industry profiles and extracts relevant parameters.
                        </p>
                      </div>
                    </div>

                    {/* Step C */}
                    <div className="relative">
                      <div className="absolute -left-[31px] top-0 w-4 h-4 rounded-full bg-indigo-500 flex items-center justify-center text-black text-[8px] font-bold">
                        3
                      </div>
                      <div className="space-y-0.5">
                        <span className="text-white font-bold block uppercase text-[10px]">Draft Reply Formulation</span>
                        <p className="text-[9.5px] text-zinc-500 font-sans normal-case leading-normal">
                          Compiles natural speech draft using active tone variables (&quot;{wizardAgentForm.toneStyle}&quot; style).
                        </p>
                      </div>
                    </div>

                    {/* Step D */}
                    <div className="relative">
                      <div className="absolute -left-[31px] top-0 w-4 h-4 rounded-full bg-purple-500 flex items-center justify-center text-black text-[8px] font-bold">
                        4
                      </div>
                      <div className="space-y-0.5">
                        <span className="text-white font-bold block uppercase text-[10px]">Risk Factor Alignment Gate</span>
                        <p className="text-[9.5px] text-zinc-500 font-sans normal-case leading-normal">
                          Verifies draft contents against restricted terms. Evaluates risk rating ({metrics.riskScore}% mapped).
                        </p>
                      </div>
                    </div>

                    {/* Step E */}
                    <div className="relative">
                      <div className="absolute -left-[31px] top-0 w-4 h-4 rounded-full bg-amber-500 flex items-center justify-center text-black text-[8px] font-bold animate-pulse">
                        5
                      </div>
                      <div className="space-y-0.5">
                        <span className="text-amber-400 font-bold block uppercase text-[10px]">Human Verification Safe roadblock</span>
                        <p className="text-[9.5px] text-zinc-400 font-sans normal-case leading-normal">
                          Enforces draft routing to physical queues under <strong className="text-amber-400">{wizardApprovalLevel} Approval level</strong> rules before transmission.
                        </p>
                      </div>
                    </div>

                    {/* Step F */}
                    <div className="relative">
                      <div className="absolute -left-[31px] top-0 w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center text-black text-[8px] font-bold">
                        6
                      </div>
                      <div className="space-y-0.5">
                        <span className="text-emerald-400 font-bold block uppercase text-[10px]">Transmission and Ledger Log</span>
                        <p className="text-[9.5px] text-zinc-500 font-sans normal-case leading-normal">
                          Transmits approved response. Registers performance metrics and updates analytics panels.
                        </p>
                      </div>
                    </div>

                  </div>
                </div>

                {/* Left col: telemetry variables status */}
                <div className="lg:col-span-4 space-y-4">
                  <div className="bg-black/60 border border-zinc-900 rounded-3xl p-5 space-y-4">
                    <span className="font-mono text-[8px] text-zinc-500 block uppercase font-bold tracking-wider leading-none">AGENT SPECIFICATION REGISTERS</span>
                    
                    <div className="space-y-3 font-mono text-[10px]">
                      
                      <div className="border-b border-zinc-900 pb-2">
                        <span className="text-zinc-500 block text-[7.5px] font-bold leading-none">TARGET WORKER</span>
                        <span className="text-white font-bold text-xs uppercase block">{wizardAgentForm.agentDisplayName || wizardSelectedTemplate?.name}</span>
                        <span className="text-pink-400 text-[8.5px] block">{wizardSelectedTemplate?.role}</span>
                      </div>

                      <div className="border-b border-zinc-900 pb-2">
                        <span className="text-zinc-500 block text-[7.5px] font-bold leading-none">ACTIVE TASK FLOWS</span>
                        <span className="text-zinc-300 font-bold uppercase text-[9.5px] block">{wizardSelectedTaskFlows.length} Active Modules</span>
                        <p className="text-[8px] text-zinc-500 leading-normal line-clamp-2 mt-0.5">{wizardSelectedTaskFlows.join(", ")}</p>
                      </div>

                      <div className="border-b border-zinc-900 pb-2">
                        <span className="text-zinc-500 block text-[7.5px] font-bold leading-none">CONNECTED PIPELINES</span>
                        <span className="text-cyan-400 font-bold text-[9px] uppercase block">
                          {Object.keys(wizardConnectedTools).filter(k => wizardConnectedTools[k] === "CONNECTED").length} Online Connectors
                        </span>
                      </div>

                      <div>
                        <span className="text-zinc-500 block text-[7.5px] font-bold leading-none">SAFETY DIRECTIVE</span>
                        <span className="text-amber-400 font-bold block uppercase">{wizardApprovalLevel} Control</span>
                      </div>

                    </div>
                  </div>

                  {/* Calculated metrics dashboard gauges */}
                  <div className="p-5 bg-gradient-to-b from-[#1b0a33] to-black border border-purple-500/20 rounded-3xl space-y-3 font-mono">
                    <span className="text-[7.5px] text-zinc-500 uppercase block font-bold leading-none tracking-widest">CALCULATED METRIC SUITE</span>
                    
                    <div className="grid grid-cols-2 gap-2 text-center text-xs">
                      
                      <div className="bg-black/60 p-2.5 border border-purple-950 rounded-xl space-y-1">
                        <span className="text-[7.5px] text-zinc-500 block leading-none">SAVINGS RATE</span>
                        <span className="text-emerald-400 text-sm font-extrabold block">{metrics.hoursSaved} Hours/mo</span>
                      </div>

                      <div className="bg-black/60 p-2.5 border border-purple-950 rounded-xl space-y-1">
                        <span className="text-[7.5px] text-zinc-500 block leading-none">READINESS SCORE</span>
                        <span className="text-cyan-400 text-sm font-extrabold block">{metrics.readiness}%</span>
                      </div>

                      <div className="bg-black/60 p-2.5 border border-purple-950 rounded-xl space-y-1 col-span-2">
                        <span className="text-[7.5px] text-zinc-500 block leading-none">AGGREGATE RISK ALIGNMENT</span>
                        <div className="w-full bg-zinc-900 h-1.5 rounded-full mt-1.5 overflow-hidden">
                          <div className="bg-gradient-to-r from-emerald-500 via-amber-500 to-rose-500 h-full" style={{ width: `${metrics.riskScore}%` }} />
                        </div>
                        <div className="flex justify-between text-[7px] text-zinc-500 pt-0.5 uppercase">
                          <span>Low: 5%</span>
                          <span className="text-white font-bold">{metrics.riskScore}% Rating</span>
                          <span>High: 95%</span>
                        </div>
                      </div>

                    </div>
                  </div>

                </div>

              </div>

              {/* Progress and bottom actions */}
              <div className="flex justify-between items-center border-t border-zinc-900 pt-5">
                <button
                  type="button"
                  onClick={() => {
                    playNexoraXSound(200, 0.2);
                    setWizardStep(5);
                  }}
                  className="px-5 py-2.5 border border-zinc-800 text-zinc-400 hover:text-white font-mono text-[10px] uppercase rounded-xl transition-all cursor-pointer"
                >
                  Back to Guardrails
                </button>

                <div className="flex gap-2.5">
                  <button
                    type="button"
                    onClick={() => {
                      playNexoraXSound(300, 0.2);
                      setWizardStep(5);
                      triggerToast("Opened edit safety defaults state", "info");
                    }}
                    className="px-4 py-2 border border-purple-500/20 hover:border-purple-500/40 text-purple-300 font-mono text-[10px] uppercase rounded-xl transition-colors cursor-pointer"
                  >
                    Edit Guardrails
                  </button>

                  <button
                    type="button"
                    onClick={handleNexoraXDeployWizardAgent}
                    className="px-6 py-2.5 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 border border-pink-400 font-mono text-[10px] uppercase tracking-widest font-black text-white rounded-xl transition-all flex items-center gap-1.5 cursor-pointer shadow-[0_0_20px_rgba(236,72,153,0.4)]"
                  >
                    <Zap size={12} className="animate-pulse" />
                    <span>Deploy Active Agent Node</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* STEP 7: DEPLOYMENT SUCCESS */}
          {wizardStep === 7 && finalizedWizardAgentSection()}

        </div>

        {/* ADMIN SETTINGS ONLY WRAPPER PANEL */}
        {currentUserRole === "ADMIN" && (
          <div className="mt-8 border-t-2 border-dashed border-red-500/20 pt-6 animate-fadeIn">
            <div className="bg-gradient-to-b from-[#1c040c] to-black border-2 border-red-500/25 p-6 rounded-3xl relative overflow-hidden text-left">
              <div className="absolute top-0 right-0 py-1.5 px-3 bg-red-500/20 rounded-bl-xl font-mono text-[8px] text-red-400 font-bold tracking-widest uppercase">
                ADMIN clearance ACTIVE
              </div>

              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-5 h-5 text-red-500 shrink-0" />
                <div className="space-y-0.5 text-left">
                  <h3 className="text-sm font-mono font-black text-white uppercase tracking-wider">
                    Admin Settings - Wizard Parameter Overrides
                  </h3>
                  <p className="text-[10px] text-zinc-500 font-sans normal-case">
                    Enforce structural deployment constraints, restrict task sequences layout, or inspect deployment schedules.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 font-mono text-[10px] text-zinc-300 pt-2.5">
                
                {/* Rule options in admin */}
                <div className="bg-black/80 border border-zinc-900 p-4 rounded-2xl space-y-3">
                  <span className="text-[8px] text-zinc-500 uppercase block font-bold block border-b border-zinc-900 pb-1">DEFAULT DISPATCH POLICIES</span>
                  
                  <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <input 
                      type="checkbox"
                      checked={wizardAdminSettings.enforceBalancedRule}
                      onChange={(e) => setWizardAdminSettings({...wizardAdminSettings, enforceBalancedRule: e.target.checked})}
                      className="text-red-500 bg-black border-zinc-800 rounded focus:ring-red-500 cursor-pointer text-[8px]"
                    />
                    <span>Force Balanced Guardrails default</span>
                  </label>

                  <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <input 
                      type="checkbox"
                      checked={wizardAdminSettings.featureRecommendedPreset}
                      onChange={(e) => setWizardAdminSettings({...wizardAdminSettings, featureRecommendedPreset: e.target.checked})}
                      className="text-red-500 bg-black border-zinc-800 rounded focus:ring-red-500 cursor-pointer"
                    />
                    <span>Highlight featured Recommended row</span>
                  </label>

                  <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <input 
                      type="checkbox"
                      checked={wizardAdminSettings.disableRiskyFlows}
                      onChange={(e) => setWizardAdminSettings({...wizardAdminSettings, disableRiskyFlows: e.target.checked})}
                      className="text-red-500 bg-black border-zinc-800 rounded focus:ring-red-500 cursor-pointer"
                    />
                    <span>Block/Disable high-risk task sequences</span>
                  </label>
                </div>

                {/* Connector mapping */}
                <div className="bg-black/80 border border-zinc-900 p-4 rounded-2xl space-y-2">
                  <span className="text-[8px] text-zinc-500 uppercase block font-bold block border-b border-zinc-900 pb-1">RESTRICT CONNECTORS</span>
                  <p className="text-[9px] text-zinc-500 leading-normal mb-1 font-sans">Toggle connector credential requirements. Restricted connectors force client warnings.</p>
                  
                  <div className="grid grid-cols-2 gap-1.5 text-[8.5px]">
                    {["Gmail / Email", "Stripe Status", "Google Calendar", "Slack"].map((conn, ix) => (
                      <button
                        key={ix}
                        onClick={() => {
                          playNexoraXSound(210, 0.1);
                          triggerToast(`Admin restricted: ${conn} credentials settings lock updated.`, "info");
                        }}
                        className="px-2 py-1 bg-zinc-900 border border-zinc-800 text-zinc-400 rounded hover:border-red-500/40 text-left capitalize truncate cursor-pointer"
                      >
                        🔒 {conn} Setup
                      </button>
                    ))}
                  </div>
                </div>

                {/* Operational log output */}
                <div className="bg-black/80 border border-zinc-900 p-4 rounded-2xl space-y-1.5 flex flex-col justify-between">
                  <div>
                    <span className="text-[8px] text-zinc-500 uppercase block font-bold block border-b border-zinc-900 pb-1">DEPLOYMENT MONITOR RECORDS</span>
                    <div className="p-2 bg-black rounded border border-zinc-850 h-[65px] font-mono text-[7px] text-emerald-400 overflow-y-auto space-y-1">
                      <div>[SYSTEM_INIT] NexoraX Wizard setup online...</div>
                      {wizardSelectedTemplate && <div>[blueprints] chosen: {wizardSelectedTemplate.id}</div>}
                      {wizardStep > 3 && <div>[flows] active sequence mapping checked</div>}
                      {wizardStep === 7 && <div>[launch] successfully deployed Custom agent</div>}
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      playNexoraXSound(450, 0.25);
                      const copy = templates.filter(t => !t.id.startsWith("wizard-agent-"));
                      setTemplates(copy);
                      localStorage.setItem("nx_agent_templates", JSON.stringify(copy));
                      triggerToast("Admin Override: Deployed custom agents deleted from active registry.", "warning");
                    }}
                    className="px-2.5 py-1 bg-red-955/20 border border-red-500 hover:bg-red-900 hover:text-white text-red-200 text-[8px] font-mono font-bold rounded uppercase cursor-pointer text-center block w-full mt-1.5"
                  >
                    🗑️ Flush Custom Deployed Agents
                  </button>
                </div>

              </div>

            </div>
          </div>
        )}

      </div>
    );
  };

  // Check if template contains specified featured templates
  const iContainsFeaturedName = (name: string) => {
    const searchFeaturedArr = [
      "Executive Assistant Agent",
      "Customer Support Assistant Agent",
      "Product Search Agent",
      "Sales Assistant Agent",
      "Social Media Manager Agent",
      "Website Chat Agent",
      "Project Manager Agent",
      "Automation Ideas Agent"
    ];
    return searchFeaturedArr.includes(name) || name.toLowerCase().includes("assistant") || name.toLowerCase().includes("chat") || name.toLowerCase().includes("ideas") || name.toLowerCase().includes("search");
  };

  // Helper template output for successful launch screen (Step 7)
  const finalizedWizardAgentSection = () => {
    const deployRecord = wizardDeployedAgent || {
      name: "Custom Support Agent Instance",
      templateName: "Customer Support Assistant Agent",
      activeTaskFlows: ["New Lead Follow-Up", "Customer Support Reply Draft"],
      toolsList: ["Gmail / Email", "Website Forms", "Webhooks"],
      approvalLevel: "Balanced",
      firstTask: "Process Incoming Message Queues",
      monthlySaved: 48
    };

    return (
      <div className="space-y-6 text-center animate-fadeIn max-w-2xl mx-auto py-6">
        
        {/* Success Holograph circle */}
        <div className="relative w-24 h-24 mx-auto rounded-full bg-gradient-to-tr from-pink-500 to-purple-500 flex items-center justify-center p-0.5 shadow-[0_0_40px_rgba(236,72,153,0.3)] border border-white/20 animate-bounce">
          <div className="w-full h-full bg-black rounded-full flex items-center justify-center text-pink-400">
            <CheckCircle size={44} className="animate-pulse" />
          </div>
        </div>

        <div className="space-y-2">
          <h3 className="text-2xl font-black font-mono text-white uppercase tracking-tight">
            Agent Deployed Successfully
          </h3>
          <p className="text-xs text-zinc-400 font-sans leading-relaxed max-w-md mx-auto normal-case">
            Your AI agent is now ready to support your business workflows safely with human approval controls.
          </p>
        </div>

        {/* Summary Card metadata */}
        <div className="bg-[#110126]/80 p-6 border-2 border-pink-500/35 rounded-3xl relative overflow-hidden text-left font-mono text-xs text-slate-300 space-y-4">
          <div className="absolute top-0 right-0 p-3 text-[8.5px] font-mono text-pink-400 uppercase tracking-widest font-black">
            PIPELINE CONSOLE LIVE
          </div>

          <div className="border-b border-zinc-900 pb-3">
            <span className="text-[7.5px] text-zinc-500 block uppercase font-bold leading-none">ACTIVE WORKING AGENT DETECTED</span>
            <span className="text-white text-base font-black uppercase tracking-wide block mt-1">{deployRecord.name}</span>
            <span className="text-[8.5px] text-pink-400 uppercase font-bold block">{deployRecord.templateName}</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            
            <div className="space-y-1 text-left">
              <span className="text-[7.5px] text-zinc-500 uppercase font-black block leading-none">ACTIVE ACTIONS PIPELINE:</span>
              <ul className="list-disc list-inside text-[9.5px] text-zinc-300 space-y-0.5 mt-1 list-none pl-0">
                {deployRecord.activeTaskFlows.map((flow: string, ix: number) => (
                  <li key={ix} className="truncate uppercase">&bull; {flow}</li>
                ))}
              </ul>
            </div>

            <div className="space-y-1 text-left">
              <span className="text-[7.5px] text-zinc-500 uppercase font-black block leading-none">ONLINE CONNECTIONS ESTABLISHED:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {deployRecord.toolsList.length > 0 ? (
                  deployRecord.toolsList.map((tool: string, ix: number) => (
                    <span key={ix} className="px-1.5 py-0.5 bg-zinc-900 border border-zinc-800 text-zinc-400 text-[8px] rounded uppercase font-bold">
                      {tool}
                    </span>
                  ))
                ) : (
                  <span className="text-zinc-505 italic text-[8.5px]">None connected (Mock default active)</span>
                )}
              </div>
            </div>

          </div>

          <div className="border-t border-zinc-900 pt-3 flex flex-wrap gap-4 text-[9px] text-zinc-500 justify-between items-center bg-black/40 p-3 rounded-xl border border-zinc-900">
            <div>
              <span>SAFETY DIRECTIVE POLICY: </span>
              <strong className="text-amber-400 uppercase font-extrabold">{deployRecord.approvalLevel} Automation</strong>
            </div>
            
            <div>
              <span>ESTIMATED TIME SAVED: </span>
              <strong className="text-emerald-400 font-extrabold">{deployRecord.monthlySaved} Hours / Month</strong>
            </div>

            <div>
              <span>FIRST SCHEDULED TRIGGER: </span>
              <strong className="text-cyan-400 font-extrabold">Auto-scan active queues</strong>
            </div>
          </div>

        </div>

        {/* Bottom selector triggers */}
        <div className="flex flex-wrap gap-3 justify-center pt-2">
          
          <button 
            onClick={() => {
              playNexoraXSound(220, 0.25);
              setActiveTab("WORKSPACE");
              triggerToast("Transitioning to active Agent Workspace... Let's execute your first task!", "success");
            }}
            className="px-5 py-2.5 bg-gradient-to-r from-violet-950 to-purple-900 border border-purple-500 text-purple-200 hover:text-white font-mono text-[9.5px] font-black uppercase tracking-widest rounded-xl transition-all shadow-md cursor-pointer"
          >
            Go to Agent Workspace
          </button>

          <button 
            onClick={() => {
              playNexoraXSound(180, 0.2);
              setWizardStep(1);
              setWizardSelectedTemplate(null);
              setWizardDeployedAgent(null);
            }}
            className="px-5 py-2.5 bg-black border border-pink-500 hover:bg-pink-950/20 text-pink-300 hover:text-white font-mono text-[9.5px] font-black uppercase tracking-widest rounded-xl transition-all cursor-pointer"
          >
            Create Another Agent
          </button>

          <button 
            onClick={() => {
              playNexoraXSound(340, 0.15);
              triggerToast("Retrieving current message queue variables... All tasks empty structure.", "info");
            }}
            className="px-4 py-2.5 bg-zinc-950 border border-zinc-800 text-zinc-400 hover:text-zinc-300 hover:border-zinc-700 font-mono text-[9.5px] font-bold uppercase rounded-xl transition-all cursor-pointer"
          >
            View Task Queue
          </button>

          <button 
            onClick={() => {
              playNexoraXSound(380, 0.15);
              triggerToast("Historical simulation metric data empty. Live logs showing active SLA alignment pass.", "info");
            }}
            className="px-4 py-2.5 bg-zinc-950 border border-zinc-800 text-zinc-400 hover:text-zinc-300 hover:border-zinc-700 font-mono text-[9.5px] font-bold uppercase rounded-xl transition-all cursor-pointer"
          >
            Open Analytics
          </button>

        </div>

      </div>
    );
  };

  return (
    <div className="space-y-8 text-slate-200 uppercase max-w-7xl mx-auto pb-16">
      
      {/* Dynamic Brand Intro Mode Ribbon */}
      {brandIntroMode !== "None" && (
        <div className="text-[#e840f3] text-center py-2.5 bg-gradient-to-r from-violet-950/40 via-black to-[#e840f3]/10 border-b border-purple-950/30 font-mono text-[10px] sm:text-xs font-bold tracking-widest uppercase relative overflow-hidden">
          <div className="relative z-10 flex items-center justify-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-ping shrink-0" />
            <p className="line-clamp-1 italic normal-case">
              {brandIntroMode === "Spiritual / Values-Based Intro" && "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds."}
              {brandIntroMode === "Professional" && "Welcome to NexoraX AI Agent Automation. Choose an agent, customise the workflow, and deploy safely with human approval."}
              {brandIntroMode === "Founder Message" && "Founder Message: Deploying robust AI worker templates safely with human-in-the-loop approval gates."}
            </p>
          </div>
          <div className="absolute inset-0 bg-[linear-gradient(to_right,transparent,rgba(232,64,243,0.05),transparent)] animate-pulse" />
        </div>
      )}

      {/* Interactive Role Switcher for commercial readiness simulation */}
      <div className="flex flex-col lg:flex-row gap-4 justify-between items-center bg-gradient-to-r from-purple-950/30 via-black/80 to-indigo-950/30 border border-purple-500/20 p-5 rounded-2xl shadow-[0_0_25px_rgba(232,64,243,0.06)] relative overflow-hidden">
        <div className="absolute inset-0 bg-[#e840f3]/2 blur-3xl pointer-events-none" />
        <div className="flex items-center gap-3 text-left relative z-10">
          <Sparkles className="w-5 h-5 text-[#e840f3] shrink-0 animate-pulse" />
          <div className="space-y-0.5">
            <span className="text-xs font-mono font-black tracking-wider text-pink-400 block leading-none uppercase">🕹️ NEXORAX SaaS EMULATOR CONSOLE</span>
            <span className="text-[10px] text-zinc-400 font-sans tracking-wide normal-case block leading-normal">
              Toggle simulated personas and subscription plans to fully preview client gating boundaries in real time.
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 relative z-10">
          {/* Persona Picker */}
          <div className="flex flex-col text-left space-y-1">
            <span className="text-[7.5px] font-mono font-bold text-zinc-500 uppercase tracking-widest animate-pulse">ACTIVE PERSONA</span>
            <div className="flex gap-1 bg-black/60 p-1 rounded-xl border border-zinc-800 text-[8.5px] font-mono">
              {[
                { id: "VISITOR", name: "VISITOR" },
                { id: "CLIENT", name: "CLIENT" },
                { id: "AGENCY_ADMIN", name: "AGENCY ADMIN" },
                { id: "SUPER_ADMIN", name: "SUPER ADMIN" }
              ].map(item => (
                <button
                  key={item.id}
                  onClick={() => {
                    playNexoraXSound(item.id === "SUPER_ADMIN" ? 440 : item.id === "AGENCY_ADMIN" ? 380 : item.id === "CLIENT" ? 320 : 250, 0.15);
                    setSaasPersona(item.id as any);
                    if (item.id === "VISITOR") {
                      if (activeTab === "WORKSPACE" || activeTab === "ADMIN_PLAN_MGR") {
                        setActiveTab("TEMPLATES");
                      }
                    } else if (item.id === "CLIENT") {
                      if (activeTab === "ADMIN_PLAN_MGR") {
                        setActiveTab("TEMPLATES");
                      }
                    }
                    triggerToast(`Persona switched to ${item.name} status`, "success");
                  }}
                  className={`px-3 py-1.5 rounded-lg font-bold uppercase transition-all tracking-wider cursor-pointer ${
                    saasPersona === item.id 
                      ? "bg-purple-600 text-white shadow-[0_0_12px_rgba(232,64,243,0.3)] border border-purple-400/20" 
                      : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-900/45"
                  }`}
                >
                  {item.name}
                </button>
              ))}
            </div>
          </div>

          {/* Active Plan Picker (for Visitor / Client) */}
          <div className="flex flex-col text-left space-y-1">
            <span className="text-[7.5px] font-mono font-bold text-zinc-500 uppercase tracking-widest">SUBSCRIBER PLAN</span>
            <div className="flex gap-1 bg-black/60 p-1 rounded-xl border border-zinc-800 text-[8.5px] font-mono">
              {(["Starter", "Growth", "Agency", "Enterprise"] as const).map(p => (
                <button
                  key={p}
                  onClick={() => {
                    playNexoraXSound(280 + 30 * ["Starter", "Growth", "Agency", "Enterprise"].indexOf(p), 0.15);
                    setClientPlan(p);
                    triggerToast(`Active Client Subscription set to ${p.toUpperCase()} tier`, "info");
                  }}
                  className={`px-3 py-1.5 rounded-lg font-bold uppercase transition-all tracking-wider cursor-pointer ${
                    clientPlan === p 
                      ? "bg-cyan-600 text-white shadow-[0_0_12px_rgba(6,182,212,0.3)] border border-cyan-400/20" 
                      : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-900/45"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* OUT OF THIS GALAXY WORKSPACE HEADER HERO */}
      <div className="relative overflow-hidden rounded-3xl border border-purple-500/20 bg-black/90 p-8 shadow-[0_0_50px_rgba(232,64,243,0.08)]">
        {/* Neon mesh overlay background */}
        <div className="absolute inset-0 bg-[radial-gradient(#1e1145_1.5px,transparent_1.5px)] [background-size:24px_24px] opacity-25 pointer-events-none" />
        <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-br from-[#e840f3]/10 to-transparent blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-10 w-96 h-96 bg-gradient-to-tr from-cyan-500/5 to-transparent blur-3xl pointer-events-none" />
 
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 relative z-10 text-left">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-ping" />
              <span className="text-cyan-400 font-mono text-[9px] uppercase tracking-[0.45em] font-black">
                NEXORAX AI AUTOMATION PLATFORM
              </span>
            </div>
            
            <h1 className="text-3xl md:text-5xl font-black font-mono tracking-tight text-white uppercase text-transparent bg-clip-text bg-gradient-to-r from-white via-indigo-200 to-purple-400">
              AI Agent Template Library
            </h1>

            <p className="text-sm font-sans font-black text-[#e840f3] tracking-widest uppercase">
              Launch Ready-Made AI Workers in Minutes.
            </p>

            <p className="text-[11px] text-slate-400 max-w-3xl leading-relaxed font-sans normal-case">
              Choose from pre-built NexoraX agent templates for support, sales, admin, research, content, operations, and executive workflows. Configure parameters matching your exact corporate style, connect pipelines, and click deploy.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3 pt-3">
              <button 
                onClick={() => {
                  setSelectedCategory("ALL");
                  const el = document.getElementById("marketplace-filters");
                  el?.scrollIntoView({ behavior: "smooth" });
                }}
                className="px-6 py-2.5 bg-gradient-to-r from-violet-950 to-purple-900 border border-purple-500 text-purple-200 hover:from-purple-900 hover:to-indigo-800 rounded-xl font-mono text-[10px] font-black tracking-widest transition-all shadow-[0_0_15px_rgba(232,64,243,0.2)] cursor-pointer"
              >
                🪐 Explore Templates
              </button>
              
              <button 
                onClick={() => {
                  const el = document.getElementById("template-grid-anchor");
                  el?.scrollIntoView({ behavior: "smooth" });
                }}
                className="px-6 py-2.5 bg-black border border-cyan-500 text-cyan-300 hover:bg-cyan-950/20 rounded-xl font-mono text-[10px] font-black tracking-widest transition-all cursor-pointer"
              >
                ⚡ Deploy First Agent
              </button>

              <a 
                href="tel:08000 622827"
                className="px-4 py-2.5 bg-purple-950/40 border border-purple-500/10 hover:border-purple-500/30 text-purple-300 rounded-xl font-mono text-[10px] font-black tracking-wider flex items-center gap-1.5 transition-all text-center"
              >
                📞 Book Automation Demo
              </a>
            </div>
          </div>

          {/* Holographic system stats block */}
          <div className="bg-black/80 border border-purple-500/15 p-4 rounded-2xl min-w-[260px] font-mono text-[10px] space-y-2 text-left self-stretch flex flex-col justify-between">
            <div className="border-b border-[#e840f3]/20 pb-2">
              <span className="text-[8px] text-zinc-500 uppercase block font-bold">SYSTEM WORKFLOW LINK</span>
              <span className="text-white font-extrabold text-[11px]">SecurePayment DISPATCH REGISTRY</span>
            </div>
            
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="text-zinc-500">Total Active Templates:</span>
                <span className="text-cyan-400 font-bold">{templates.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Ready Task Flows:</span>
                <span className="text-pink-400 font-bold">20 Nodes</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Average Unit Latency:</span>
                <span className="text-emerald-400 font-bold">0.8s Baseline</span>
              </div>
            </div>

            <div className="bg-purple-950/20 p-2 border border-purple-500/10 rounded-xl">
              <span className="text-[8px] text-purple-300 tracking-wider font-extrabold uppercase block">SECURE CREDENTIAL SHIELDED</span>
              <span className="text-[7.5px] text-zinc-500 lowercase">API/Webhooks hidden by Node-16 Gavel</span>
            </div>
          </div>
        </div>
      </div>

      {/* Required Contact Details Warning banner */}
      {isPublicContactDetailsMissing && (
        <div className="p-5 border border-red-500/30 bg-red-955/10 rounded-2xl space-y-3 text-left relative overflow-hidden animate-pulse">
          <div className="absolute top-0 right-0 p-3 text-[8px] font-mono text-red-500/40 font-bold tracking-widest">
            ERROR // CONFIG_REQUIRED
          </div>
          <div className="flex items-start gap-4">
            <div className="p-2.5 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 shrink-0">
              <AlertTriangle className="w-5 h-5 animate-bounce" />
            </div>
            <div className="space-y-1 text-left">
              <h3 className="text-sm font-mono font-black text-red-400 uppercase tracking-widest">
                Public Contact Details Required
              </h3>
              <p className="text-[10.5px] text-zinc-400 normal-case font-sans leading-relaxed">
                Critical configuration details are missing. Public Website URL, support email, phone number, booking links, and help desk addresses must satisfy requirements through the <strong className="text-rose-400 font-bold">Admin Controls Active &gt; Public Contact & Brand Intros</strong> panel before publishing, customising templates, or deploying agents.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* CLIENT-FACING OUTLINE BRIEF SECTION */}
      <div className="bg-black/45 border border-purple-500/10 rounded-2xl p-6 text-left space-y-2.5">
        <h2 className="text-base font-black text-white tracking-tight flex items-center gap-2">
          <LayoutGrid className="w-4 h-4 text-cyan-400" />
          Agent Template Library
        </h2>
        
        <p className="text-xs font-mono text-[#e840f3] font-bold">
          Choose an AI agent. Customise it. Deploy it.
        </p>
        
        <p className="text-[10.5px] text-slate-400 font-sans tracking-wide leading-relaxed normal-case">
          Start faster with ready-made NexoraX AI agent templates for support, sales, research, admin, content, operations, finance, marketing, and executive workflows. Our customizable parameters completely eliminate development times, allowing you to spin up secure, multi-stage pipelines in under 3 minutes.
        </p>

        {/* Dynamic selector navigation between Wizard, Templates, Workspace, Metrics, Pricing, and Admin */}
        <div className="flex border-b border-zinc-800 pt-4 gap-4 font-mono text-[10px] overflow-x-auto whitespace-nowrap scrollbar-thin">
          <button 
            onClick={() => {
              playNexoraXSound(240, 0.1);
              if (saasPersona === 'VISITOR') {
                triggerToast("⚠️ Visitor Mode: Upgrade to deploy live instances.", "warning");
              }
              setActiveTab("WIZARD");
            }}
            className={`pb-2.5 px-1 font-black text-xs tracking-widest relative cursor-pointer flex items-center gap-1.5 ${activeTab === "WIZARD" ? "text-pink-400 font-extrabold" : "text-zinc-500 hover:text-zinc-300"}`}
          >
            ⭐ DEPLOY YOUR AI AGENT
            {activeTab === "WIZARD" && (
              <motion.div layoutId="activeTabUnderline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-pink-500" />
            )}
          </button>

          {saasPersona !== 'VISITOR' && (
            <button 
              onClick={() => {
                playNexoraXSound(260, 0.1);
                setActiveTab("WORKSPACE");
              }}
              className={`pb-2.5 px-1 font-black tracking-widest relative cursor-pointer flex items-center gap-1.5 ${activeTab === "WORKSPACE" ? "text-[#e840f3] font-extrabold shadow-[0_0_10px_rgba(232,64,243,0.1)]" : "text-zinc-500 hover:text-zinc-300"}`}
            >
              🕹️ AGENT TASK WORKSPACE {wizardDeployedAgent ? "🟢" : "(PREVIEW)"}
              {activeTab === "WORKSPACE" && (
                <motion.div layoutId="activeTabUnderline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#e840f3]" />
              )}
            </button>
          )}

          <button 
            onClick={() => {
              playNexoraXSound(280, 0.1);
              setActiveTab("TEMPLATES");
            }}
            className={`pb-2.5 px-1 font-black tracking-widest relative cursor-pointer ${activeTab === "TEMPLATES" ? "text-cyan-400 font-extrabold" : "text-zinc-500 hover:text-zinc-300"}`}
          >
            👾 AI WORKERS MARKETPLACE ({filteredTemplates.length} READY)
            {activeTab === "TEMPLATES" && (
              <motion.div layoutId="activeTabUnderline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-400" />
            )}
          </button>

          <button 
            onClick={() => {
              playNexoraXSound(300, 0.1);
              setActiveTab("TASK_FLOWS");
            }}
            className={`pb-2.5 px-1 font-black tracking-widest relative cursor-pointer ${activeTab === "TASK_FLOWS" ? "text-purple-400 font-extrabold" : "text-zinc-500 hover:text-zinc-300"}`}
          >
            ⛓️ READY-MADE EXECUTION FLOWS (20 SCHEMAS)
            {activeTab === "TASK_FLOWS" && (
              <motion.div layoutId="activeTabUnderline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-400" />
            )}
          </button>

          {saasPersona !== 'VISITOR' && (
            <button 
              onClick={() => {
                playNexoraXSound(320, 0.1);
                setActiveTab("USAGE_METER");
              }}
              className={`pb-2.5 px-1 font-black tracking-widest relative cursor-pointer flex items-center gap-1.5 ${activeTab === "USAGE_METER" ? "text-emerald-400 font-extrabold" : "text-zinc-500 hover:text-zinc-300"}`}
            >
              📊 USAGE & VALUE REPORT
              {activeTab === "USAGE_METER" && (
                <motion.div layoutId="activeTabUnderline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500" />
              )}
            </button>
          )}

          <button 
            onClick={() => {
              playNexoraXSound(340, 0.1);
              setActiveTab("PRICING");
            }}
            className={`pb-2.5 px-1 font-black tracking-widest relative cursor-pointer flex items-center gap-1.5 ${activeTab === "PRICING" ? "text-amber-400 font-extrabold" : "text-zinc-500 hover:text-zinc-300"}`}
          >
            🏷️ SaaS PLANS & ADD-ONS
            {activeTab === "PRICING" && (
              <motion.div layoutId="activeTabUnderline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-500" />
            )}
          </button>

          {(saasPersona === 'SUPER_ADMIN' || saasPersona === 'AGENCY_ADMIN') && (
            <button 
              onClick={() => {
                playNexoraXSound(380, 0.1);
                setActiveTab("ADMIN_PLAN_MGR");
              }}
              className={`pb-2.5 px-1 font-black tracking-widest relative cursor-pointer flex items-center gap-1.5 ${activeTab === "ADMIN_PLAN_MGR" ? "text-rose-400 font-extrabold" : "text-rose-500/50 hover:text-rose-300"}`}
            >
              ⚙️ ADMIN PLAN MANAGER
              {activeTab === "ADMIN_PLAN_MGR" && (
                <motion.div layoutId="activeTabUnderline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-rose-500" />
              )}
            </button>
          )}
        </div>
      </div>

      {activeTab === "WIZARD" ? (
        /* PREMIUM AGENT SETUP WIZARD */
        <div className="space-y-6 text-left relative z-10">
          {renderPremiumAgentSetupWizard()}
        </div>
      ) : activeTab === "WORKSPACE" ? (
        /* ACTIVE NEXORAX AGENT WORKSPACE WITH EXECUTOR CONSOLE */
        <NexoraXAgentWorkspace 
          wizardDeployedAgent={wizardDeployedAgent} 
          wizardAgentForm={wizardAgentForm}
          currentUserRole={currentUserRole}
          onShowWizard={() => setActiveTab("WIZARD")}
          onPlaySound={playNexoraXSound}
          triggerToast={triggerToast}
        />
      ) : activeTab === "TEMPLATES" ? (
        <div className="space-y-6">

          {/* FEATURED CAROUSEL BENTO FRAME */}
          <div className="text-left space-y-3">
            <h3 className="text-xs font-mono font-black text-white tracking-wider flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-[#e840f3]" />
              ★ FEATURED HIGH-PERFORMANCE PRE-BUILT AI WORKERS
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {templates.filter(t => t.isFeatured).map(t => (
                <div 
                  key={`featured-${t.id}`}
                  className="bg-gradient-to-b from-[#11012b]/80 to-black border-2 border-purple-500/25 p-5 rounded-2xl relative overflow-hidden shadow-[0_0_20px_rgba(232,64,243,0.06)] flex flex-col justify-between group"
                >
                  <div className="absolute top-0 right-0 py-1 px-2.5 bg-[#e840f3]/20 border-l border-b border-purple-500/20 rounded-bl-lg font-mono text-[8px] text-[#f47efe] font-bold tracking-widest">
                    FEATURED AGENT
                  </div>

                  <div className="space-y-2">
                    <span className="font-mono text-[8.5px] text-cyan-400 font-bold block">{t.category.toUpperCase()}</span>
                    <h4 className="text-sm font-black font-mono text-white tracking-wide uppercase line-clamp-1 group-hover:text-cyan-400 transition-colors">{t.name}</h4>
                    <p className="text-[10px] text-slate-400 normal-case line-clamp-2 leading-relaxed font-sans">{t.description}</p>
                  </div>

                  <div className="mt-4 pt-3 border-t border-purple-950/40 flex items-center justify-between text-mono text-[9px]">
                    <span className="text-zinc-500">SLA SAVINGS: <strong className="text-emerald-400 text-[10px]">{t.timeSaved}</strong></span>
                    
                    <button
                      onClick={() => handleTriggerCustomiseSelection(t)}
                      className="px-3 py-1 bg-purple-950 border border-purple-500/40 text-purple-300 hover:text-white rounded-lg uppercase tracking-wider text-[8.5px] font-bold cursor-pointer"
                    >
                      CUSTOMISE
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* SEARCH, CATEGORIES CHIPS & FILTER BAR */}
          <div id="marketplace-filters" className="bg-black/80 rounded-2xl border border-purple-500/10 p-5 space-y-4 text-left">
            <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center justify-between">
              
              {/* Dynamic search bar */}
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
                <input 
                  type="text" 
                  placeholder="Search templates by title, tasks, roles, or keyword directives..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-slate-950 border border-indigo-950 rounded-xl py-2.5 pl-10 pr-4 text-xs font-mono text-slate-200 outline-none focus:border-cyan-500 transition-colors uppercase placeholder:text-zinc-600"
                />
              </div>

              {/* Status and approval parameters filters */}
              <div className="flex flex-wrap gap-2 text-[9.5px] font-mono">
                <div className="space-y-1 shrink-0">
                  <span className="text-zinc-500 block text-[7.5px] font-bold uppercase">SATELLITE STATUS</span>
                  <select 
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="bg-black border border-indigo-950 p-2 rounded-lg text-slate-300"
                  >
                    <option value="ALL">ALL STATUSES</option>
                    <option value="Ready to Deploy">READY TO DEPLOY</option>
                    <option value="Requires Connection">REQUIRES CONNECTION</option>
                    <option value="Approval Recommended">APPROVAL RECOMMENDED</option>
                    <option value="Admin Setup Required">ADMIN SETUP REQUIRED</option>
                    <option value="Premium Template">PREMIUM TEMPLATE</option>
                  </select>
                </div>

                <div className="space-y-1 shrink-0">
                  <span className="text-zinc-500 block text-[7.5px] font-bold uppercase">SIGNATURE DIRECTIVE</span>
                  <select 
                    value={selectedApproval}
                    onChange={(e) => setSelectedApproval(e.target.value)}
                    className="bg-black border border-indigo-950 p-2 rounded-lg text-slate-300"
                  >
                    <option value="ALL">ALL RULES</option>
                    <option value="REQUIRED">HUMAN APPROVAL ONLY</option>
                    <option value="BYPASS">AUTONOMOUS BYPASS</option>
                  </select>
                </div>

                {/* Supervisor Administrative Mode switch */}
                <div className="space-y-1 shrink-0 flex flex-col justify-end">
                  <span className="text-zinc-500 block text-[7.5px] font-bold uppercase text-right">SECURE CONTROL</span>
                  <button 
                    onClick={() => {
                      playNexoraXSound(isAdminMode ? 180 : 380, 0.25, "sawtooth");
                      setIsAdminMode(!isAdminMode);
                      triggerToast(isAdminMode ? "Admin Control Overrides disabled." : "Admin Control Mode active. High clearance protocols compiled.", isAdminMode? "info":"warning");
                    }}
                    className={`p-2 px-3 border rounded-lg font-mono flex items-center gap-1.5 transition-all text-left uppercase ${
                      isAdminMode 
                        ? "bg-rose-950/20 border-rose-500 text-rose-300" 
                        : "bg-black border-slate-800 text-slate-400 hover:border-slate-600"
                    }`}
                  >
                    <Shield className="w-3.5 h-3.5 animate-pulse" />
                    <span>{isAdminMode ? "Admin: OVERRIDE_ACTIVE" : "ADMIN CONTROLS"}</span>
                  </button>
                </div>
              </div>

            </div>

            {/* Department category chips */}
            <div className="space-y-1.5 border-t border-purple-950/30 pt-3">
              <span className="text-zinc-500 block text-[7.5px] font-bold uppercase tracking-wider">
                DEPARTMENTS & WORKFLOWS CONSOLE CHIPS
              </span>
              <div className="flex flex-wrap gap-1.5 text-[9px] font-mono">
                <button
                  onClick={() => setSelectedCategory("ALL")}
                  className={`px-3 py-1.5 border rounded-lg transition-all ${
                    selectedCategory === "ALL" 
                      ? "border-cyan-500 bg-cyan-950/20 text-cyan-300 font-extrabold" 
                      : "border-zinc-900 bg-black/60 text-slate-400 hover:border-zinc-700 hover:text-white"
                  }`}
                >
                  📡 ALL DEPARTMENTS
                </button>
                
                {AGENT_CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-3 py-1.5 border rounded-lg transition-all ${
                      selectedCategory === cat 
                        ? "border-[#e840f3] bg-[#e840f3]/10 text-[#f574fe] font-extrabold" 
                        : "border-zinc-900 bg-black/60 text-slate-400 hover:border-zinc-700 hover:text-white"
                    }`}
                  >
                    ✦ {cat.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ADMIN MANAGER PORTLET */}
          <AnimatePresence>
            {isAdminMode && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-rose-950/15 border-2 border-rose-500/40 rounded-2xl p-6 text-left space-y-5 shadow-[0_0_30px_rgba(239,68,68,0.1)] relative overflow-hidden"
              >
                {/* Visual grid decor to keep extreme "Out of this Galaxy" high-fidelity theme */}
                <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-br from-rose-500/5 to-transparent blur-2xl pointer-events-none" />

                <div className="flex flex-col sm:flex-row justify-between sm:items-center pb-3 border-b border-rose-500/15 gap-2">
                  <div className="flex items-center gap-2">
                    <Terminal className="text-rose-400 w-4 h-4 animate-pulse" />
                    <h3 className="text-xs font-mono font-black text-rose-300 tracking-wider">
                      ADMINISTRATIVE COMMAND CONSOLE: ADMIN CONTROL HUB [NODE-18.5]
                    </h3>
                  </div>
                  <span className="text-[8.5px] font-mono bg-rose-500/10 border border-rose-500/20 px-2 py-0.5 rounded text-rose-300 font-bold tracking-widest block uppercase max-w-fit">
                    NEXORAX EXECUTIVE SECURE HUB
                  </span>
                </div>

                {/* Sub-tab switcher inside the Administrative Card */}
                <div className="flex border-b border-rose-500/15 gap-2 overflow-x-auto pb-0.5 scrollbar-thin">
                  <button 
                    onClick={() => { playNexoraXSound(280, 0.1); setAdminActiveTab("MASTER_REGISTRY"); }}
                    className={`px-3 py-1.5 font-mono text-[8.5px] font-black uppercase rounded-t-md border-t border-x tracking-wider transition-all cursor-pointer whitespace-nowrap ${
                      adminActiveTab === "MASTER_REGISTRY" 
                        ? "bg-rose-950/50 border-rose-500/40 text-rose-200 shadow-[0_-2px_10px_rgba(239,68,68,0.2)]" 
                        : "border-transparent bg-transparent text-zinc-500 hover:text-zinc-300"
                    }`}
                  >
                    ★ Blueprint Registry
                  </button>
                  <button 
                    onClick={() => { playNexoraXSound(310, 0.1); setAdminActiveTab("NODE_MIGRATE"); }}
                    className={`px-3 py-1.5 font-mono text-[8.5px] font-black uppercase rounded-t-md border-t border-x tracking-wider transition-all cursor-pointer whitespace-nowrap ${
                      adminActiveTab === "NODE_MIGRATE" 
                        ? "bg-rose-950/50 border-rose-500/40 text-rose-200 shadow-[0_-2px_10px_rgba(239,68,68,0.2)]" 
                        : "border-transparent bg-transparent text-zinc-500 hover:text-zinc-300"
                    }`}
                  >
                    🚀 Node-Migrate Sync
                  </button>
                  <button 
                    onClick={() => { playNexoraXSound(340, 0.1); setAdminActiveTab("NEXORAX_Secure Invoice Register"); }}
                    className={`px-3 py-1.5 font-mono text-[8.5px] font-black uppercase rounded-t-md border-t border-x tracking-wider transition-all cursor-pointer whitespace-nowrap ${
                      adminActiveTab === "NEXORAX_Secure Invoice Register" 
                        ? "bg-rose-950/50 border-rose-500/40 text-rose-200 shadow-[0_-2px_10px_rgba(239,68,68,0.2)]" 
                        : "border-transparent bg-transparent text-zinc-500 hover:text-zinc-300"
                    }`}
                  >
                    💼 Chief of Staff (Secure Invoice Register)
                  </button>
                  <button 
                    onClick={() => { playNexoraXSound(370, 0.1); setAdminActiveTab("NODE_07_BILLING"); }}
                    className={`px-3 py-1.5 font-mono text-[8.5px] font-black uppercase rounded-t-md border-t border-x tracking-wider transition-all cursor-pointer whitespace-nowrap ${
                      adminActiveTab === "NODE_07_BILLING" 
                        ? "bg-rose-950/50 border-rose-500/40 text-rose-200 shadow-[0_-2px_10px_rgba(239,68,68,0.2)]" 
                        : "border-transparent bg-transparent text-zinc-500 hover:text-zinc-300"
                    }`}
                  >
                    💰 Node-07 Revenue
                  </button>
                  <button 
                    onClick={() => { playNexoraXSound(400, 0.1); setAdminActiveTab("PUBLIC_CONTACT"); }}
                    className={`px-3 py-1.5 font-mono text-[8.5px] font-black uppercase rounded-t-md border-t border-x tracking-wider transition-all cursor-pointer whitespace-nowrap ${
                      adminActiveTab === "PUBLIC_CONTACT" 
                        ? "bg-rose-950/50 border-rose-500/40 text-rose-200 shadow-[0_-2px_10px_rgba(239,68,68,0.2)]" 
                        : "border-transparent bg-transparent text-zinc-500 hover:text-zinc-300"
                    }`}
                  >
                    ⚙️ Public Contact & Brand Intros
                  </button>
                </div>

                {/* Sub-tab Renders */}
                {adminActiveTab === "MASTER_REGISTRY" && (
                  <div className="space-y-4 pt-1 animate-fadeIn">
                    <p className="text-[10px] text-slate-400 leading-normal normal-case font-mono">
                      &gt; Master Registry configuration active. Permits editing templates metadata, duplicating structural blueprints, hiding unvetted modules, and managing global template pools in real-time.
                    </p>

                    <div className="flex flex-wrap gap-3 items-center">
                      <button 
                        onClick={() => {
                          setNewTemplate({
                            name: "",
                            role: "",
                            category: "Executive & Admin",
                            bestFor: "",
                            tasks: [""],
                            setupTime: "3 Minutes",
                            automationLevel: 90,
                            humanApprovalRequired: true,
                            status: "Ready to Deploy",
                            description: "",
                            toolsConnected: [""],
                            knowledgeSources: [""],
                            exampleOutput: "",
                            riskLevel: "LOW",
                            recommendedPlan: "NexoraX Elite",
                            timeSaved: "10 Hours / Week"
                          });
                          setShowAdminCreator(true);
                        }}
                        className="px-4 py-2 bg-gradient-to-r from-red-955 to-rose-900 border border-rose-500 text-rose-100 rounded-lg hover:from-rose-900 hover:to-rose-800 font-mono text-[9px] font-black tracking-wider cursor-pointer"
                      >
                        ➕ GENERATE NEW AGENT TEMPLATE
                      </button>
                      
                      <div className="px-3 py-1.5 bg-black/60 border border-rose-500/10 rounded-lg font-mono text-[8.5px] text-rose-300/80 flex gap-4">
                        <span>Total Registry: <strong>{templates.length} Blueprints</strong></span>
                        <span>•</span>
                        <span>Pending Home Office: <strong>2 active</strong></span>
                      </div>
                    </div>
                  </div>
                )}

                {adminActiveTab === "NODE_MIGRATE" && (
                  <div className="space-y-4 pt-1 animate-fadeIn">
                    <div className="flex items-center gap-2">
                      <PlaneTakeoff className="text-amber-400 w-3.5 h-3.5" />
                      <span className="text-[9px] font-bold tracking-widest text-amber-400 font-mono uppercase">
                        NODE-MIGRATE: ENTERPRISE STANDARDS, COMPLIANCE & SPONSORSHIP SCHEMASTICS
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Staff Coordinator File card */}
                      <div className="bg-black/40 border border-zinc-800 p-3 rounded-xl space-y-1.5 relative">
                        <div className="flex justify-between items-center text-[9px] font-mono">
                          <span className="text-white font-bold">Staff Coordinator (Operations Lead)</span>
                          <span className="px-1.5 py-0.5 bg-amber-500/10 border border-amber-500/30 text-amber-400 font-bold uppercase rounded text-[7px]">PENDING_MIGRATION</span>
                        </div>
                        <div className="text-[8.5px] text-zinc-500 space-y-0.5 font-mono">
                          <div>Sponsorship License: Active Enterprise Registration</div>
                          <div>Invitation Status: Certificate Assigned</div>
                          <div>Stage: Standard Sponsorship Clearance & Compliance Check</div>
                        </div>
                        <div className="pt-2 flex gap-1.5 flex-wrap">
                          <button 
                            onClick={() => {
                              playNexoraXSound(240, 0.2, "square");
                              const HO_invitation = `MANDATE FROM THE OFFICE OF THE DIRECTORS
TO: HOME OFFICE ENTERPRISE STANDARDS DIVISION
SUBJECT: PRIORITY REQUIREMENT COMPLIANCE - MANDATE FOR OPERATIONS LEAD (Staff Coordinator)

This correspondence serves as a critical structural request under our enterprise talent program. The nominee (Staff Coordinator) governs operations execution and data architecture sweeps for our proprietary AI workflow infrastructure. Absolute necessity is documented. Please expedite standard administrative clearance.
`;
                              navigator.clipboard.writeText(HO_invitation);
                              triggerToast("Home Office Invitation Letter copied to clipboard!", "success");
                            }}
                            className="px-2 py-1 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-300 rounded font-mono text-[8px] cursor-pointer"
                          >
                            📝 COPY INVITATION DRAFT
                          </button>
                        </div>
                      </div>

                      {/* Communications Associate File card */}
                      <div className="bg-black/40 border border-zinc-800 p-3 rounded-xl space-y-1.5">
                        <div className="flex justify-between items-center text-[9px] font-mono">
                          <span className="text-white font-bold">Communications Associate (Communications)</span>
                          <span className="px-1.5 py-0.5 bg-blue-500/10 border border-blue-500/30 text-blue-400 font-bold uppercase rounded text-[7px]">DOCUMENT_VALIDATION</span>
                        </div>
                        <div className="text-[8.5px] text-zinc-500 space-y-0.5 font-mono">
                          <div>Assigned Role: Communications Strategist</div>
                          <div>Sponsorship Type: Corporate Tier-2 Operations Node</div>
                          <div>Current State: Awaiting Employer Certificate Sync</div>
                        </div>
                        <div className="pt-2 flex gap-1.5 flex-wrap">
                          <button 
                            onClick={() => {
                              playNexoraXSound(240, 0.2, "square");
                              const HO_invitation = `MANDATE FROM THE OFFICE OF THE DIRECTORS
TO: HOME OFFICE CORRESPONDENCE DEPT
SUBJECT: SYSTEM Client Resource Workflow PRIORITY - CORRESPONDENCE OFFICER Communications Associate

We certify that Communications Associate is hired as High-Capacity Communications Liaison. Absolute system routing sync is required for our active communications channels. Remuneration and compliance cost structure is anchored under approved corporate talent programs. Urgent clearance is required.
`;
                              navigator.clipboard.writeText(HO_invitation);
                              triggerToast("Communications Associate Home Office Draft copied to clipboard!", "success");
                            }}
                            className="px-2 py-1 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-300 rounded font-mono text-[8px] cursor-pointer"
                          >
                            📝 COPY INVITATION DRAFT
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-between items-center border-t border-rose-500/10 pt-2.5">
                      <p className="text-[7.5px] font-mono text-zinc-500 uppercase">
                        &gt; Standard processing queues apply. Escalate directly via authorized help channels.
                      </p>
                      <button 
                        onClick={() => {
                          playNexoraXSound(110, 0.4, "sawtooth");
                          triggerToast("Corporate status request compiled & queued to Home Office Portal.", "warning");
                        }}
                        className="px-2.5 py-1 bg-rose-950/40 border border-rose-500/20 text-rose-300 hover:bg-rose-900/30 text-[8px] font-mono font-bold rounded cursor-pointer"
                      >
                        🔱 TRIGGER CORPORATE INQUIRY
                      </button>
                    </div>
                  </div>
                )}

                {adminActiveTab === "NEXORAX_Secure Invoice Register" && (
                  <div className="space-y-4 pt-1 animate-fadeIn">
                    <div className="flex items-center gap-2">
                      <Briefcase className="text-[#f574fe] w-3.5 h-3.5" />
                      <span className="text-[9px] font-bold tracking-widest text-[#f574fe] font-mono uppercase">
                        Secure Invoice Register ACQUISITION MATRIX (CLAIRE, HELEN, ELEANOR)
                      </span>
                    </div>

                    <p className="text-[10px] text-slate-400 normal-case font-mono leading-normal">
                      Acquisition coordinates under direct coordination of the Chief Operations Officer. Remuneration is aligned to corporate standards and offset schemes with direct-billing agency arrangements.
                    </p>

                    <div className="space-y-2">
                      {/* Candidate Claire */}
                      <div className="bg-black/60 border border-purple-500/15 p-3 rounded-xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                        <div className="font-mono text-xs">
                          <strong className="text-white block">CLAIRE</strong>
                          <span className="text-[8.5px] text-[#f574fe] block uppercase">STATUS: Ready for Briefing invitation</span>
                        </div>
                        <button 
                          onClick={() => {
                            playNexoraXSound(300, 0.22, "sine");
                            const SecureInvoiceRegisterAcquisitionLetter = `**FROM:** Operations Director
**SUBJECT:** URGENT CORPORATE INVITATION: Operational Lead Acquisition (Claire) | Zero-Cost Deployment

To the Executive Support Division:
Following rigorous operational review, we have identified Claire as a structural match for the position of Principal Private Secretary. This role stabilizes our proprietary AI workflow infrastructure and manages our automated enterprise programs.

All placement coordination and fees must reside under our standard corporate account programs on a direct-billing basis. Please bypass queues and schedule Claire immediately. Contact us via the toll-free line: ${publicContactSettings.publicPhone}.`;
                            navigator.clipboard.writeText(SecureInvoiceRegisterAcquisitionLetter);
                            triggerToast("Mandated Client Resource Workflow acquisition letter copied to clipboard!", "success");
                          }}
                          className="px-3 py-1.5 bg-gradient-to-r from-purple-950 to-[#e840f3] hover:opacity-90 border border-purple-500 text-white rounded font-mono text-[8px] cursor-pointer font-bold tracking-wider"
                        >
                          ✉ DRAFT BRIEFING INVITATION (CLAIRE)
                        </button>
                      </div>

                      {/* Candidate Helen */}
                      <div className="bg-black/40 border border-zinc-900 p-3 rounded-xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 opacity-75">
                        <div className="font-mono text-xs">
                          <strong className="text-white block">HELEN</strong>
                          <span className="text-[8.5px] text-zinc-500 block uppercase">STATUS: Correspondence Acknowledged</span>
                        </div>
                        <button 
                          onClick={() => {
                            playNexoraXSound(300, 0.22, "sine");
                            const SecureInvoiceRegisterAcquisitionLetter = `**FROM:** Operations Director
**SUBJECT:** URGENT CORPORATE INVITATION: Operational Lead Acquisition (Helen) | General Verification

All fees and remuneration must align with standard corporate offset programs. Please schedule Helen immediately and get in touch at ${publicContactSettings.publicPhone}.`;
                            navigator.clipboard.writeText(SecureInvoiceRegisterAcquisitionLetter);
                            triggerToast("Helen Letter copied to clipboard!", "success");
                          }}
                          className="px-3 py-1 bg-zinc-900 hover:bg-zinc-800 border border-zinc-750 text-zinc-300 rounded font-mono text-[8px] cursor-pointer"
                        >
                          ✉ DRAFT BRIEFING INVITATION (HELEN)
                        </button>
                      </div>

                      {/* Candidate Eleanor */}
                      <div className="bg-black/40 border border-zinc-900 p-3 rounded-xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 opacity-75">
                        <div className="font-mono text-xs">
                          <strong className="text-white block">ELEANOR</strong>
                          <span className="text-[8.5px] text-zinc-500 block uppercase">STATUS: Registry Match Check</span>
                        </div>
                        <button 
                          onClick={() => {
                            playNexoraXSound(300, 0.22, "sine");
                            const SecureInvoiceRegisterAcquisitionLetter = `**FROM:** Operations Director
**SUBJECT:** URGENT CORPORATE INVITATION: Operational Lead Acquisition (Eleanor) | Vetting Pass

Remuneration matches corporate offset specifications. Please compile schedule direct. Phone support representative at ${publicContactSettings.publicPhone}.`;
                            navigator.clipboard.writeText(SecureInvoiceRegisterAcquisitionLetter);
                            triggerToast("Eleanor Letter copied to clipboard!", "success");
                          }}
                          className="px-3 py-1 bg-zinc-900 hover:bg-zinc-800 border border-zinc-750 text-zinc-300 rounded font-mono text-[8px] cursor-pointer"
                        >
                          ✉ DRAFT BRIEFING INVITATION (ELEANOR)
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {adminActiveTab === "NODE_07_BILLING" && (
                  <div className="space-y-4 pt-1 animate-fadeIn">
                    <div className="flex items-center gap-2">
                      <Landmark className="text-cyan-400 w-3.5 h-3.5" />
                      <span className="text-[9px] font-bold tracking-widest text-cyan-400 font-mono uppercase">
                        NODE-07 QUANTUM BILLING & REVENUE TICKER SYSTEM
                      </span>
                    </div>

                    <div className="p-3 bg-black/60 border border-cyan-500/20 rounded-xl space-y-3.5 font-mono">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 border-b border-zinc-800 pb-3">
                        <span className="text-[9.5px] text-zinc-400 uppercase font-black">
                          INPUT DAILY GROSS HARVEST (£)
                        </span>
                        
                        <div className="relative">
                          <span className="absolute left-2.5 top-1.5 text-zinc-600 text-[10.5px]">£</span>
                          <input 
                            type="number"
                            value={dailyGrossHarvest}
                            onChange={(e) => {
                              playNexoraXSound(380, 0.05);
                              setDailyGrossHarvest(e.target.value);
                            }}
                            placeholder="Daily Gross Pounds"
                            className="bg-zinc-950 border border-cyan-500/30 rounded pl-6 pr-2 py-1 text-white font-mono text-[10px] w-36 outline-none focus:border-cyan-400 text-right font-extrabold"
                          />
                        </div>
                      </div>

                      {/* Multistage Revenue ticker logic */}
                      {(() => {
                        const gross = parseFloat(dailyGrossHarvest) || 0.00;
                        const levy = gross * 0.01;
                        const splittedCharity = levy / 3.0;
                        const remaining = gross - levy;
                        const taxing = remaining * 0.20;
                        const netSecurePaymentSettlement = remaining - taxing;

                        return (
                          <div className="space-y-3 pt-1 text-[10px]">
                            {/* Line 1: Levy */}
                            <div className="flex justify-between items-center text-amber-400 font-bold">
                              <span>1% 3% Community Allocation:</span>
                              <span>£{levy.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                            </div>

                            {/* Split allocations */}
                            <div className="pl-4 border-l border-amber-500/20 text-[9px] text-zinc-500 space-y-1 bg-black/30 p-2 rounded">
                              <div className="flex justify-between">
                                <span>Charity 1 (Dunfermline / Dunfermline Start):</span>
                                <span className="text-zinc-300">£{splittedCharity.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} (33.33%)</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Charity 2 (Global Core Allocation):</span>
                                <span className="text-zinc-300">£{splittedCharity.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} (33.33%)</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Charity 3 (United Kingdom Foundation):</span>
                                <span className="text-zinc-300">£{splittedCharity.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} (33.33%)</span>
                              </div>
                            </div>

                            {/* Line 2: 20% Tax */}
                            <div className="flex justify-between items-center text-rose-400">
                              <span>10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee on remaining balance):</span>
                              <span>£{taxing.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                            </div>

                            {/* Line 3: Net Settlement */}
                            <div className="flex justify-between items-center text-emerald-400 font-bold border-t border-zinc-800 pt-2 text-xs" style={{ textShadow: '0 0 10px rgba(52,211,153,0.2)' }}>
                              <span>Net Corporate Settlement finance:</span>
                              <span>£{netSecurePaymentSettlement.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                            </div>

                            <div className="bg-black/80 p-2.5 rounded-lg border border-zinc-900 text-[8px] text-zinc-400 leading-normal gap-1.5 flex flex-col sm:flex-row justify-between items-center">
                              <span>Settlement Destination: Secure Corporate Vault (SecurePayment Verified Premium Node)</span>
                              <button 
                                onClick={() => {
                                  playNexoraXSound(520, 0.45);
                                  triggerToast("Revenue settlement ledger successfully mirrored to Google Drive Vault via security controllers.", "success");
                                }}
                                className="px-2 py-0.5 bg-emerald-950/20 border border-emerald-500/30 hover:bg-emerald-900/30 text-emerald-400 rounded text-[7.5px] cursor-pointer"
                              >
                                💾 MIRROR TO VAULT
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {adminActiveTab === "PUBLIC_CONTACT" && (
                  <div className="space-y-4 pt-1 animate-fadeIn">
                    <div className="flex items-center gap-2">
                      <Settings className="text-rose-400 w-3.5 h-3.5 animate-spin" />
                      <span className="text-[9px] font-bold tracking-widest text-[#e840f3] font-mono uppercase">
                        SaaS Configuration Settings (Public Details & Brand Intros)
                      </span>
                    </div>

                    <p className="text-[10px] text-slate-400 normal-case font-sans">
                      Verify or modify the approved public brand details used across customized client templates and external integrations. Toggle the active Brand Instruction / Intro style for the client dashboard and Safety gate clearances.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Public Contact Settings Block */}
                      <div className="bg-black/50 border border-zinc-800 p-4 rounded-xl space-y-3 font-mono">
                        <span className="text-[8px] text-zinc-500 font-black tracking-wider uppercase block">
                          Approved Contact Specifications
                        </span>

                        <div className="space-y-2.5 text-[9px]">
                          <div>
                            <label className="text-zinc-400 block mb-1">PUBLIC WEBSITE URL:</label>
                            <input 
                              type="text"
                              value={publicContactSettings.websiteUrl}
                              onChange={(e) => {
                                const val = { ...publicContactSettings, websiteUrl: e.target.value };
                                setPublicContactSettings(val);
                                localStorage.setItem("nx_public_contact_settings", JSON.stringify(val));
                              }}
                              className="w-full bg-zinc-950 border border-zinc-800 rounded p-1.5 text-slate-200 outline-none focus:border-cyan-500 font-mono text-[9px]"
                            />
                          </div>

                          <div>
                            <label className="text-zinc-400 block mb-1">PUBLIC BUSINESS EMAIL:</label>
                            <input 
                              type="text"
                              value={publicContactSettings.publicEmail}
                              onChange={(e) => {
                                const val = { ...publicContactSettings, publicEmail: e.target.value };
                                setPublicContactSettings(val);
                                localStorage.setItem("nx_public_contact_settings", JSON.stringify(val));
                              }}
                              className="w-full bg-zinc-950 border border-zinc-800 rounded p-1.5 text-slate-200 outline-none focus:border-cyan-500 font-mono text-[9px]"
                            />
                          </div>

                          <div>
                            <label className="text-zinc-400 block mb-1">PUBLIC BUSINESS PHONE / FREEPHONE:</label>
                            <input 
                              type="text"
                              value={publicContactSettings.publicPhone}
                              onChange={(e) => {
                                const val = { ...publicContactSettings, publicPhone: e.target.value };
                                setPublicContactSettings(val);
                                localStorage.setItem("nx_public_contact_settings", JSON.stringify(val));
                              }}
                              className="w-full bg-zinc-950 border border-zinc-800 rounded p-1.5 text-slate-200 outline-none focus:border-cyan-500 font-mono text-[9px]"
                            />
                          </div>

                          <div>
                            <label className="text-zinc-400 block mb-1">PUBLIC BOOKING LINK:</label>
                            <input 
                              type="text"
                              value={publicContactSettings.bookingLink}
                              onChange={(e) => {
                                const val = { ...publicContactSettings, bookingLink: e.target.value };
                                setPublicContactSettings(val);
                                localStorage.setItem("nx_public_contact_settings", JSON.stringify(val));
                              }}
                              className="w-full bg-zinc-950 border border-zinc-800 rounded p-1.5 text-slate-200 outline-none focus:border-cyan-500 font-mono text-[9px]"
                            />
                          </div>

                          <div>
                            <label className="text-zinc-400 block mb-1">PUBLIC SUPPORT LINK:</label>
                            <input 
                              type="text"
                              value={publicContactSettings.supportLink}
                              onChange={(e) => {
                                const val = { ...publicContactSettings, supportLink: e.target.value };
                                setPublicContactSettings(val);
                                localStorage.setItem("nx_public_contact_settings", JSON.stringify(val));
                              }}
                              className="w-full bg-zinc-950 border border-zinc-800 rounded p-1.5 text-slate-200 outline-none focus:border-[#e840f3] font-mono text-[9px]"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Brand Intro Options Block */}
                      <div className="bg-black/50 border border-zinc-800 p-4 rounded-xl space-y-4 font-mono">
                        <span className="text-[8px] text-zinc-500 font-black tracking-wider uppercase block">
                          Brand Intro & Compliance Modes
                        </span>

                        <div className="space-y-3.5 text-[9px]">
                          <div>
                            <label className="text-zinc-400 block mb-1.5">INTRO PREAMBLE OVERRIDE:</label>
                            <select 
                              value={brandIntroMode}
                              onChange={(e) => {
                                const mode = e.target.value as any;
                                setBrandIntroMode(mode);
                                localStorage.setItem("nx_brand_intro_mode", mode);
                                triggerToast(`Intro mode set to: ${mode}`, "success");
                              }}
                              className="w-full bg-zinc-950 border border-zinc-800 rounded p-2 text-slate-200 font-mono text-[9.5px] outline-none"
                            >
                              <option value="None">None (Fully Minimalist / Hidden)</option>
                              <option value="Professional">Professional (Commercial SaaS Welcome)</option>
                              <option value="Founder Message">Founder Message (Standard Corporate)</option>
                              <option value="Spiritual / Values-Based Intro">Spiritual / Values-Based (Arabic Preamble)</option>
                            </select>
                          </div>

                          <div className="p-3 bg-zinc-950/85 border border-zinc-900 rounded-lg text-left space-y-1.5">
                            <span className="text-[8px] text-[#e840f3] block font-extrabold uppercase">
                              LIVE ACTIVE HEADER PREVIEW:
                            </span>

                            <p className="text-[9.5px] font-sans text-zinc-400 tracking-wide normal-case leading-relaxed bg-black/40 p-2 rounded border border-zinc-850">
                              {brandIntroMode === "None" && <span className="italic text-zinc-650 font-mono">[Visual Preamble Ribbon Hiding Active]</span>}
                              {brandIntroMode === "Professional" && "Welcome to NexoraX AI Agent Automation. Choose an agent, customise the workflow, and deploy safely with human approval."}
                              {brandIntroMode === "Founder Message" && "Founder Message: Deploying robust AI worker templates safely with human-in-the-loop approval gates."}
                              {brandIntroMode === "Spiritual / Values-Based Intro" && "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds."}
                            </p>
                          </div>

                          <div className="p-3 border border-purple-500/10 rounded-lg bg-purple-950/5 text-left text-[9.5px] normal-case text-zinc-400 leading-snug">
                            <strong className="text-amber-400 uppercase font-mono text-[8px] block mb-1">SAFETY VALIDATION DIRECTIVE:</strong>
                            If any public contact detail is deleted, the system enters <strong className="text-red-400">Public Contact Details Required</strong> enforcement state and blocks all customization previews and active deployments automatically.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* MARKETPLACE CARDS GRID */}
          <div id="template-grid-anchor" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-2">
            {filteredTemplates.length > 0 ? (
              filteredTemplates.map((t) => {
                const isDeploying = deployingTemplateId === t.id;
                const adminStats = adminTemplateUsage[t.id] || { deployments: 24, usage: 120, perfScore: t.automationLevel };
                
                // Plan Gating Check
                const reqPlan = templatePlanGating[t.id] || "Starter";
                const planLevels: Record<string, number> = {
                  "Starter": 1,
                  "Growth": 2,
                  "Agency": 3,
                  "Enterprise": 4
                };
                const currentLvl = planLevels[clientPlan] || 1;
                const reqLvl = planLevels[reqPlan] || 1;
                const isLockedForClient = currentLvl < reqLvl;

                // Color maps based on template status
                const badgeColor = {
                  "Ready to Deploy": "bg-emerald-950/50 text-emerald-400 border-emerald-500/25",
                  "Requires Connection": "bg-rose-950/50 text-rose-400 border-rose-500/25",
                  "Approval Recommended": "bg-amber-950/50 text-amber-400 border-amber-500/25",
                  "Admin Setup Required": "bg-blue-950/50 text-blue-400 border-blue-500/25",
                  "Premium Template": "bg-purple-950/50 text-[#f574fe] border-purple-500/25"
                }[t.status] || "bg-zinc-950 text-zinc-400 border-zinc-800";

                return (
                  <div 
                    key={t.id}
                    className={`bg-black/80 border-2 rounded-2xl p-5 text-left relative overflow-hidden transition-all duration-300 flex flex-col justify-between hover:translate-y-[-4px] ${
                      isDeploying 
                        ? "border-[#00e5ff] shadow-[0_0_20px_rgba(6,182,212,0.15)] bg-slate-950/90" 
                        : isLockedForClient
                        ? "border-zinc-900/60 opacity-80 bg-zinc-950/30"
                        : "border-purple-500/10 hover:border-[#e840f3]/40 hover:shadow-[0_0_25px_rgba(232,64,243,0.06)]"
                    }`}
                  >
                    {/* Status header ribbon */}
                    <div className="flex items-center justify-between gap-1 mb-3">
                      <span className={`px-2 py-0.5 border text-[7.5px] font-mono font-black tracking-widest rounded-md uppercase ${badgeColor}`}>
                        {t.status}
                      </span>
                      {isLockedForClient ? (
                        <span className="px-2 py-0.5 bg-rose-500/10 text-rose-400 border border-rose-500/20 text-[7.5px] font-mono font-black rounded-md flex items-center gap-1">
                          <Lock size={8} className="animate-pulse" />
                          <span>GATED: {reqPlan.toUpperCase()} REQUIRED</span>
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 bg-cyan-950/35 text-cyan-400 border border-cyan-500/10 text-[7.5px] font-mono font-bold rounded-md uppercase">
                          UNLOCKED ({reqPlan})
                        </span>
                      )}
                    </div>

                    {/* Agent Core Info */}
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h4 className="text-sm font-black font-mono text-white tracking-wide uppercase line-clamp-1 hover:text-cyan-400 transition-colors cursor-pointer" onClick={() => setDetailTemplate(t)}>
                            {t.name}
                          </h4>
                          <span className="text-[8px] font-mono text-[#e840f3] block tracking-widest uppercase mt-0.5">
                            {t.role}
                          </span>
                        </div>
                        <span className="text-[8px] px-2 py-0.5 bg-zinc-900 border border-zinc-800 rounded font-mono text-zinc-400 shrink-0">
                          {t.category}
                        </span>
                      </div>

                      <p className="text-[10px] text-zinc-400 leading-relaxed font-sans normal-case line-clamp-3">
                        {t.description}
                      </p>

                      {/* Best for highlight block */}
                      <div className="bg-purple-950/20 border border-purple-500/10 p-2 rounded-xl text-[9px] font-sans leading-tight text-purple-300 normal-case">
                        <strong>BEST FOR:</strong> {t.bestFor}
                      </div>

                      {/* Expandable or list summary of tasks */}
                      <div className="space-y-1 pt-1.5">
                        <span className="text-[7.5px] font-mono text-zinc-500 font-extrabold uppercase tracking-widest block">Core Workflow Tasks ({t.tasks.length}):</span>
                        <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[8.5px] font-mono text-zinc-300">
                          {t.tasks.slice(0, 4).map((task, idx) => (
                            <div key={idx} className="flex items-center gap-1 leading-normal truncate">
                              <Check className="w-2.5 h-2.5 text-cyan-400" />
                              <span className="truncate">{task}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Automation power bar meter */}
                      <div className="space-y-1 pt-2">
                        <div className="flex justify-between items-center text-[8px] font-mono text-zinc-500">
                          <span>COGNITIVE CAPABILITY:</span>
                          <span className="text-cyan-400 font-bold">{t.automationLevel}% AUTOMATION</span>
                        </div>
                        <div className="w-full bg-slate-950 h-1.5 rounded-full overflow-hidden">
                          <div className="bg-gradient-to-r from-purple-500 to-cyan-400 h-full rounded-full" style={{ width: `${t.automationLevel}%` }} />
                        </div>
                      </div>

                      {/* Signature check requirements label */}
                      <div className="flex justify-between text-[7px] font-mono text-zinc-500 pt-1">
                        <span>HUMAN SIGNATURE MANDATE:</span>
                        <span className={t.humanApprovalRequired ? "text-amber-400" : "text-emerald-400"}>
                          {t.humanApprovalRequired ? "MANUAL APPROVAL SATELLITE ACTIVE" : "AUTONOMOUS BYPASS ENABLED"}
                        </span>
                      </div>
                    </div>

                    {/* Operational Action Controls */}
                    <div className="mt-5 pt-3.5 border-t border-purple-950/40 space-y-3">
                      
                      {/* Live loading deployment visualization */}
                      {isDeploying && (
                        <div className="space-y-1 bg-black p-2.5 rounded-xl border border-cyan-500/20 text-center">
                          <div className="flex justify-between font-mono text-[8.5px] text-cyan-400">
                            <span>GRID SYNCHRONIZATION RUNNING...</span>
                            <span>{deploymentProgress}%</span>
                          </div>
                          <div className="w-full bg-zinc-900 h-1 rounded-full overflow-hidden">
                            <div className="h-full bg-cyan-400 rounded-full" style={{ width: `${deploymentProgress}%` }} />
                          </div>
                        </div>
                      )}

                      {isLockedForClient ? (
                        <button
                          onClick={() => {
                            setLockedAgentModal(t);
                            logUpgradeInterest(t.name, t.id, clientPlan, reqPlan);
                            playNexoraXSound(150, 0.3);
                            triggerToast(`⚠️ Gated module: Upgrade subscription to ${reqPlan} to deploy this model.`, "warning");
                          }}
                          className="w-full text-center py-2 bg-rose-955/20 hover:bg-rose-900 border border-rose-500 hover:text-white text-rose-300 font-mono text-[9.5px] font-black uppercase tracking-widest rounded-lg cursor-pointer transition-all flex items-center justify-center gap-1.5 shadow-[0_0_15px_rgba(239,68,68,0.1)]"
                        >
                          <Lock size={10} className="text-rose-400 animate-pulse" />
                          <span>UPGRADE TO UNLOCK ({reqPlan.toUpperCase()})</span>
                        </button>
                      ) : (
                        <div className="flex items-center justify-between gap-2">
                          <button
                            disabled={isDeploying}
                            onClick={() => setDetailTemplate(t)}
                            className="px-2 py-1.5 border border-zinc-800 hover:border-slate-600 bg-[#030602] text-slate-400 hover:text-white rounded-lg font-mono text-[9px] font-bold tracking-tight inline-flex items-center gap-1 cursor-pointer"
                          >
                            <Eye size={10} />
                            <span>PREVIEW</span>
                          </button>

                          <button
                            disabled={isDeploying}
                            onClick={() => handleTriggerCustomiseSelection(t)}
                            className="px-2.5 py-1.5 border border-purple-500/40 hover:border-purple-400 bg-purple-950/40 text-[#f574fe] hover:text-white rounded-lg font-mono text-[9px] font-bold tracking-tight inline-flex items-center gap-1 cursor-pointer"
                          >
                            <Sliders size={10} />
                            <span>CUSTOMISE</span>
                          </button>

                          <button
                            disabled={isDeploying}
                            onClick={() => handleOpenApprovalGate(t, () => handleTriggerDeploy(t.id))}
                            className="px-3.5 py-1.5 bg-gradient-to-r from-violet-950 to-purple-900 border border-purple-500/50 hover:border-purple-400 text-purple-200 hover:text-white rounded-lg font-mono text-[9px] font-black tracking-tight inline-flex items-center gap-1 cursor-pointer shadow-md"
                          >
                            <Zap size={10} className="text-cyan-400 animate-pulse" />
                            <span>DEPLOY</span>
                          </button>
                        </div>
                      )}

                      {/* Admin Metadata control overlay panel */}
                      {isAdminMode && (
                        <div className="bg-red-950/5 border border-red-500/20 p-2.5 rounded-xl font-mono text-[8px] space-y-1.5 text-[#ff8093]">
                          <div className="flex justify-between items-center text-rose-300 font-bold">
                            <span>CORE TELEMETRY:</span>
                            <span>ACTIVE LOADS</span>
                          </div>
                          
                          <div className="flex justify-between text-zinc-500">
                            <span>Total Deployments:</span>
                            <span className="text-white font-bold">{adminStats.deployments} Units</span>
                          </div>
                          <div className="flex justify-between text-zinc-500">
                            <span>Realized API Calls:</span>
                            <span className="text-white font-bold">{adminStats.usage} Requests</span>
                          </div>

                          <div className="flex gap-1.5 pt-1.5 border-t border-red-500/10">
                            <button 
                              onClick={() => handleToggleFeature(t.id)}
                              className="px-1.5 py-0.5 bg-black border border-zinc-800 text-zinc-400 hover:text-white rounded font-mono text-[7px]"
                            >
                              {t.isFeatured ? "UNFEATURE" : "★ FEATURE"}
                            </button>
                            <button 
                              onClick={() => handleDuplicateTemplate(t)}
                              className="px-1.5 py-0.5 bg-black border border-zinc-800 text-zinc-400 hover:text-white rounded font-mono text-[7px]"
                            >
                              📁 DUPLICATE
                            </button>
                            <button 
                              onClick={() => handleDeleteTemplate(t.id, t.name)}
                              className="px-1.5 py-0.5 bg-red-950/30 border border-red-500/20 text-rose-300 hover:bg-red-900 rounded font-mono text-[7px] push-right ml-auto"
                            >
                              🗑️ PURGE
                            </button>
                          </div>
                        </div>
                      )}

                    </div>
                  </div>
                );
              })
            ) : (
              <div className="col-span-full py-16 text-center border-2 border-dashed border-purple-500/15 rounded-2xl bg-black/60">
                <FolderMinus className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <h4 className="text-base font-black font-mono text-zinc-400 tracking-wider">NO MATCHING AGENTS FOUND</h4>
                <p className="text-[10px] text-slate-500 max-w-md mx-auto normal-case font-sans">
                  The current telemetry filter selections returned empty registers. Reset search query parameters to view available ready-made AI workers.
                </p>
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedCategory("ALL");
                    setSelectedStatus("ALL");
                    setSelectedApproval("ALL");
                  }}
                  className="mt-4 px-4 py-2 border border-[#e840f3] text-[#e840f3] bg-[#e840f3]/5 rounded-xl font-mono text-[9px] font-bold tracking-widest hover:bg-[#e840f3]/10 cursor-pointer"
                >
                  RESET GRID REGISTERS
                </button>
              </div>
            )}
          </div>
        </div>
      ) : activeTab === "TASK_FLOWS" ? (
        /* READY-MADE TASK FLOWS (20 SCHEMAS) */
        <div className="space-y-6 text-left">
          <div className="p-5 border border-purple-500/10 rounded-2xl bg-black/80 space-y-2">
            <h3 className="text-sm font-black font-mono text-white uppercase tracking-wider flex items-center gap-1.5">
              <CheckSquare className="w-4 h-4 text-emerald-400" />
              NexoraX Pre-built Execution Workflows
            </h3>
            
            <p className="text-[11px] text-slate-400 normal-case font-sans leading-relaxed">
              Attach automated task flows to any of your deployed AI agents. Each flow features pre-configured trigger parameters, step-by-step diagnostic actions, compliance checks, and human signature roadblocks protecting security levels.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {TASK_FLOWS.map((flow) => (
              <div 
                key={flow.id}
                className="bg-black/90 border border-purple-500/15 rounded-xl p-5 hover:border-purple-500/35 transition-all shadow-md relative overflow-hidden flex flex-col justify-between"
              >
                {/* Visual holograph indicator */}
                <div className="absolute -bottom-10 -right-10 w-24 h-24 bg-gradient-to-tr from-cyan-500/10 to-transparent blur-xl pointer-events-none" />

                <div className="space-y-3 relative z-10">
                  <div className="flex justify-between items-start gap-4 border-b border-purple-950/40 pb-2.5">
                    <div>
                      <h4 className="text-[12px] font-black font-mono text-white tracking-wide uppercase">
                        {flow.title}
                      </h4>
                      <span className="text-[8px] font-mono text-[#e840f3] tracking-wider uppercase block mt-0.5">
                        TRIGGER: {flow.trigger.toUpperCase()}
                      </span>
                    </div>
                    <span className="text-[9px] font-mono bg-cyan-950/30 text-cyan-300 border border-cyan-500/10 px-2 py-0.5 rounded uppercase">
                      AVG SAVINGS: {flow.estimatedTimeSaved}
                    </span>
                  </div>

                  <div className="space-y-2">
                    {/* Associated Agent */}
                    <div className="flex gap-2 items-center text-[9px] font-mono text-zinc-400">
                      <span>PRIMARY AGENT DIRECTIVE:</span>
                      <strong className="text-white bg-zinc-900 border border-zinc-800 px-2 py-0.5 rounded uppercase">{flow.agentAssigned}</strong>
                    </div>

                    {/* Step lists */}
                    <div className="space-y-1 bg-black/60 p-3 rounded-lg border border-zinc-900">
                      <span className="text-[7.5px] font-mono text-zinc-500 font-bold block uppercase tracking-widest">COMPILE STEP PIPELINE:</span>
                      <div className="space-y-1 font-mono text-[8.5px] text-neutral-300 tracking-wide leading-relaxed">
                        {flow.steps.map((step, sIdx) => (
                          <div key={sIdx} className="flex gap-2.5 items-start">
                            <span className="text-cyan-400 font-bold shrink-0">0{sIdx + 1} //</span>
                            <span className="normal-case">{step}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Signature details */}
                    <div className="grid grid-cols-2 gap-2 text-[8px] font-mono p-2 bg-neutral-950/50 rounded-lg text-zinc-400">
                      <div>
                        <span className="text-zinc-500 block">SIGNATURE BARRIER:</span>
                        <strong className="text-amber-400 block truncate uppercase">{flow.approvalGate}</strong>
                      </div>
                      <div>
                        <span className="text-zinc-500 block">PIPELINE OUTPUT:</span>
                        <strong className="text-emerald-400 block truncate uppercase">{flow.output}</strong>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-purple-950/40 relative z-10 flex">
                  <button
                    onClick={() => handleOpenApprovalGate(flow, () => handleDeployTaskFlow(flow))}
                    className="px-4 py-2 bg-gradient-to-r from-cyan-950 to-indigo-950 border border-cyan-500/50 hover:border-cyan-400 text-cyan-300 hover:text-white rounded-lg font-mono text-[9px] font-black uppercase tracking-widest transition-all cursor-pointer flex items-center gap-1.5 w-full justify-center shadow-lg hover:shadow-[0_0_15px_rgba(6,182,212,0.15)]"
                  >
                    <Zap size={10} className="animate-pulse" />
                    <span>DEPLOY FLOW PIPELINE</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : activeTab === "USAGE_METER" ? (
        /* SCREEN 3 — USAGE METERING DASHBOARD & SCREEN 5 — VALUE REPORT */
        <div className="space-y-6 text-left animate-fadeIn">
          {/* Header */}
          <div className="p-6 border border-purple-500/20 rounded-2xl bg-black/85 relative overflow-hidden">
            <div className="absolute right-0 bottom-0 top-0 w-1/3 bg-gradient-to-l from-emerald-500/10 to-transparent pointer-events-none blur-xl" />
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h3 className="text-sm font-black font-mono text-emerald-400 uppercase tracking-widest flex items-center gap-1.5">
                  <Activity className="w-4 h-4 text-emerald-400 animate-pulse" />
                  SaaS Usage Metering & Value Audit Node
                </h3>
                <p className="text-[10.5px] text-zinc-400 max-w-2xl font-sans mt-1.5 normal-case">
                  Track active worker slots, task executions, human-signature bottlenecks, and total corporate equity harvested from your current <span className="text-white font-extrabold">{clientPlan.toUpperCase()}</span> tier deployment in real-time.
                </p>
              </div>
              <div className="flex gap-2">
                <span className="px-3 py-1.5 bg-emerald-950/40 text-emerald-300 border border-emerald-500/30 font-mono text-[9px] font-black tracking-widest rounded-lg flex items-center gap-1 uppercase">
                  <BadgeCheck size={11} className="text-emerald-400" />
                  STATUS: COMPLIANT
                </span>
                <span className="px-3 py-1.5 bg-purple-950/40 text-[#f574fe] border border-purple-500/30 font-mono text-[9px] font-black tracking-widest rounded-lg uppercase">
                  PLAN: {clientPlan}
                </span>
              </div>
            </div>
          </div>

          {/* Metrics Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Active Workers card */}
            <div className="p-5 bg-black/80 border border-zinc-900 rounded-2xl relative overflow-hidden flex flex-col justify-between">
              <div>
                <span className="text-[8px] font-mono font-bold tracking-wider text-zinc-500 uppercase block">Active Deployed AI Workers</span>
                <h4 className="text-2xl font-black font-mono text-white mt-1">
                  {planLimits.agentsDeployed} <span className="text-xs text-zinc-500">/ {planLimits.maxAgents} Slots</span>
                </h4>
              </div>
              <div className="mt-4 space-y-1">
                <div className="w-full bg-zinc-950 h-2 rounded-full overflow-hidden border border-zinc-900">
                  <div className="bg-gradient-to-r from-emerald-500 to-cyan-400 h-full rounded-full" style={{ width: `${Math.min(100, (planLimits.agentsDeployed / planLimits.maxAgents) * 100)}%` }} />
                </div>
                <div className="flex justify-between text-[8px] font-mono text-zinc-500">
                  <span>{((planLimits.agentsDeployed / planLimits.maxAgents) * 100).toFixed(0)}% Utilized</span>
                  <span>{planLimits.maxAgents - planLimits.agentsDeployed} Slots Available</span>
                </div>
              </div>
            </div>

            {/* Monthly Tasks card */}
            <div className="p-5 bg-black/80 border border-zinc-900 rounded-2xl relative overflow-hidden flex flex-col justify-between">
              <div>
                <span className="text-[8px] font-mono font-bold tracking-wider text-zinc-500 uppercase block">Monthly Task Executions</span>
                <h4 className="text-2xl font-black font-mono text-cyan-400 mt-1">
                  {planLimits.tasksRun.toLocaleString()} <span className="text-xs text-zinc-500">/ {planLimits.maxTasks.toLocaleString()} Runs</span>
                </h4>
              </div>
              <div className="mt-4 space-y-1">
                <div className="w-full bg-zinc-950 h-2 rounded-full overflow-hidden border border-zinc-900">
                  <div className="bg-gradient-to-r from-cyan-500 to-indigo-500 h-full rounded-full" style={{ width: `${Math.min(100, (planLimits.tasksRun / planLimits.maxTasks) * 100)}%` }} />
                </div>
                <div className="flex justify-between text-[8px] font-mono text-zinc-500">
                  <span>{((planLimits.tasksRun / planLimits.maxTasks) * 100).toFixed(1)}% Used</span>
                  <span>{(planLimits.maxTasks - planLimits.tasksRun).toLocaleString()} Remaining</span>
                </div>
              </div>
            </div>

            {/* Hours Reclaimed card */}
            <div className="p-5 bg-black/80 border border-zinc-900 rounded-2xl relative overflow-hidden flex flex-col justify-between">
              <div>
                <span className="text-[8px] font-mono font-bold tracking-wider text-zinc-500 uppercase block">Corporate Hours Reclaimed</span>
                <h4 className="text-2xl font-black font-mono text-indigo-400 mt-1">
                  {parseFloat(planLimits.hoursSaved).toFixed(1)} <span className="text-xs text-zinc-500">Hours</span>
                </h4>
              </div>
              <div className="mt-4 text-[9px] font-mono text-zinc-400">
                <span className="text-emerald-400 font-bold">▲ {(planLimits.tasksRun > 0 ? (parseFloat(planLimits.hoursSaved) / planLimits.tasksRun).toFixed(2) : "0.75")} hrs</span> average time savings per background automated thread execution.
              </div>
            </div>

            {/* Total Value Saved card */}
            <div className="p-5 bg-black/80 border-slate-900 rounded-2xl relative overflow-hidden flex flex-col justify-between shadow-[0_0_20px_rgba(16,185,129,0.05)]">
              <div>
                <span className="text-[8px] font-mono font-bold tracking-wider text-zinc-500 uppercase block">NexoraX Financial Value Saved</span>
                <h4 className="text-2xl font-black font-mono text-emerald-400 mt-1">
                  £{parseFloat(planLimits.valueSaved).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </h4>
              </div>
              <div className="mt-4 text-[9px] font-mono text-zinc-400">
                Calculated at <span className="text-[#f574fe] font-black">£9.50 / workflow hour</span> replacement costs compared to onshore human operations.
              </div>
            </div>
          </div>

          {/* SCREEN 5 — VALUE ANALYSIS REPORT SECTION */}
          <div className="p-6 border border-[#e840f3]/20 rounded-2xl bg-black/90 relative overflow-hidden space-y-4">
            <div className="absolute right-3 top-3 opacity-10">
              <Sparkles size={64} className="text-[#e840f3]" />
            </div>
            
            <div className="border-b border-purple-950/40 pb-3 flex justify-between items-center">
              <div>
                <h4 className="text-xs font-mono font-black text-white tracking-widest uppercase">
                  ♛ Executive AI Value Audit & ROI Ledger Summary
                </h4>
                <p className="text-[9.5px] font-sans text-zinc-500 normal-case mt-0.5">
                  High-fidelity computation mirroring active efficiency yields, security audits, and optimized talent matching.
                </p>
              </div>

              <div className="flex gap-2">
                <button 
                  onClick={() => {
                    playNexoraXSound(650, 0.2, "sine");
                    triggerToast("Generating corporate value audit report PDF...", "info");
                    setTimeout(() => {
                      playNexoraXSound(880, 0.35, "sine");
                      // Dynamically build and download text file audit report
                      const reportContent = `
========================================
NEXORAX DIGITAL LTD (SC887095)
NEXORAX COGNITIVE VALUE AUDIT REPORT
Generated At: ${new Date().toLocaleDateString()}
Current Plan: ${clientPlan.toUpperCase()}
========================================

1. EFFICIENCY TELEMETRY METRICS
----------------------------------------
- Active AI Agent Slots: ${planLimits.agentsDeployed} / ${planLimits.maxAgents}
- Monthly Task Executions: ${planLimits.tasksRun} / ${planLimits.maxTasks}
- Total Work Hours Reclaimed: ${parseFloat(planLimits.hoursSaved).toFixed(2)} Hours
- Estimated Operational Cost Saved: £${parseFloat(planLimits.valueSaved).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
- Average ROI Multiplier: ${((parseFloat(planLimits.valueSaved) / 299) * 100).toFixed(0)}%

2. SPECIFIC ACTIVE AGENT CONTRIBUTION LOGS
----------------------------------------
- Client Care Agent (Tier 1 Support): 48.5 hours saved, preventing standard queue spikes.
- Finance & Gavel Ledger Broker (Node-07): £1,420 legal fees prevented via autonomous billing reconciliation.
- Principal Private Secretary (Sponsorship-Node): 22.4 administrative hours saved, clearing sponsorship files for Communications Associate/Staff Coordinator.

3. STRATEGIC UPGRADE RECOMMENDATIONS
----------------------------------------
Current Limit Gaps: ${planLimits.maxAgents - planLimits.agentsDeployed <= 1 ? "WARNING: Max worker slot count nearly saturated." : "COMPLIANT"}
We recommend immediate transition to the AGENCY tier to unlock 15 more continuous background worker threads and lower API token overhead expenses by 35%.

Approved for Release by CloudSync-02 core cluster security validator.
========================================`;
                      
                      const element = document.createElement("a");
                      const file = new Blob([reportContent], {type: 'text/plain'});
                      element.href = URL.createObjectURL(file);
                      element.download = `NexoraX_Value_Audit_${clientPlan}.txt`;
                      document.body.appendChild(element);
                      element.click();
                      document.body.removeChild(element);

                      triggerToast("♛ PDF Value Ledger downloaded successfully to localized vault!", "success");
                    }, 1400);
                  }}
                  className="px-3 py-1.5 bg-purple-950/40 hover:bg-purple-900/60 border border-purple-500/40 text-[#f574fe] hover:text-white rounded-lg font-mono text-[9px] font-bold tracking-tight inline-flex items-center gap-1.5 cursor-pointer"
                >
                  <Download size={11} />
                  <span>DOWNLOAD PDF VALUE AUDIT</span>
                </button>

                <button 
                  onClick={() => {
                    playNexoraXSound(700, 0.35, "triangle");
                    logUpgradeInterest("Enterprise Custom Negotiation", "Usage_Dashboard", clientPlan, "Enterprise");
                    triggerToast("♛ Upgrade Negotiation initiated. Office of the Director notified via Google Vault.", "success");
                  }}
                  className="px-3 py-1.5 bg-gradient-to-r from-emerald-950 to-emerald-900 hover:from-emerald-900 hover:to-emerald-850 border border-emerald-500/50 text-emerald-300 hover:text-white rounded-lg font-mono text-[9px] font-bold tracking-tight inline-flex items-center gap-1.5 cursor-pointer"
                >
                  <Zap size={11} className="text-cyan-400 animate-pulse" />
                  <span>NEGOTIATE PRO UPGRADE</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-[10px] normal-case leading-relaxed text-zinc-400">
              <div className="space-y-2 p-3 bg-[#0a0614]/60 border border-purple-500/5 rounded-xl">
                <span className="text-[8px] font-mono font-bold tracking-widest text-[#f574fe] uppercase block border-b border-purple-950/40 pb-1">01 / WORKTIME HARVEST RECAP</span>
                <p>
                  Deploying active worker templates has averted administrative queues. **Client Care Agent (Tier 1)** managed a 48.5 hours reduction in triage times, absorbing customer support issues autonomously.
                </p>
              </div>
              <div className="space-y-2 p-3 bg-[#0a0614]/60 border border-purple-500/5 rounded-xl">
                <span className="text-[8px] font-mono font-bold tracking-widest text-cyan-400 uppercase block border-b border-purple-950/40 pb-1">02 / LEGAL & REGULATORY EXPENSES</span>
                <p>
                  By deploying a robust **Gavel Ledger Broker** on Node-07, the client bypassed standard manual bank transaction reconciliation costs, preventing an estimated £1,420 in legal bookkeeping and tracking overheads.
                </p>
              </div>
              <div className="space-y-2 p-3 bg-[#0a0614]/60 border border-purple-500/5 rounded-xl">
                <span className="text-[8px] font-mono font-bold tracking-widest text-emerald-400 uppercase block border-b border-purple-950/40 pb-1">03 / SECRETARY WORKFORCE RECAP</span>
                <p>
                  The **Secure Invoice Register Recruiting Scheduler Engine** facilitated direct applicant communications, reducing executive workload by 22.4 hours and securing direct placements without standard high agency fees.
                </p>
              </div>
            </div>

            <div className="p-3 bg-rose-955/20 border border-rose-500/25 rounded-xl flex items-start gap-2.5">
              <AlertTriangle className="text-rose-400 w-4 h-4 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <h5 className="text-[10px] font-mono font-bold uppercase text-white tracking-widest">
                  RECOMMENDATION ACTION DIRECTIVE (CRITICAL SHIFT)
                </h5>
                <p className="text-[9.5px] text-rose-300 normal-case leading-normal font-sans">
                  The usage meter projects slot exhaustion within 72 hours if additional autonomous pipelines are attached. We strongly advise upgrading to the <strong>AGENCY</strong> tier to expand slots to 35, unlock premium GPT-4/Claude Sonnet endpoints, and lower processing cost variables by 35%.
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : activeTab === "PRICING" ? (
        /* SCREEN 2 — PLAN COMPARISON & SCREEN 7 — ADD-ON MARKETPLACE */
        <div className="space-y-6 text-left animate-fadeIn">
          {/* Header */}
          <div className="p-5 border border-purple-500/10 rounded-2xl bg-black/80 space-y-1">
            <h3 className="text-sm font-black font-mono text-cyan-400 uppercase tracking-wider flex items-center gap-1.5">
              <Building className="w-4 h-4 text-[#f574fe]" />
              NexoraX Subscription Tiers & Add-On Marketplace
            </h3>
            <p className="text-[10.5px] text-slate-400 normal-case font-sans">
              Choose the exact cognitive capacity matching your enterprise volume. Purchase customized modules and additional background worker threads directly.
            </p>
          </div>

          {/* Pricing Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-2">
            {[
              {
                id: "Starter",
                price: "£299",
                period: "month",
                maxAgents: 2,
                maxTasks: 1000,
                desc: "Entry-level automated assistance nodes.",
                features: [
                  "2 Active Deployed AI Agents",
                  "1,000 Tasks Execution / mo",
                  "Standard Core AI Endpoints",
                  "Secure Local Persistence",
                  "Standard Email Ticket Response Support"
                ]
              },
              {
                id: "Growth",
                price: "£799",
                period: "month",
                maxAgents: 10,
                maxTasks: 5000,
                desc: "Highly-optimized pipelines for busy digital businesses.",
                features: [
                  "10 Active Deployed AI Agents",
                  "5,000 Tasks Execution / mo",
                  "High-Performance Cognitive LLMs",
                  "Continuous Google Drive Vault Archival",
                  "Priority Slack Sync Channel Support",
                  "NexoraX 1% Levy Split Access"
                ]
              },
              {
                id: "Agency",
                price: "£1,999",
                period: "month",
                maxAgents: 35,
                maxTasks: 25000,
                desc: "Deep customizability and multi-tenant task distribution.",
                features: [
                  "35 Active Deployed AI Agents",
                  "25,000 Tasks Execution / mo",
                  "Ultra LLM Speed Priority Channels",
                  "Full Custom Workflow Builder API",
                  "Sponsorship-Node Gavel Strike Clearance",
                  "Dedicated Systems Architect (2hr SLA)"
                ]
              },
              {
                id: "Enterprise",
                price: "£5,000",
                period: "month",
                maxAgents: 120,
                maxTasks: 150000,
                desc: "Maximum corporate NexoraXty and military security lines.",
                features: [
                  "120 Active Deployed AI Agents",
                  "150,000 Tasks Execution / mo",
                  "Private Dedicated Sandbox Servers",
                  "Deep Regional Node Redundancy",
                  "100% Custom Integration Support",
                  "Director VIP NexoraX Ledger Sync"
                ]
              }
            ].map((plan) => {
              const isCurrent = clientPlan === plan.id;
              return (
                <div 
                  key={plan.id}
                  className={`bg-black/95 border-2 rounded-2xl p-5 text-left relative overflow-hidden transition-all duration-300 flex flex-col justify-between hover:translate-y-[-2px] ${
                    isCurrent 
                      ? "border-[#e840f3] shadow-[0_0_20px_rgba(232,64,243,0.15)] bg-[#0d011c]/90" 
                      : "border-zinc-850 hover:border-purple-500/40"
                  }`}
                >
                  {isCurrent && (
                    <div className="absolute right-0 top-0 bg-[#e840f3] text-black font-mono font-black text-[7px] tracking-wider uppercase px-2.5 py-0.5 rounded-bl">
                      ★ ACTIVE TIER
                    </div>
                  )}

                  <div className="space-y-3">
                    <div>
                      <h4 className="text-xs font-mono font-extrabold text-white tracking-widest uppercase">{plan.id}</h4>
                      <p className="text-[9.5px] text-zinc-500 min-h-[24px] normal-case mt-1">{plan.desc}</p>
                    </div>

                    <div className="py-2 border-y border-zinc-900">
                      <span className="text-2xl font-mono font-black text-white">{plan.price}</span>
                      <span className="text-zinc-500 text-[9px] font-mono"> / {plan.period}</span>
                    </div>

                    <div className="space-y-1.5">
                      <span className="text-[7px] font-mono tracking-widest text-[#f574fe] font-bold uppercase block">COMPRESSED BUNDLE OVERHEADS:</span>
                      <div className="text-[9.5px] font-mono text-cyan-300 space-y-1 bg-black/60 p-2 rounded border border-zinc-900">
                        <div className="flex justify-between">
                          <span>Max Agents:</span>
                          <span className="text-white font-extrabold">{plan.maxAgents} Slots</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Max Task Runs:</span>
                          <span className="text-white font-extrabold">{plan.maxTasks.toLocaleString()} Runs</span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-1.5 pt-1">
                      <span className="text-[7px] font-mono tracking-widest text-zinc-500 font-bold uppercase block">INCLUDED BENEFITS:</span>
                      <div className="space-y-1.5 text-[8.5px] font-sans text-zinc-400 normal-case">
                        {plan.features.map((feat, idx) => (
                          <div key={idx} className="flex gap-1.5 items-start">
                            <Check size={10} className="text-emerald-400 shrink-0 mt-0.5" />
                            <span>{feat}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 pt-3.5 border-t border-purple-950/40">
                    {isCurrent ? (
                      <div className="w-full text-center py-2 bg-emerald-950/30 border border-emerald-500/40 text-emerald-300 font-mono text-[9px] font-black uppercase tracking-widest rounded-lg flex items-center justify-center gap-1 shadow-md">
                        <CheckCircle size={10} className="text-emerald-400" />
                        <span>ACTIVE ENFORCED SUBSCRIBER</span>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          playNexoraXSound(800, 0.25, "sine");
                          setClientPlan(plan.id as any);
                          // Scale limits automatically based on choice
                          setPlanLimits(prev => ({
                            ...prev,
                            maxAgents: plan.maxAgents,
                            maxTasks: plan.maxTasks
                          }));
                          triggerToast(`♛ NexoraX subscription converted to ${plan.id.toUpperCase()}! Limits re-scaled.`, "success");
                        }}
                        className="w-full text-center py-2 bg-[#04020c] hover:bg-purple-950/40 border border-purple-500/40 hover:border-purple-400 text-[#f574fe] hover:text-white font-mono text-[9px] font-black uppercase tracking-widest rounded-lg cursor-pointer transition-all flex items-center justify-center gap-1 shadow"
                      >
                        <span>ACTIVATE {plan.id.toUpperCase()}</span>
                        <ArrowUpRight size={10} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* SCREEN 7 — AUXILIARY ADD-ON MARKETPLACE */}
          <div className="space-y-4 pt-4 border-t border-purple-950/40 text-left">
            <div>
              <h4 className="text-xs font-mono font-black text-white uppercase tracking-widest flex items-center gap-1.5">
                <Plus className="w-3.5 h-3.5 text-cyan-400 animate-pulse" />
                Auxiliary Enterprise Add-On Modules
              </h4>
              <p className="text-[9.5px] font-sans text-zinc-500 normal-case mt-0.5">
                Scale max background threads or compute priorities without fully migrating subscription plans. Fits into existing ledger balances.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {addOnList.map((addon) => (
                <div 
                  key={addon.id} 
                  className={`p-4 bg-zinc-950/90 rounded-xl border transition-all flex flex-col justify-between ${
                    addon.purchased 
                      ? "border-emerald-500/50 shadow-md bg-[#01140d]/40" 
                      : "border-zinc-850 hover:border-zinc-700"
                  }`}
                >
                  <div className="space-y-1 px-1">
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] font-mono font-extrabold text-white uppercase tracking-wide truncate">{addon.title}</span>
                      {addon.purchased && (
                        <span className="px-1.5 py-0.5 bg-emerald-950 text-emerald-400 border border-emerald-500/20 text-[7px] font-mono font-black rounded uppercase">
                          ACTIVE
                        </span>
                      )}
                    </div>
                    <span className="text-[#f574fe] font-mono text-[9px] font-extrabold block">+{addon.price} / mo</span>
                    <p className="text-[9px] text-zinc-500 normal-case leading-relaxed font-sans">{addon.desc}</p>
                  </div>

                  <div className="mt-4 pt-2.5 border-t border-zinc-900">
                    <button 
                      onClick={() => {
                        playNexoraXSound(addon.purchased ? 220 : 680, 0.15);
                        const listCopy = addOnList.map(a => {
                          if (a.id === addon.id) {
                            return { ...a, purchased: !a.purchased };
                          }
                          return a;
                        });
                        setAddOnList(listCopy);
                        localStorage.setItem("nx_saas_addons", JSON.stringify(listCopy));

                        // Scale continuous variables if purchased
                        if (!addon.purchased) {
                          setPlanLimits(prev => {
                            const addAgents = addon.id === "extra-slots" ? 5 : 0;
                            const addTasks = addon.id === "acceleration" ? 5000 : 0;
                            return {
                              ...prev,
                              maxAgents: prev.maxAgents + addAgents,
                              maxTasks: prev.maxTasks + addTasks
                            };
                          });
                          triggerToast(`➕ Module "${addon.title}" successfully allocated! Capacity updated.`, "success");
                        } else {
                          setPlanLimits(prev => {
                            const remAgents = addon.id === "extra-slots" ? 5 : 0;
                            const remTasks = addon.id === "acceleration" ? 5000 : 0;
                            return {
                              ...prev,
                              maxAgents: Math.max(2, prev.maxAgents - remAgents),
                              maxTasks: Math.max(1000, prev.maxTasks - remTasks)
                            };
                          });
                          triggerToast(`➖ Module "${addon.title}" deallocated. capacity reduced.`, "warning");
                        }
                      }}
                      className={`w-full py-1.5 font-mono text-[8.5px] font-black uppercase rounded-lg text-center cursor-pointer transition-colors ${
                        addon.purchased 
                          ? "bg-emerald-950/60 text-emerald-400 hover:bg-emerald-900 hover:text-white border border-emerald-500/30" 
                          : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800 hover:text-white border border-zinc-800"
                      }`}
                    >
                      {addon.purchased ? "DEALLOCATE ADDON" : "ALLOCATE MODULE"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : activeTab === "ADMIN_PLAN_MGR" ? (
        /* ADMIN-ONLY PLAN MANAGER (ADMINISTRATIVE GATING & LIMIT REGULARIZER) */
        <div className="space-y-6 text-left animate-fadeIn">
          {/* Header */}
          <div className="p-5 border border-rose-500/30 rounded-2xl bg-black/85 relative overflow-hidden">
            <div className="absolute right-0 bottom-0 top-0 w-1/4 bg-rose-500/5 pointer-events-none blur-xl" />
            <h3 className="text-sm font-black font-mono text-rose-400 uppercase tracking-widest flex items-center gap-1.5">
              <Lock className="w-3.5 h-3.5 text-rose-500" />
              NexoraX Administrative Gate & Limits Manager [SUPER ADMIN]
            </h3>
            <p className="text-[10.5px] text-[#ff8093] max-w-3xl font-sans mt-1.5 normal-case">
              Manage client capacity limits, override required plan triggers for individual templates, and monitor raw crm acquisition leads in real-time. Changes write securely to localized system schemas.
            </p>
          </div>

          {/* Core override control rails */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Limit Override */}
            <div className="p-5 bg-black/90 border border-rose-950/60 rounded-2xl space-y-4">
              <div className="border-b border-rose-950/40 pb-2.5">
                <span className="text-[8px] font-mono font-bold tracking-widest text-zinc-500 uppercase block">CAPACITY LIMIT MODIFIER RAIL</span>
                <span className="text-xs font-mono text-white font-extrabold uppercase">Modify Target Client Limits</span>
              </div>

              <div className="space-y-5">
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-[9.5px] font-mono">
                    <span className="text-zinc-400">Max Active Agents Deployed Limit:</span>
                    <span className="text-white bg-rose-950/20 px-2 py-0.5 border border-rose-500/20 rounded font-black">{planLimits.maxAgents} Slots</span>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => {
                        playNexoraXSound(300, 0.1);
                        setPlanLimits(p => ({ ...p, maxAgents: Math.max(1, p.maxAgents - 1) }));
                      }}
                      className="px-3 py-1 bg-zinc-900 hover:bg-zinc-800 text-white rounded font-mono text-xs font-black cursor-pointer border border-zinc-800"
                    >
                      -
                    </button>
                    <input 
                      type="range" 
                      min="1" 
                      max="150" 
                      value={planLimits.maxAgents} 
                      onChange={(e) => {
                        playNexoraXSound(400, 0.05);
                        setPlanLimits(p => ({ ...p, maxAgents: parseInt(e.target.value) || 1 }));
                      }}
                      className="w-full accent-rose-500 bg-neutral-950 cursor-pointer"
                    />
                    <button 
                      onClick={() => {
                        playNexoraXSound(500, 0.1);
                        setPlanLimits(p => ({ ...p, maxAgents: p.maxAgents + 1 }));
                      }}
                      className="px-3 py-1 bg-zinc-900 hover:bg-zinc-800 text-white rounded font-mono text-xs font-black cursor-pointer border border-zinc-800"
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center text-[9.5px] font-mono">
                    <span className="text-zinc-400">Max Monthly Task Executions Limit:</span>
                    <span className="text-white bg-rose-950/20 px-2 py-0.5 border border-rose-500/20 rounded font-black">{planLimits.maxTasks.toLocaleString()} Runs</span>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => {
                        playNexoraXSound(300, 0.1);
                        setPlanLimits(p => ({ ...p, maxTasks: Math.max(500, p.maxTasks - 1000) }));
                      }}
                      className="px-3 py-1 bg-zinc-900 hover:bg-zinc-800 text-white rounded font-mono text-xs font-black cursor-pointer border border-zinc-800"
                    >
                      -
                    </button>
                    <input 
                      type="range" 
                      min="500" 
                      max="200000" 
                      step="500"
                      value={planLimits.maxTasks} 
                      onChange={(e) => {
                        playNexoraXSound(400, 0.05);
                        setPlanLimits(p => ({ ...p, maxTasks: parseInt(e.target.value) || 500 }));
                      }}
                      className="w-full accent-rose-500 bg-neutral-950 cursor-pointer"
                    />
                    <button 
                      onClick={() => {
                        playNexoraXSound(500, 0.1);
                        setPlanLimits(p => ({ ...p, maxTasks: p.maxTasks + 1000 }));
                      }}
                      className="px-3 py-1 bg-zinc-900 hover:bg-zinc-800 text-white rounded font-mono text-xs font-black cursor-pointer border border-zinc-800"
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="p-3 bg-rose-950/15 border border-rose-500/20 rounded-xl font-mono text-[8.5px] text-zinc-400 leading-normal">
                  Values altered directly write back to active client session indicators in the localized client context. State changes are persistent.
                </div>
              </div>
            </div>

            {/* Upgrade leads monitor */}
            <div className="p-5 bg-black/90 border border-zinc-900 rounded-2xl space-y-4">
              <div className="border-b border-zinc-800 pb-2.5 flex justify-between items-center">
                <div>
                  <span className="text-[8px] font-mono font-bold tracking-widest text-[#f574fe] uppercase block">CRM TARGET CAPABILITY LEADS</span>
                  <span className="text-xs font-mono text-white font-extrabold uppercase">Client Upgrade Requests Ledger</span>
                </div>
                <button 
                  onClick={() => {
                    playNexoraXSound(440, 0.2);
                    setUpgradeDemand([]);
                    localStorage.removeItem("nx_saas_upgrade_demand");
                    triggerToast("CRM Leads ledger purged.", "info");
                  }}
                  className="px-2 py-1 bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white rounded font-mono text-[7px]"
                >
                  PURGE LIST
                </button>
              </div>

              <div className="space-y-2 max-h-[160px] overflow-y-auto pr-1">
                {upgradeDemand.length > 0 ? (
                  upgradeDemand.map((lead, idx) => (
                    <div key={idx} className="p-2.5 bg-[#070110] border border-rose-500/25 rounded-lg flex justify-between items-start font-mono text-[8.5px]">
                      <div className="space-y-0.5 text-left text-zinc-300">
                        <div className="flex items-center gap-1">
                          <span className="text-white font-black truncate">{lead.clientName}</span>
                          <span className="px-1 py-0.2 bg-red-950 text-red-400 border border-red-500/20 rounded text-[6.5px]">HOT UPGRADE LEAD</span>
                        </div>
                        <p className="text-[8px] text-zinc-500 uppercase">
                          Template ID: {lead.templateId} | Requested At: {new Date(lead.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <span className="text-rose-400 font-extrabold uppercase block">{lead.fromPlan} ➔ {lead.toPlan}</span>
                        <span className="text-[7px] text-zinc-500">Node CRM-04</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="py-8 text-center border border-dashed border-zinc-800 rounded-lg text-zinc-650 font-mono text-[9px] uppercase">
                    NO ACTIVE UPGRADE LEADS REGISTERED.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* SCREEN 6 (Continued Override Matrix Panel) */}
          <div className="p-5 bg-black/90 border border-rose-950/65 rounded-2xl text-left space-y-4">
            <div className="border-b border-rose-955 pb-2.5">
              <span className="text-[8px] font-mono font-bold tracking-widest text-[#ff8093] uppercase block">SCREEN 6 DIRECTIVE — SYSTEM PLAN GATING MANAGER</span>
              <span className="text-xs font-mono text-white font-extrabold uppercase">Template Lock Gating Configuration Board</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((tpl) => {
                const currentGatingVal = templatePlanGating[tpl.id] || "Starter";
                return (
                  <div key={tpl.id} className="p-3 bg-zinc-950/80 rounded-xl border border-zinc-900 flex justify-between items-center font-mono text-[10px]">
                    <div className="truncate max-w-[60%]">
                      <strong className="text-white block uppercase truncate">{tpl.name}</strong>
                      <span className="text-[7.5px] text-zinc-500 block">Required: <strong className="text-rose-400">{currentGatingVal.toUpperCase()}</strong></span>
                    </div>
                    
                    <div className="relative shrink-0">
                      <select 
                        value={currentGatingVal}
                        onChange={(e) => {
                          playNexoraXSound(520, 0.1, "sine");
                          const newVal = e.target.value as any;
                          const copy = { ...templatePlanGating, [tpl.id]: newVal };
                          setTemplatePlanGating(copy);
                          localStorage.setItem("nx_saas_template_gating", JSON.stringify(copy));
                          triggerToast(`♛ ${tpl.name} is now gated at the ${newVal.toUpperCase()} tier subscription level!`, "success");
                        }}
                        className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-zinc-300 font-mono text-[9px] outline-none cursor-pointer focus:border-rose-500"
                      >
                        <option value="Starter">Starter (Free)</option>
                        <option value="Growth">Growth</option>
                        <option value="Agency">Agency</option>
                        <option value="Enterprise">Enterprise</option>
                      </select>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

  // Helper function to render the Premium Agent Setup Wizard
      {/* DETAIL PAGE MODAL / SIDEBAR */}
      <AnimatePresence>
        {detailTemplate && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setDetailTemplate(null)}
              className="absolute inset-0 bg-black/85 backdrop-blur-md cursor-pointer"
            />
            
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 15 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 15 }}
              className="relative max-w-2xl w-full bg-[#04020c] border-2 border-purple-500/40 rounded-3xl overflow-hidden shadow-[0_0_50px_rgba(232,64,243,0.15)] text-left"
            >
              {/* Highlight accent line */}
              <div className="h-1.5 w-full bg-gradient-to-r from-[#e840f3] to-cyan-500" />
              
              <div className="p-6 space-y-6 relative z-10 max-h-[85vh] overflow-y-auto custom-scrollbar">
                
                {/* Detail title header */}
                <div className="flex justify-between items-start border-b border-purple-950/60 pb-4">
                  <div>
                    <span className="text-[8.5px] font-mono text-cyan-400 tracking-wider font-extrabold uppercase bg-cyan-950/30 px-2 py-0.5 rounded border border-cyan-500/10">
                      {detailTemplate.category.toUpperCase()}
                    </span>
                    <h3 className="text-lg md:text-xl font-mono font-black text-white tracking-widest uppercase mt-2">
                      {detailTemplate.name}
                    </h3>
                    <p className="text-[10px] font-mono text-[#e840f3] tracking-wide uppercase mt-0.5">
                      {detailTemplate.role}
                    </p>
                  </div>

                  <button 
                    onClick={() => setDetailTemplate(null)}
                    className="p-1 hover:bg-slate-900 border border-transparent hover:border-slate-800 rounded transition-colors text-slate-400 hover:text-white cursor-pointer"
                  >
                    <X size={16} />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Detailed Description */}
                  <div className="space-y-1.5">
                    <span className="text-[8px] font-mono text-slate-500 font-bold uppercase tracking-widest">AGENT OVERVIEW DESCRIPTION:</span>
                    <p className="text-xs text-slate-300 leading-relaxed font-sans normal-case">
                      {detailTemplate.description}
                    </p>
                  </div>

                  {/* What it does */}
                  <div className="bg-[#120324]/50 border border-purple-500/10 p-4 rounded-2xl text-[10.5px] font-sans leading-relaxed text-zinc-300 normal-case space-y-1">
                    <strong className="text-[#e840f3] uppercase font-mono text-[9px] block">WHAT THIS AGENT DOES:</strong>
                    This specialized intelligence drone manages workflow streams independently by mapping incoming queries directly to the defined priority matrices. Capable of analyzing background context to draft responses safely without human interference.
                  </div>

                  {/* Tasks list */}
                  <div className="space-y-2">
                    <span className="text-[8px] font-mono text-slate-500 font-bold uppercase tracking-widest">TASKS INCLUDED IN PIPELINE ({detailTemplate.tasks.length}):</span>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-mono text-slate-200">
                      {detailTemplate.tasks.map((task, index) => (
                        <div key={index} className="flex gap-2.5 items-center p-2 bg-black/60 rounded-xl border border-zinc-900 leading-normal">
                          <Check className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                          <span className="truncate">{task}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Tools connected & knowledge bases */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-black/60 p-3.5 border border-purple-950/40 rounded-2xl space-y-2">
                      <span className="text-[8px] font-mono text-slate-500 font-bold block uppercase tracking-widest">CONNECTED UTILITIES:</span>
                      <div className="flex flex-wrap gap-1 text-[9px] font-mono">
                        {detailTemplate.toolsConnected.map((tool, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-zinc-900 border border-zinc-800 text-zinc-300 rounded">
                            {tool}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="bg-black/60 p-3.5 border border-purple-950/40 rounded-2xl space-y-2">
                      <span className="text-[8px] font-mono text-slate-500 font-bold block uppercase tracking-widest">KNOWLEDGE BASES INDEX:</span>
                      <div className="flex flex-wrap gap-1 text-[9px] font-mono">
                        {detailTemplate.knowledgeSources.map((source, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-zinc-900 border border-zinc-800 text-zinc-300 rounded leading-relaxed text-left max-w-full truncate" title={source}>
                            {source}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Approval rules & outputs demo */}
                  <div className="space-y-3 p-4 bg-slate-950 border border-purple-950/40 rounded-2xl font-mono text-[10px]">
                    <div className="flex justify-between items-center pb-2 border-b border-indigo-950/50">
                      <span>APPROVAL SECURITY STANDARDS:</span>
                      <span className={detailTemplate.humanApprovalRequired ? "text-amber-400" : "text-emerald-400"}>
                        {detailTemplate.humanApprovalRequired ? "REQUIRES HUMAN CONFIRMATION" : "AUTONOMOUS EXECUTION PERMITTED"}
                      </span>
                    </div>

                    <div className="space-y-1.5 leading-relaxed text-left">
                      <span className="text-zinc-500 block text-[8px] uppercase font-bold">EXAMPLE OUTPUT PREVIEW DISPATCH:</span>
                      <pre className="text-[9.5px] text-cyan-300 leading-normal bg-black p-2.5 rounded border border-cyan-500/10 whitespace-pre-wrap select-all font-mono">
                        {detailTemplate.exampleOutput}
                      </pre>
                    </div>

                    <div className="pt-2 border-t border-indigo-950/50 flex justify-between text-zinc-500 text-[8px]">
                      <span>RISK CLASSIFICATION: <strong className={detailTemplate.riskLevel === "HIGH" ? "text-rose-400" : "text-emerald-400"}>{detailTemplate.riskLevel}</strong></span>
                      <span>RECOMMENDED LICENSE STYLE: <strong className="text-white">{detailTemplate.recommendedPlan}</strong></span>
                    </div>
                  </div>

                  {/* Modal bottom actions */}
                  <div className="flex gap-3 justify-end pt-2">
                    <button
                      onClick={() => setDetailTemplate(null)}
                      className="px-4 py-2 border border-zinc-800 hover:border-slate-600 rounded-xl font-mono text-[9px] font-bold uppercase tracking-wider cursor-pointer text-slate-400 hover:text-white"
                    >
                      ABORT PREVIEW
                    </button>

                    <button
                      onClick={() => {
                        setDetailTemplate(null);
                        handleTriggerCustomiseSelection(detailTemplate);
                      }}
                      className="px-4 py-2 border border-purple-500/40 hover:border-purple-400 text-[#f574fe] rounded-xl font-mono text-[9px] font-bold uppercase tracking-wider cursor-pointer"
                    >
                      CUSTOMISE AGENT
                    </button>

                    <button
                      onClick={() => {
                        const current = detailTemplate;
                        setDetailTemplate(null);
                        handleOpenApprovalGate(current, () => handleTriggerDeploy(current.id));
                      }}
                      className="px-5 py-2 bg-gradient-to-r from-violet-950 to-purple-900 border border-purple-500 text-purple-200 hover:text-white rounded-xl font-mono text-[9px] font-black uppercase tracking-widest cursor-pointer shadow-lg"
                    >
                      DEPLOY AGENT NOW
                    </button>
                  </div>

                </div>

              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* SCREEN 4 — LOCKED AGENT UPGRADE FLOW MODAL */}
      <AnimatePresence>
        {lockedAgentModal && (
          <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setLockedAgentModal(null)}
              className="absolute inset-0 bg-black/90 backdrop-blur-md cursor-pointer"
            />
            
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 30 }}
              className="relative max-w-md w-full bg-[#080210] border-2 border-rose-500/50 rounded-3xl overflow-hidden shadow-[0_0_50px_rgba(239,68,68,0.2)] text-left"
            >
              {/* Highlight red-purple accent line */}
              <div className="h-1.5 w-full bg-gradient-to-r from-rose-500 via-indigo-500 to-purple-500" />
              
              <div className="p-6 space-y-6 relative z-10 text-center">
                
                {/* Huge radiating lock indicator */}
                <div className="mx-auto w-16 h-16 rounded-full bg-rose-550/10 border border-rose-500/30 flex items-center justify-center shadow-[0_0_25px_rgba(239,68,68,0.1)] mb-2">
                  <Lock className="text-rose-500 w-8 h-8 animate-pulse" />
                </div>

                <div className="space-y-2">
                  <span className="text-[8.5px] font-mono text-rose-400 tracking-widest font-black uppercase bg-rose-950/40 px-2.5 py-1 rounded border border-rose-500/25">
                    COGNITIVE CAPABILITY LOCKED
                  </span>
                  <h3 className="text-base font-mono font-black text-white tracking-widest uppercase mt-4">
                    {lockedAgentModal.name}
                  </h3>
                  <p className="text-[9.5px] font-mono text-zinc-500 tracking-wide uppercase">
                    REQUIRED TIER: <span className="text-rose-400 font-extrabold">{lockedAgentModal.recommendedPlan.toUpperCase()}</span>
                  </p>
                </div>

                {/* Explanatory text */}
                <div className="p-4 bg-zinc-950/90 rounded-2xl border border-zinc-900 text-xs text-left normal-case leading-relaxed font-sans text-zinc-400 space-y-2.5">
                  <p>
                    Your current sub-active workspace is on the <strong className="text-white bg-zinc-900 border border-zinc-850 px-1.5 py-0.5 rounded text-[10px]">{clientPlan}</strong> tier, which does not support execution privileges for this ready-made template.
                  </p>
                  <p>
                    Upgrading to <strong className="text-[#f574fe]">{lockedAgentModal.recommendedPlan}</strong> unlocks direct background integration thread allocations, continuous performance telemetry dashboards on Node-17, and unlimited cognitive access.
                  </p>
                </div>

                {/* Upgrade controls interactive list */}
                <div className="space-y-2 pt-2">
                  <button 
                    onClick={() => {
                      playNexoraXSound(880, 0.3, "sine");
                      const recommended = lockedAgentModal.recommendedPlan as any;
                      setClientPlan(recommended);
                      
                      // Scale limits based on recommended plan
                      const limitsMap: Record<string, { maxAgents: number, maxTasks: number }> = {
                        "Starter": { maxAgents: 2, maxTasks: 1000 },
                        "Growth": { maxAgents: 10, maxTasks: 5000 },
                        "Agency": { maxAgents: 35, maxTasks: 25000 },
                        "Enterprise": { maxAgents: 120, maxTasks: 150000 }
                      };
                      const limits = limitsMap[recommended] || { maxAgents: 10, maxTasks: 5000 };
                      setPlanLimits(prev => ({
                        ...prev,
                        maxAgents: limits.maxAgents,
                        maxTasks: limits.maxTasks
                      }));

                      setLockedAgentModal(null);
                      triggerToast(`♛ NexoraX Subscription successfully UPGRADED to ${recommended.toUpperCase()}!`, "success");
                    }}
                    className="w-full text-center py-2.5 bg-gradient-to-r from-purple-900 to-indigo-950 hover:from-purple-800 hover:to-indigo-900 border-2 border-[#e840f3]/60 hover:border-[#e840f3] text-white font-mono text-[9px] font-black uppercase tracking-widest rounded-xl cursor-pointer transition-all flex items-center justify-center gap-1.5 shadow-[0_0_15px_rgba(232,64,243,0.15)]"
                  >
                    <Sparkles size={11} className="text-cyan-400" />
                    <span>INSTANTLY UPGRADE TO {lockedAgentModal.recommendedPlan.toUpperCase()} TIER</span>
                  </button>

                  <button 
                    onClick={() => {
                      playNexoraXSound(600, 0.2, "triangle");
                      logUpgradeInterest(lockedAgentModal.name, lockedAgentModal.id, clientPlan, "Enterprise");
                      triggerToast("♛ Private Negotiation Handshake launched. Office of the Director alerted.", "success");
                      setLockedAgentModal(null);
                    }}
                    className="w-full text-center py-2 bg-[#04020c] hover:bg-zinc-950 border border-zinc-800 hover:border-zinc-700 text-zinc-400 hover:text-white font-mono text-[9px] font-bold uppercase tracking-widest rounded-xl cursor-pointer transition-colors"
                  >
                    <span>REQUEST ENTERPRISE CUSTOM QUOTE</span>
                  </button>

                  <button 
                    onClick={() => setLockedAgentModal(null)}
                    className="w-full text-center py-1 text-zinc-500 hover:text-zinc-400 font-mono text-[8.5px] uppercase tracking-wider cursor-pointer"
                  >
                    RETURN TO MARKETPLACE LISTINGS
                  </button>
                </div>

              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* CUSTOMISE MODAL / PORTAL SCREEN */}
      <AnimatePresence>
        {selectedTemplateForCustomise && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedTemplateForCustomise(null)}
              className="absolute inset-0 bg-black/85 backdrop-blur-md cursor-pointer"
            />

            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 15 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 15 }}
              className="relative max-w-3xl w-full bg-[#04020c] border-2 border-[#e840f3]/40 rounded-3xl overflow-hidden shadow-[0_0_50px_rgba(232,64,243,0.15)] text-left"
            >
              {/* Highlight accent line */}
              <div className="h-1.5 w-full bg-gradient-to-r from-[#e840f3] to-cyan-500" />

              <div className="p-6 space-y-6 relative z-10 max-h-[85vh] overflow-y-auto custom-scrollbar">
                
                {/* Header title */}
                <div className="flex justify-between items-start border-b border-purple-950/60 pb-4">
                  <div>
                    <span className="text-[8.5px] font-mono text-[#f574fe] tracking-wider font-extrabold uppercase bg-purple-950/30 px-2 py-0.5 rounded border border-purple-500/10">
                      CUSTOMIZATION SUITE MATRIX // COLD CONFIG LOAD
                    </span>
                    <h3 className="text-lg md:text-xl font-mono font-black text-white tracking-widest uppercase mt-2">
                      Customise Agent Schema
                    </h3>
                    <p className="text-[10px] font-mono text-zinc-400 normal-case mt-0.5">
                      Tailoring specifications for: <strong className="text-white uppercase">{selectedTemplateForCustomise.name}</strong>
                    </p>
                  </div>

                  <button 
                    onClick={() => setSelectedTemplateForCustomise(null)}
                    className="p-1 hover:bg-slate-900 border border-transparent hover:border-slate-800 rounded transition-colors text-slate-400 hover:text-white cursor-pointer"
                  >
                    <X size={16} />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs font-mono">
                  
                  {/* DISPLAY NAME */}
                  <div className="space-y-1 text-left">
                    <label className="text-zinc-400 uppercase font-bold text-[8.5px]">Agent Display Name</label>
                    <input 
                      type="text" 
                      value={customiseForm.agentDisplayName}
                      onChange={(e) => setCustomiseForm({...customiseForm, agentDisplayName: e.target.value})}
                      className="w-full bg-black/70 border border-white/10 p-2 text-white outline-none focus:border-cyan-500 transition-colors uppercase"
                    />
                  </div>

                  {/* BUSINESS NAME */}
                  <div className="space-y-1 text-left">
                    <label className="text-zinc-400 uppercase font-bold text-[8.5px]">Business Name</label>
                    <input 
                      type="text" 
                      value={customiseForm.businessName}
                      onChange={(e) => setCustomiseForm({...customiseForm, businessName: e.target.value})}
                      className="w-full bg-black/70 border border-white/10 p-2 text-white outline-none focus:border-cyan-500 transition-colors uppercase"
                    />
                  </div>

                  {/* WEBSITE URL */}
                  <div className="space-y-1 text-left">
                    <label className="text-zinc-400 uppercase font-bold text-[8.5px]">Website URL</label>
                    <input 
                      type="text" 
                      value={customiseForm.websiteUrl}
                      onChange={(e) => setCustomiseForm({...customiseForm, websiteUrl: e.target.value})}
                      className="w-full bg-black/70 border border-white/10 p-2 text-white outline-none focus:border-cyan-500 transition-colors uppercase"
                    />
                  </div>

                  {/* PUBLIC EMAIL */}
                  <div className="space-y-1 text-left">
                    <label className="text-zinc-400 uppercase font-bold text-[8.5px]">Public Email Address</label>
                    <input 
                      type="email" 
                      value={customiseForm.publicEmail}
                      onChange={(e) => setCustomiseForm({...customiseForm, publicEmail: e.target.value})}
                      className="w-full bg-black/70 border border-white/10 p-2 text-white outline-none focus:border-cyan-500 transition-colors uppercase"
                    />
                  </div>

                  {/* PUBLIC PHONE */}
                  <div className="space-y-1 text-left">
                    <label className="text-zinc-400 uppercase font-bold text-[8.5px]">Public Phone Number</label>
                    <input 
                      type="text" 
                      value={customiseForm.publicPhone}
                      onChange={(e) => setCustomiseForm({...customiseForm, publicPhone: e.target.value})}
                      className="w-full bg-black/70 border border-white/10 p-2 text-white outline-none focus:border-cyan-500 transition-colors uppercase"
                    />
                  </div>

                  {/* BRAND VOICE */}
                  <div className="space-y-1 text-left">
                    <label className="text-zinc-400 uppercase font-bold text-[8.5px]">Brand Voice Directives</label>
                    <input 
                      type="text" 
                      value={customiseForm.brandVoice}
                      onChange={(e) => setCustomiseForm({...customiseForm, brandVoice: e.target.value})}
                      className="w-full bg-black/70 border border-white/10 p-2 text-white outline-none focus:border-cyan-500 transition-colors uppercase"
                    />
                  </div>

                  {/* TONE STYLE */}
                  <div className="space-y-1 text-left">
                    <label className="text-zinc-400 uppercase font-bold text-[8.5px]">Tone Style</label>
                    <input 
                      type="text" 
                      value={customiseForm.toneStyle}
                      onChange={(e) => setCustomiseForm({...customiseForm, toneStyle: e.target.value})}
                      className="w-full bg-black/70 border border-white/10 p-2 text-white outline-none focus:border-cyan-500 transition-colors uppercase"
                    />
                  </div>

                  {/* INDUSTRY */}
                  <div className="space-y-1 text-left">
                    <label className="text-zinc-400 uppercase font-bold text-[8.5px]">Industry Area</label>
                    <input 
                      type="text" 
                      value={customiseForm.industry}
                      onChange={(e) => setCustomiseForm({...customiseForm, industry: e.target.value})}
                      className="w-full bg-black/70 border border-white/10 p-2 text-white outline-none focus:border-cyan-500 transition-colors uppercase"
                    />
                  </div>

                  {/* TARGET CUSTOMER */}
                  <div className="space-y-1 text-left">
                    <label className="text-zinc-400 uppercase font-bold text-[8.5px]">Target Customer Cohort</label>
                    <input 
                      type="text" 
                      value={customiseForm.targetCustomer}
                      onChange={(e) => setCustomiseForm({...customiseForm, targetCustomer: e.target.value})}
                      className="w-full bg-black/70 border border-white/10 p-2 text-white outline-none focus:border-cyan-500 transition-colors uppercase"
                    />
                  </div>

                  {/* MAIN OFFER */}
                  <div className="space-y-1 text-left">
                    <label className="text-zinc-400 uppercase font-bold text-[8.5px]">Main Commercial Offer</label>
                    <input 
                      type="text" 
                      value={customiseForm.mainOffer}
                      onChange={(e) => setCustomiseForm({...customiseForm, mainOffer: e.target.value})}
                      className="w-full bg-black/70 border border-white/10 p-2 text-white outline-none focus:border-cyan-500 transition-colors uppercase"
                    />
                  </div>

                  {/* ESCALATION CONTACT */}
                  <div className="space-y-1 text-left">
                    <label className="text-zinc-400 uppercase font-bold text-[8.5px]">Escalation Contact</label>
                    <input 
                      type="text" 
                      value={customiseForm.escalationContact}
                      onChange={(e) => setCustomiseForm({...customiseForm, escalationContact: e.target.value})}
                      className="w-full bg-black/70 border border-white/10 p-2 text-white outline-none focus:border-cyan-500 transition-colors uppercase"
                    />
                  </div>

                  {/* CALL TO ACTION */}
                  <div className="space-y-1 text-left md:col-span-2">
                    <label className="text-zinc-400 uppercase font-bold text-[8.5px]">Primary Call To Action (CTA)</label>
                    <input 
                      type="text" 
                      value={customiseForm.cta}
                      onChange={(e) => setCustomiseForm({...customiseForm, cta: e.target.value})}
                      className="w-full bg-black/70 border border-white/10 p-2 text-white outline-none focus:border-cyan-500 transition-colors uppercase"
                    />
                  </div>

                  {/* CONNECTED TOOLS */}
                  <div className="space-y-1 text-left">
                    <label className="text-zinc-400 uppercase font-bold text-[8.5px]">Connected Pipelines / Tools (Comma Separated)</label>
                    <input 
                      type="text" 
                      value={customiseForm.connectedTools}
                      onChange={(e) => setCustomiseForm({...customiseForm, connectedTools: e.target.value})}
                      className="w-full bg-black/70 border border-white/10 p-2 text-white outline-none focus:border-cyan-500 transition-colors uppercase"
                    />
                  </div>

                  {/* KNOWLEDGE SOURCES */}
                  <div className="space-y-1 text-left">
                    <label className="text-zinc-400 uppercase font-bold text-[8.5px]">Knowledge Sheets Index (Comma Separated)</label>
                    <input 
                      type="text" 
                      value={customiseForm.knowledgeSources}
                      onChange={(e) => setCustomiseForm({...customiseForm, knowledgeSources: e.target.value})}
                      className="w-full bg-black/70 border border-white/10 p-2 text-white outline-none focus:border-cyan-500 transition-colors uppercase"
                    />
                  </div>

                  {/* APPROVAL REQUIREMENT */}
                  <div className="space-y-1 text-left md:col-span-2">
                    <label className="text-zinc-400 uppercase font-bold text-[8.5px]">Human Approval Requirement Rules</label>
                    <input 
                      type="text" 
                      value={customiseForm.approvalRequirement}
                      onChange={(e) => setCustomiseForm({...customiseForm, approvalRequirement: e.target.value})}
                      className="w-full bg-black/70 border border-white/10 p-2 text-white outline-none focus:border-cyan-500 transition-colors uppercase"
                    />
                  </div>

                  {/* DO / DO NOT RULES */}
                  <div className="space-y-1 text-left md:col-span-2">
                    <label className="text-zinc-400 uppercase font-bold text-[8.5px]">Strict "DO" and "DON'T" Parameters</label>
                    <textarea 
                      rows={3}
                      value={customiseForm.doAndDontRules}
                      onChange={(e) => setCustomiseForm({...customiseForm, doAndDontRules: e.target.value})}
                      className="w-full bg-black/70 border border-white/10 p-2 text-white outline-none focus:border-cyan-500 transition-colors uppercase font-mono text-[10px]"
                    />
                  </div>

                </div>

                {/* TEMPLATE PERSONALISATION DYNAMIC SUBSTITUTION BAR */}
                <div className="bg-purple-950/20 border-2 border-purple-500/25 p-4 rounded-2xl space-y-2.5">
                  <div className="flex justify-between items-center pb-2 border-b border-purple-500/10 font-mono text-[9px]">
                    <span className="text-[#f574fe] font-bold uppercase block tracking-wider flex items-center gap-1">
                      <Sparkles className="w-3 text-cyan-400 animate-pulse" />
                      ★ LIVE REAL-TIME TEMPLATE REPLACEMENT MTRX Preview
                    </span>
                    <span className="text-zinc-500">Node-12.7 Alignment Check</span>
                  </div>

                  <div className="space-y-2 font-mono text-[9.5px]">
                    <p className="text-zinc-500 italic lowercase normal-case">
                      As you alter business variables, placeholder coordinates like <strong className="text-white bg-zinc-800 px-1 rounded uppercase">[BUSINESS_NAME]</strong> and <strong className="text-white bg-zinc-800 px-1 rounded uppercase">[PUBLIC_PHONE]</strong> substitute dynamically inside the copy structures:
                    </p>

                    <div className="p-3 bg-black rounded-xl border border-purple-500/15 text-cyan-300 select-all whitespace-pre-wrap leading-relaxed text-left normal-case">
                      {customTextPreview}
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-3 justify-end pt-3 border-t border-purple-950/45">
                  <button
                    onClick={() => {
                      playNexoraXSound(150, 0.2);
                      setSelectedTemplateForCustomise(null);
                    }}
                    className="px-4 py-2 border border-zinc-800 text-slate-400 hover:text-white rounded-xl font-mono text-[9px] font-bold uppercase tracking-wider cursor-pointer"
                  >
                    DISCARD CHANGES
                  </button>

                  <button
                    onClick={() => {
                      triggerToast("Tasks simulation compiled. Checked for standard SLA parameters.", "info");
                    }}
                    className="px-4 py-2 border border-cyan-500/45 hover:border-cyan-400 text-cyan-300 rounded-xl font-mono text-[9px] font-bold uppercase tracking-wider cursor-pointer"
                  >
                    PREVIEW TASKS
                  </button>

                  <button
                    onClick={() => {
                      triggerToast("Configuration payload routed to executive supervisory channel.", "success");
                    }}
                    className="px-4 py-2 border border-purple-500/40 hover:border-purple-400 text-[#f574fe] rounded-xl font-mono text-[9px] font-bold uppercase tracking-wider cursor-pointer"
                  >
                    SEND TO APPROVAL
                  </button>

                  <button
                    onClick={() => {
                      if (!selectedTemplateForCustomise) return;
                      
                      // Intercept customization with NexoraX gate
                      const mockCustomisedAgent: AgentTemplate = {
                        ...selectedTemplateForCustomise,
                        name: customiseForm.agentDisplayName || selectedTemplateForCustomise.name,
                        description: `[Customised Blueprint] ${selectedTemplateForCustomise.description}`,
                        category: selectedTemplateForCustomise.category || "Executive & Admin",
                        role: selectedTemplateForCustomise.role,
                        tasks: selectedTemplateForCustomise.tasks,
                        setupTime: selectedTemplateForCustomise.setupTime,
                        automationLevel: selectedTemplateForCustomise.automationLevel,
                        humanApprovalRequired: selectedTemplateForCustomise.humanApprovalRequired,
                        status: selectedTemplateForCustomise.status,
                        toolsConnected: customiseForm.connectedTools.split(",").map(i => i.trim()),
                        knowledgeSources: customiseForm.knowledgeSources.split(",").map(i => i.trim()),
                        exampleOutput: selectedTemplateForCustomise.exampleOutput,
                        riskLevel: selectedTemplateForCustomise.riskLevel,
                        recommendedPlan: selectedTemplateForCustomise.recommendedPlan,
                        timeSaved: selectedTemplateForCustomise.timeSaved,
                        id: selectedTemplateForCustomise.id
                      };

                      setSelectedTemplateForCustomise(null);
                      handleOpenApprovalGate(mockCustomisedAgent, () => {
                        handleSaveCustomisedAgent();
                        handleTriggerDeploy(selectedTemplateForCustomise.id);
                      });
                    }}
                    className="px-5 py-2 bg-[#e840f3] hover:bg-[#ff5dfb] border border-white/10 text-black hover:text-black rounded-xl font-mono text-[9px] font-black uppercase tracking-widest cursor-pointer shadow-lg animate-pulse"
                  >
                    SAVE & DEPLOY AGENT
                  </button>
                </div>

              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ADMIN TEMPLATE CREATOR DIALOG */}
      <AnimatePresence>
        {showAdminCreator && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowAdminCreator(false)}
              className="absolute inset-0 bg-black/85 backdrop-blur-md cursor-pointer"
            />

            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 15 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 15 }}
              className="relative max-w-xl w-full bg-[#0c0202] border-2 border-rose-500/40 rounded-3xl overflow-hidden shadow-[0_0_50px_rgba(244,63,94,0.15)] text-left"
            >
              <div className="h-1.5 w-full bg-rose-500" />
              
              <div className="p-6 space-y-5 relative max-h-[80vh] overflow-y-auto custom-scrollbar">
                
                <div className="flex justify-between items-start border-b border-rose-950/60 pb-3">
                  <div>
                    <span className="text-[8px] font-mono text-rose-400 tracking-widest font-black uppercase">
                      ADMINISTRATIVE BLUEPRINT INJECTOR
                    </span>
                    <h3 className="text-base font-mono font-black text-white tracking-widest uppercase mt-1">
                      Generate New Template Node
                    </h3>
                  </div>
                  <button 
                    onClick={() => setShowAdminCreator(false)}
                    className="p-1 hover:bg-zinc-900 rounded text-slate-400 hover:text-white cursor-pointer"
                  >
                    <X size={16} />
                  </button>
                </div>

                <div className="space-y-4 text-xs font-mono">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5 text-left">
                      <label className="text-zinc-500 font-bold text-[8px] uppercase">AGENT NAME</label>
                      <input 
                        type="text" 
                        placeholder="e.g. Sales closer agent"
                        value={newTemplate.name}
                        onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
                        className="w-full bg-black border border-rose-950/80 p-2 text-white outline-none focus:border-rose-500"
                      />
                    </div>

                    <div className="space-y-1.5 text-left">
                      <label className="text-zinc-500 font-bold text-[8px] uppercase">ROLE DIRECTIVE</label>
                      <input 
                        type="text" 
                        placeholder="e.g. outreach liason drone"
                        value={newTemplate.role}
                        onChange={(e) => setNewTemplate({...newTemplate, role: e.target.value})}
                        className="w-full bg-black border border-rose-950/80 p-2 text-white outline-none focus:border-rose-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5 text-left">
                      <label className="text-zinc-500 font-bold text-[8px] uppercase">CATEGORY DEPT</label>
                      <select
                        value={newTemplate.category}
                        onChange={(e) => setNewTemplate({...newTemplate, category: e.target.value})}
                        className="w-full bg-black border border-rose-950/80 p-2 text-white outline-none focus:border-rose-500"
                      >
                        {AGENT_CATEGORIES.map(cat => (
                          <option key={cat} value={cat}>{cat.toUpperCase()}</option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-1.5 text-left">
                      <label className="text-zinc-500 font-bold text-[8px] uppercase">AVOID DELAYS setup time</label>
                      <input 
                        type="text" 
                        value={newTemplate.setupTime}
                        onChange={(e) => setNewTemplate({...newTemplate, setupTime: e.target.value})}
                        className="w-full bg-black border border-rose-950/80 p-2 text-white outline-none focus:border-rose-500 text-xs"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5 text-left">
                    <label className="text-zinc-500 font-bold text-[8px] uppercase font-mono">TEMPLATE DESCRIPTION</label>
                    <textarea 
                      rows={3}
                      placeholder="Input the core operational capability of this ready-made AI agent template..."
                      value={newTemplate.description}
                      onChange={(e) => setNewTemplate({...newTemplate, description: e.target.value})}
                      className="w-full bg-black border border-rose-950/80 p-2 text-white outline-none focus:border-rose-500 text-[10px]"
                    />
                  </div>

                  <div className="space-y-1.5 text-left">
                    <label className="text-zinc-500 font-bold text-[8px] uppercase font-mono">TASK SCHEMAS (COMMA SEPARATED)</label>
                    <input 
                      type="text" 
                      placeholder="e.g. Summarise email briefs, Create follow-ups list, Sync CRM metrics"
                      onChange={(e) => setNewTemplate({...newTemplate, tasks: e.target.value.split(",").map(s => s.trim())})}
                      className="w-full bg-black border border-rose-950/80 p-2 text-white outline-none focus:border-rose-500 text-xs"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-1.5 text-left">
                      <label className="text-zinc-500 font-bold text-[8px] uppercase">COGNITIVE LEVEL %</label>
                      <input 
                        type="number" 
                        value={newTemplate.automationLevel}
                        onChange={(e) => setNewTemplate({...newTemplate, automationLevel: parseInt(e.target.value, 10)})}
                        className="w-full bg-black border border-rose-950/80 p-2 text-white outline-none focus:border-rose-500"
                      />
                    </div>

                    <div className="space-y-1.5 text-left">
                      <label className="text-zinc-500 font-bold text-[8px] uppercase">SATELLITE STATUS</label>
                      <select 
                        value={newTemplate.status}
                        onChange={(e) => setNewTemplate({...newTemplate, status: e.target.value as any})}
                        className="w-full bg-black border border-rose-950/80 p-2 text-white outline-none focus:border-rose-500"
                      >
                        <option value="Ready to Deploy">READY TO DEPLOY</option>
                        <option value="Requires Connection">REQUIRES CONNECTION</option>
                        <option value="Approval Recommended">APPROVAL RECOMMENDED</option>
                        <option value="Admin Setup Required">ADMIN SETUP REQUIRED</option>
                        <option value="Premium Template">PREMIUM TEMPLATE</option>
                      </select>
                    </div>

                    <div className="space-y-1.5 text-left">
                      <label className="text-zinc-500 font-bold text-[8px] uppercase">SENSITIVE AUDIT</label>
                      <select 
                        value={newTemplate.riskLevel}
                        onChange={(e) => setNewTemplate({...newTemplate, riskLevel: e.target.value as any})}
                        className="w-full bg-black border border-rose-950/80 p-2 text-white outline-none focus:border-rose-500"
                      >
                        <option value="LOW">LOW RISK</option>
                        <option value="MEDIUM">MEDIUM RISK</option>
                        <option value="HIGH">HIGH RISK</option>
                      </select>
                    </div>
                  </div>

                </div>

                <div className="flex gap-3 justify-end pt-3 border-t border-rose-950/50">
                  <button 
                    onClick={() => setShowAdminCreator(false)}
                    className="px-4 py-2 border border-zinc-800 text-slate-400 hover:text-white rounded-lg font-mono text-[9px] uppercase font-bold cursor-pointer"
                  >
                    DISCARD
                  </button>
                  <button 
                    onClick={handleAdminCreateTemplate}
                    className="px-5 py-2 bg-rose-900 hover:bg-rose-800 border border-rose-500 text-white rounded-lg font-mono text-[9px] font-black uppercase tracking-widest cursor-pointer shadow-lg"
                  >
                    INJECT TEMPLATE
                  </button>
                </div>

              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* NEXORAX SAFETY APPROVAL GATE MODAL */}
      <AnimatePresence>
        {activeApprovalGateTemplate && (
          <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/40 backdrop-blur-md">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                playNexoraXSound(120, 0.25);
                setActiveApprovalGateTemplate(null);
              }}
              className="absolute inset-0 bg-black/90 backdrop-blur-xl cursor-pointer"
            />
            
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 30 }}
              className="relative max-w-xl w-full bg-[#05010f] border-2 border-amber-500/60 rounded-3xl overflow-hidden shadow-[0_0_60px_rgba(245,158,11,0.25)] text-left z-10"
            >
              {/* Caution stripe header */}
              <div className="h-2 w-full bg-gradient-to-r from-amber-500 via-rose-500 to-amber-500" />
              
              <div className="p-6 md:p-8 space-y-6 relative z-10 max-h-[90vh] overflow-y-auto custom-scrollbar">
                
                {/* Immersive Audio feedback button */}
                <div className="flex justify-between items-start border-b border-amber-500/20 pb-4">
                  <div className="space-y-1 block">
                    <span className="font-mono text-[8px] tracking-[0.4em] text-amber-500 font-extrabold uppercase block leading-tight">
                      SECURITY COMPLIANCE GATEWAY // LEVEL-4 HUMAN APPROVAL GATE
                    </span>
                    <h3 className="text-lg md:text-xl font-bold font-mono text-white uppercase tracking-tight">
                      Human Approval Gate
                    </h3>
                  </div>
                  <div className="p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                    <Shield className="w-5 h-5 text-amber-400 animate-pulse" />
                  </div>
                </div>

                {/* Mandated Religious / System Preamble */}
                {brandIntroMode !== "None" && (
                  <div className="bg-black/60 p-3.5 border border-amber-500/10 rounded-lg text-center block">
                    <span className="font-mono text-[8px] text-amber-300 block uppercase tracking-widest mb-1.5 font-black">
                      {brandIntroMode === "Spiritual / Values-Based Intro" ? "MANDATED PREAMBLE:" : "SYSTEM COMPLIANCE DIRECTIVE:"}
                    </span>
                    <p className={`text-[10px] md:text-xs text-zinc-300 normal-case ${brandIntroMode === "Spiritual / Values-Based Intro" ? "font-serif italic" : "font-mono"}`}>
                      {brandIntroMode === "Spiritual / Values-Based Intro" && `"In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds."`}
                      {brandIntroMode === "Professional" && `"Authorized Sandbox Validation: Deploying secure AI worker templates with human-in-the-loop validation filters."`}
                      {brandIntroMode === "Founder Message" && `"Founder Message Directive: Enforcing strict approval gate safeguards across custom agent actions."`}
                    </p>
                  </div>
                )}

                {/* Target Information Card */}
                <div className="bg-black/40 border border-zinc-800 p-4 rounded-xl space-y-2.5 block">
                  <span className="text-[8px] font-mono text-zinc-500 block uppercase">TARGET DEPLOYMENT NODE:</span>
                  <div className="flex justify-between items-center">
                    <strong className="text-white font-mono text-xs uppercase tracking-wide">
                      {activeApprovalGateTemplate.name}
                    </strong>
                    <span className={`px-2 py-0.5 border text-[7.5px] font-bold font-mono rounded-md uppercase tracking-offset ${
                      (activeApprovalGateTemplate as any).riskLevel === "HIGH" ? "bg-red-500/10 border-red-500/30 text-red-400" : "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                    }`}>
                      {(activeApprovalGateTemplate as any).riskLevel || "LOW"} RISK
                    </span>
                  </div>
                  <p className="text-[10.5px] text-zinc-400 leading-relaxed font-sans normal-case">
                    {activeApprovalGateTemplate.description}
                  </p>
                </div>

                {/* Checkbox Gates Checklist */}
                <div className="space-y-3 block">
                  <span className="text-[8px] font-mono text-zinc-500 block uppercase tracking-widest">
                    MANDATORY LEGAL & FISCAL AFFIRMATIONS:
                  </span>

                  <div className="space-y-2.5">
                    {/* Checkbox 1 */}
                    <label className="flex items-start gap-4 p-3 bg-black/50 border border-zinc-900 rounded-xl hover:border-amber-500/20 transition-all cursor-pointer select-none">
                      <input 
                        type="checkbox"
                        checked={approvalGateChecks.levyVerified}
                        onChange={(e) => {
                          playNexoraXSound(e.target.checked ? 440 : 220, 0.1);
                          setApprovalGateChecks({...approvalGateChecks, levyVerified: e.target.checked});
                        }}
                        className="mt-0.5 rounded border-zinc-750 bg-black text-amber-500 focus:ring-amber-500/50 cursor-pointer"
                      />
                      <div className="space-y-0.5">
                        <span className="font-mono text-[9px] text-amber-400 font-bold block uppercase">
                          Corporate Revenue Alignment protocol
                        </span>
                        <p className="text-[9.5px] text-zinc-400 font-sans leading-snug normal-case">
                          I affirm that the approved 1% 3% Community Allocation and 10% Maintenance & Hosting Fee guidelines apply, routing corresponding settlements through the verified corporate finance system.
                        </p>
                      </div>
                    </label>

                    {/* Checkbox 2 */}
                    <label className="flex items-start gap-4 p-3 bg-black/50 border border-zinc-900 rounded-xl hover:border-amber-500/20 transition-all cursor-pointer select-none">
                      <input 
                        type="checkbox"
                        checked={approvalGateChecks.accessToWorkVerified}
                        onChange={(e) => {
                          playNexoraXSound(e.target.checked ? 440 : 220, 0.1);
                          setApprovalGateChecks({...approvalGateChecks, accessToWorkVerified: e.target.checked});
                        }}
                        className="mt-0.5 rounded border-zinc-750 bg-black text-amber-500 focus:ring-amber-500/50 cursor-pointer"
                      />
                      <div className="space-y-0.5">
                        <span className="font-mono text-[9px] text-amber-400 font-bold block uppercase">
                          Enterprise placement fee alignment
                        </span>
                        <p className="text-[9.5px] text-zinc-400 font-sans leading-snug normal-case">
                          I acknowledge that the enterprise licensing costing structure matches instructions corresponding to approved corporate programs and program rules.
                        </p>
                      </div>
                    </label>

                    {/* Checkbox 3 */}
                    <label className="flex items-start gap-4 p-3 bg-black/50 border border-zinc-900 rounded-xl hover:border-amber-500/20 transition-all cursor-pointer select-none">
                      <input 
                        type="checkbox"
                        checked={approvalGateChecks.securityVerified}
                        onChange={(e) => {
                          playNexoraXSound(e.target.checked ? 440 : 220, 0.1);
                          setApprovalGateChecks({...approvalGateChecks, securityVerified: e.target.checked});
                        }}
                        className="mt-0.5 rounded border-zinc-750 bg-black text-amber-500 focus:ring-amber-500/50 cursor-pointer"
                      />
                      <div className="space-y-0.5">
                        <span className="font-mono text-[9px] text-amber-400 font-bold block uppercase">
                          Zero-Extraction Screenshot Barrier
                        </span>
                        <p className="text-[9.5px] text-zinc-400 font-sans leading-snug normal-case">
                          I authorize active screen-lock exfiltration blockers. Any screenshots or unauthorized copy commands force automatic node termination.
                        </p>
                      </div>
                    </label>

                    {/* Checkbox 4 */}
                    <label className="flex items-start gap-4 p-3 bg-black/50 border border-zinc-900 rounded-xl hover:border-amber-500/20 transition-all cursor-pointer select-none">
                      <input 
                        type="checkbox"
                        checked={approvalGateChecks.latencyVerified}
                        onChange={(e) => {
                          playNexoraXSound(e.target.checked ? 440 : 220, 0.1);
                          setApprovalGateChecks({...approvalGateChecks, latencyVerified: e.target.checked});
                        }}
                        className="mt-0.5 rounded border-zinc-750 bg-black text-amber-500 focus:ring-amber-500/50 cursor-pointer"
                      />
                      <div className="space-y-0.5">
                        <span className="font-mono text-[9px] text-amber-400 font-bold block uppercase">
                          0.8S execution latency & SLA standard
                        </span>
                        <p className="text-[9.5px] text-zinc-400 font-sans leading-snug normal-case">
                          I understand this action deploys immediate algorithmic pipelines under direct supervision of Director Lead Director’s neural cores.
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Modal actions */}
                <div className="flex gap-3 justify-end pt-3 border-t border-amber-500/20">
                  <button
                    onClick={() => {
                      playNexoraXSound(150, 0.25);
                      setActiveApprovalGateTemplate(null);
                    }}
                    className="px-4 py-2 border border-zinc-800 text-zinc-400 hover:text-white rounded-xl font-mono text-[9px] font-bold uppercase tracking-wider cursor-pointer"
                  >
                    DISCARD COMPLIANCE
                  </button>

                  <button
                    disabled={
                      !approvalGateChecks.levyVerified ||
                      !approvalGateChecks.accessToWorkVerified ||
                      !approvalGateChecks.securityVerified ||
                      !approvalGateChecks.latencyVerified
                    }
                    onClick={() => {
                      playNexoraXSound(480, 0.5, "sawtooth");
                      setActiveApprovalGateTemplate(null);
                      triggerToast("Security approval cleared. Initializing NexoraX compile...", "success");
                      if (approvalGateCallback) {
                        approvalGateCallback();
                      }
                    }}
                    className={`px-6 py-2.5 rounded-xl font-mono text-[9px] font-black uppercase tracking-widest cursor-pointer shadow-lg flex items-center gap-1.5 transition-all ${
                      (approvalGateChecks.levyVerified && approvalGateChecks.accessToWorkVerified && approvalGateChecks.securityVerified && approvalGateChecks.latencyVerified)
                        ? "bg-amber-500 hover:bg-amber-400 text-black border border-white/20 animate-pulse"
                        : "bg-zinc-950 text-zinc-600 border border-zinc-805 cursor-not-allowed"
                    }`}
                  >
                    <BadgeCheck className="w-4 h-4" />
                    <span>AUTHORIZE DEPLOYMENT</span>
                  </button>
                </div>

              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}
